import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import SearchIcon from '@mui/icons-material/Search';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TodayIcon from '@mui/icons-material/Today';
import HistoryIcon from '@mui/icons-material/History';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import TuneIcon from '@mui/icons-material/Tune';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DownloadIcon from '@mui/icons-material/Download';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import DatePicker from '../../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import QRCode from 'qrcode';
import {
  createPosMenuCategory,
  createPosMenuItem,
  deletePosMenuItem,
  deletePosMenuCategory,
  createPosTable,
  downloadPosOrderInvoice,
  fetchPosOrderDraftInvoiceDefaults,
  fetchPosOrderDraftInvoicePdfBlob,
  fetchPosOrderInvoicePdfBlob,
  createPosMenuVariation,
  deletePosTable,
  getPosOrderDetail,
  getPosOrderAuditLogs,
  getPosRestaurantBootstrap,
  getPosQrBrandingProfile,
  getPosQrContext,
  getPosOrders,
  deletePosMenuVariation,
  updatePosMenuCategory,
  updatePosMenuItem,
  updatePosMenuVariation,
  updatePosOrder,
  updatePosRestaurantSettings,
  updatePosRoomConfigs,
  updatePosTable,
} from '../api';
import { parsePosTaxBreakdownPercentage } from '../openOrderUtils';
import { detectThemeSync, watchThemeChanges } from '../../../utils/themeUtils';
import { formatBookingCurrency } from '../../../utils/bookingPaymentSummary';
import { can } from '../../../auth/permissionProvider';
import { PERMISSIONS } from '../../../auth/permissions';
import {
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerPanelSx,
  getBookingDrawerAuditButtonSx,
  BOOKING_DRAWER_WIDTH_SX,
} from '../../../utils/bookingDrawerLayout';
import BookingInvoicePreviewDialog from '../../../components/booking/BookingInvoicePreviewDialog';
import {
  getPosConfigCardSx as getConfigCardSx,
  getPosDashboardCardSx as getDashboardCardSx,
  getPosHeroPaperSx,
  getPosHeroContentSx,
  getPosHeroSubtitleSx,
  getPosHeroChipSx,
  getPosHeroChipRowSx,
  getPosMainTabsSx,
  getPosMenuSectionTabsSx,
  getPosOrdersDataTableContainerSx,
  getPosMiniCardSx as getMiniCardSx,
  getPosPageShellSx,
  getPosPrimaryButtonSx,
  getPosSectionDividerSx,
  getPosTabsShellSx,
  getPosThemeColors,
  getPosConfigHeaderIconSx,
  getPosOrderDrawerSectionPaperSx,
  getPosOrderDrawerSummaryPaperSx,
  POS_ACCENT,
} from '../../../utils/posRestaurantLayout';
import { downloadPdfMultiSection } from '../../../utils/reportExport';
import ErrorState from '../../../components/common/ErrorState';
import Breadcrumbs from '../../../components/common/Breadcrumbs';
import PageToastStack from '../../../components/common/PageToastStack';
import PrinterValidationBanner from '../../../components/restaurant/PrinterValidationBanner';
import {
  buildRestaurantReceiptHtml,
  downloadRestaurantReceiptHtml,
  formatReceiptOrderNumber,
  printRestaurantReceiptHtml,
} from '../../restaurant/utils/restaurantReceiptHtml';
import { openPrinterValidationDialog } from '../../../utils/desktopPrinter';
import { DataTable, FormSelect, FormTextField } from '../../../shared';
import RestaurantLandingPage from '../../restaurant/components/RestaurantLandingPage';
import PosOrderPaymentDrawer from './PosOrderPaymentDrawer';
import PosAiMenuUploaderOverlay from './PosAiMenuUploaderOverlay';
import { getHotelDateFormat } from '../../../utils/dateUtils';
import {
  getPaymentStatusColor,
  getPaymentStatusLabel,
  roundBookingMoney,
} from '../../../utils/bookingOpsCardHelpers';
import { getHotelBasicDetails } from '../../../services/api';
import { buildQrStandArtwork } from '../utils/qrStandTemplate';

const TAB_VALUES = {
  DASHBOARD: 'dashboard',
  ORDERS: 'orders',
  ROOM: 'room',
  TABLE: 'table',
  MENU: 'menu',
};
const TAB_OPTIONS = Object.values(TAB_VALUES);
const MENU_SECTION_VALUES = {
  ITEMS: 'items',
  CATEGORIES: 'categories',
  VARIATIONS: 'variations',
};
const VARIATION_DEPARTMENT_OPTIONS = [
  { value: 'size', label: 'Size' },
  { value: 'portion', label: 'Portion' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'portion_size', label: 'Portion Size' },
  { value: 'customisation', label: 'Customisation' },
  { value: 'preparation', label: 'Preparation' },
];
const MENU_DIETARY_OPTIONS = [
  { value: 'veg', label: 'Veg' },
  { value: 'non_veg', label: 'Non-Veg' },
  { value: 'egg', label: 'Egg' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'jain', label: 'Jain' },
];
const MENU_GST_TYPE_OPTIONS = [
  { value: 'services', label: 'Services' },
  { value: 'goods', label: 'Goods' },
];
const TAB_HEADER_CONFIG = {
  [TAB_VALUES.DASHBOARD]: {
    title: 'POS Dashboard',
    subtitle: 'Analytics and business insights for restaurant performance.',
  },
  [TAB_VALUES.ORDERS]: {
    title: 'POS Orders',
    subtitle: 'View and manage order history with smart filters and actions.',
  },
  [TAB_VALUES.ROOM]: {
    title: 'POS Room',
    subtitle: 'Configure room-wise restaurant billing and ordering access.',
  },
  [TAB_VALUES.TABLE]: {
    title: 'POS Table',
    subtitle: 'Create and manage table setup for dine-in service.',
  },
  [TAB_VALUES.MENU]: {
    title: 'POS Menu',
    subtitle: 'Manage menu categories, items, pricing, and availability.',
  },
};
const ACCENT_COLOR = POS_ACCENT;

const DASHBOARD_DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Date' },
  { value: 'all', label: 'All' },
];
const DASHBOARD_DATE_ICON_MAP = {
  today: TodayIcon,
  yesterday: HistoryIcon,
  last7: DateRangeIcon,
  last30: EventNoteIcon,
  thisMonth: CalendarMonthIcon,
  lastMonth: HistoryIcon,
  custom: DateRangeIcon,
  all: AllInclusiveIcon,
};

const SLOT_CONFIG = [
  { label: '12:00 AM - 04:00 AM', shortLabel: '12AM-4AM', from: 0, to: 4 },
  { label: '04:00 AM - 08:00 AM', shortLabel: '4AM-8AM', from: 4, to: 8 },
  { label: '08:00 AM - 12:00 PM', shortLabel: '8AM-12PM', from: 8, to: 12 },
  { label: '12:00 PM - 04:00 PM', shortLabel: '12PM-4PM', from: 12, to: 16 },
  { label: '04:00 PM - 08:00 PM', shortLabel: '4PM-8PM', from: 16, to: 20 },
  { label: '08:00 PM - 12:00 AM', shortLabel: '8PM-12AM', from: 20, to: 24 },
];

const getPosHotelCurrency = () => {
  const currency =
    (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.currency) || 'INR';
  return String(currency || 'INR').toUpperCase();
};

const formatCurrency = (value) => formatBookingCurrency(value, getPosHotelCurrency());

const formatShortDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getLocalDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatSalesTrendDateLabel = (value, withWeekday = false) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    ...(withWeekday ? { weekday: 'short' } : {}),
  });
};

const formatDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return getLocalDateKey(date);
};

const getOrderTypeLabel = (orderType) => {
  if (orderType === 'room_service') return 'Room Service';
  if (orderType === 'pick_up' || orderType === 'takeaway' || orderType === 'delivery') return 'Pick Up / Delivery';
  if (orderType === 'dine_in') return 'Dine In';
  return String(orderType || '-');
};

const normalizeOrderTypeForDashboard = (orderType) => {
  const normalizedType = String(orderType || '').toLowerCase().trim();
  if (normalizedType === 'pick_up' || normalizedType === 'takeaway' || normalizedType === 'delivery') {
    return 'takeaway';
  }
  if (normalizedType === 'room_service') return 'room_service';
  if (normalizedType === 'dine_in') return 'dine_in';
  return normalizedType;
};

const getOrderStatusMeta = (status) => {
  if (status === 'completed') return { label: 'Completed', color: '#16a34a' };
  if (status === 'served') return { label: 'Served', color: '#10b981' };
  if (status === 'preparing') return { label: 'Preparing', color: '#f59e0b' };
  if (status === 'cancelled') return { label: 'Cancelled', color: '#ef4444' };
  if (status === 'draft') return { label: 'Draft', color: '#94a3b8' };
  return { label: 'Placed', color: '#06b6d4' };
};

const getOrderSourceLabel = (order) => {
  if (order?.room_number) return `Room ${order.room_number}`;
  if (order?.table_name) return `Table ${order.table_name}`;
  return 'Walk-In';
};

const toTitleCaseWords = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizePaymentMethodKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[_-\s]+/g, ' ')
  .trim();

const KNOWN_PAYMENT_METHOD_LABELS = {
  upi: 'UPI',
  cash: 'Cash',
  card: 'Card',
  wallet: 'Wallet',
  cheque: 'Cheque',
  'credit card': 'Credit Card',
  'debit card': 'Debit Card',
  online: 'Online',
  'bank transfer': 'Bank Transfer',
  'net banking': 'Net Banking',
  'company settlement': 'Company Settlement',
  'credit sale': 'Credit Sale',
  'payment link': 'Payment Link',
};

const POS_PAYMENT_STATUS_FILTER_OPTIONS = [
  { value: 'full_payment', label: 'Full Payment' },
  { value: 'partial_payment', label: 'Partial Payment' },
  { value: 'due', label: 'Due' },
];

const POS_PAYMENT_METHOD_FILTER_OPTIONS = Object.entries(KNOWN_PAYMENT_METHOD_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const formatPaymentMethodLabel = (value) => {
  const normalizedMethod = normalizePaymentMethodKey(value);
  if (!normalizedMethod) return 'NA';
  return KNOWN_PAYMENT_METHOD_LABELS[normalizedMethod] || toTitleCaseWords(value);
};

const PAYMENT_METHOD_CHART_COLORS = {
  cash: '#22c55e',
  upi: '#6366f1',
  card: '#0ea5e9',
  online: '#3b82f6',
  wallet: '#a855f7',
  cheque: '#94a3b8',
  'bank transfer': '#14b8a6',
  'net banking': '#06b6d4',
  'company settlement': '#f59e0b',
  'credit sale': '#f97316',
  'payment link': '#ec4899',
  'credit card': '#0ea5e9',
  'debit card': '#0284c7',
  na: '#64748b',
};

const PAYMENT_METHOD_FALLBACK_COLORS = ['#16a34a', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6', '#ef4444'];

const getPaymentMethodChartColor = (paymentMethodKey, index = 0) => {
  const normalizedMethod = normalizePaymentMethodKey(paymentMethodKey) || 'na';
  return PAYMENT_METHOD_CHART_COLORS[normalizedMethod]
    || PAYMENT_METHOD_FALLBACK_COLORS[index % PAYMENT_METHOD_FALLBACK_COLORS.length];
};

const getOrderPaymentLabel = (order) => {
  return formatPaymentMethodLabel(order?.payment_method);
};

const getOrderPaymentStatusLabel = (order) => {
  const explicitStatus = String(order?.payment_status || '').trim().toLowerCase();
  if (explicitStatus === 'partial') return 'Partial';
  if (explicitStatus === 'due') return 'Due';
  if (explicitStatus === 'paid') return 'Paid';
  const collectionMode = String(order?.payment_collection_mode || 'collect_now').toLowerCase();
  return collectionMode === 'due' ? 'Due' : 'Paid';
};

const resolvePosOrderPaymentDisplay = (order) => {
  const totalAmount = roundBookingMoney(order?.total_amount ?? 0);
  const paymentEntries = Array.isArray(order?.payments) ? order.payments : [];
  const capturedTotal = paymentEntries
    .filter((entry) => String(entry?.status || '').trim().toLowerCase() === 'captured')
    .reduce((sum, entry) => sum + roundBookingMoney(entry?.amount || 0), 0);

  const dueAmountField = roundBookingMoney(order?.due_amount ?? 0);
  const partialPaidAmount = roundBookingMoney(order?.partial_paid_amount ?? 0);
  const paidByBalance = Math.max(0, totalAmount - dueAmountField);
  const normalizedBackendStatus = String(order?.payment_status || '').trim().toLowerCase();

  let totalPaid = capturedTotal > 0 ? capturedTotal : 0;
  if (totalPaid <= 0) {
    totalPaid = normalizedBackendStatus === 'due'
      ? 0
      : Math.max(partialPaidAmount, paidByBalance);
  }
  totalPaid = Math.min(totalPaid, totalAmount);

  let remainingAmount = roundBookingMoney(Math.max(0, totalAmount - totalPaid));
  if (dueAmountField > 0 && Math.abs(remainingAmount - dueAmountField) <= 0.02) {
    remainingAmount = dueAmountField;
  }
  if (remainingAmount <= 0.01) {
    remainingAmount = 0;
  }

  let paymentStatus = normalizedBackendStatus;
  if (totalAmount <= 0) {
    paymentStatus = 'no_payment';
  } else if (remainingAmount <= 0) {
    paymentStatus = 'full_payment';
  } else if (totalPaid <= 0) {
    paymentStatus = 'due';
  } else {
    paymentStatus = 'partial_payment';
  }

  if (normalizedBackendStatus === 'paid') paymentStatus = 'full_payment';
  else if (normalizedBackendStatus === 'partial' && remainingAmount > 0) paymentStatus = 'partial_payment';
  else if (normalizedBackendStatus === 'due' && remainingAmount > 0) paymentStatus = 'due';

  return {
    totalAmount,
    totalPaid,
    remainingAmount,
    paymentStatus,
  };
};

const resolvePosOrderPaymentMethods = (order, paymentDisplay) => {
  const paymentEntries = Array.isArray(order?.payments) ? order.payments : [];
  const methodAmountMap = new Map();

  paymentEntries
    .filter((entry) => {
      const status = String(entry?.status || '').trim().toLowerCase();
      const amount = roundBookingMoney(entry?.amount || 0);
      return status === 'captured' && amount > 0;
    })
    .forEach((entry) => {
      const methodKey = normalizePaymentMethodKey(entry?.payment_method) || 'na';
      const amount = roundBookingMoney(entry?.amount || 0);
      methodAmountMap.set(
        methodKey,
        roundBookingMoney((methodAmountMap.get(methodKey) || 0) + amount),
      );
    });

  let methods = Array.from(methodAmountMap.entries()).map(([methodKey, amount]) => ({
    key: methodKey,
    label: formatPaymentMethodLabel(methodKey),
    amount,
  }));

  if (methods.length === 0 && paymentDisplay.totalPaid > 0) {
    const fallbackMethod = String(order?.payment_method || '').trim();
    if (fallbackMethod) {
      const methodKey = normalizePaymentMethodKey(fallbackMethod) || 'na';
      methods = [{
        key: methodKey,
        label: formatPaymentMethodLabel(fallbackMethod),
        amount: paymentDisplay.totalPaid,
      }];
    }
  }

  return methods.sort((first, second) => second.amount - first.amount);
};

const getOrderPaymentMethodKeys = (order) => {
  const payment = resolvePosOrderPaymentDisplay(order);
  return resolvePosOrderPaymentMethods(order, payment).map((method) => method.key);
};

function PosOrdersCheckboxMultiFilter({
  label,
  emptyLabel,
  options,
  value = [],
  onChange,
  isDark,
  minWidth = 150,
}) {
  const selected = Array.isArray(value) ? value : [];
  const selectedLabel = useMemo(() => {
    if (!selected.length) return emptyLabel;
    if (selected.length === options.length) return emptyLabel;
    if (selected.length === 1) {
      return options.find((option) => option.value === selected[0])?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options, emptyLabel]);

  return (
    <FormControl size="small" sx={{ minWidth, width: '100%' }}>
      <InputLabel shrink sx={{ color: isDark ? '#999' : '#757575' }}>
        {label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
        }}
        label={label}
        renderValue={() => selectedLabel}
        sx={{
          color: isDark ? '#ddd' : '#212121',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#333246' : '#e0e0e0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#555' : '#999' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: POS_ACCENT },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              '& .MuiMenuItem-root': {
                color: isDark ? '#ddd' : '#212121',
                '&:hover': { backgroundColor: isDark ? '#38373f' : '#f5f5f5' },
                '&.Mui-selected': {
                  backgroundColor: isDark ? '#38373f' : alpha(POS_ACCENT, 0.12),
                  '&:hover': { backgroundColor: isDark ? '#444350' : alpha(POS_ACCENT, 0.18) },
                },
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              checked={selected.includes(option.value)}
              sx={{
                color: isDark ? '#999' : '#757575',
                '&.Mui-checked': { color: POS_ACCENT },
              }}
            />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function PosOrderFinanceCell({ row }) {
  const payment = resolvePosOrderPaymentDisplay(row);
  const showDue = payment.paymentStatus === 'partial_payment' || payment.paymentStatus === 'due';

  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 148 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
        <PaymentsIcon sx={{ fontSize: 14, color: POS_ACCENT, flexShrink: 0, opacity: 0.9 }} />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {formatCurrency(payment.totalAmount)}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.25, pl: 2.25 }}>
        <Box component="span" sx={{ color: '#10b981', fontWeight: 600 }}>
          Paid {formatCurrency(payment.totalPaid)}
        </Box>
        {showDue ? (
          <>
            <Box component="span" sx={{ mx: 0.5, opacity: 0.5 }}>
              ·
            </Box>
            <Box component="span" sx={{ color: '#ff7043', fontWeight: 700 }}>
              Due {formatCurrency(payment.remainingAmount)}
            </Box>
          </>
        ) : null}
      </Typography>
    </Stack>
  );
}

function PosOrderPaymentMethodCell({ row, isDark }) {
  const payment = resolvePosOrderPaymentDisplay(row);
  const paymentMethods = resolvePosOrderPaymentMethods(row, payment);
  const statusLabel = getPaymentStatusLabel(payment.paymentStatus);
  const statusColor = getPaymentStatusColor(payment.paymentStatus);
  const showMultipleMethodAmounts = paymentMethods.length > 1;

  return (
    <Stack spacing={0.4} sx={{ py: 0.15, minWidth: 128 }}>
      <Chip
        size="small"
        label={statusLabel}
        sx={{
          height: 22,
          width: 'fit-content',
          fontWeight: 700,
          fontSize: '0.68rem',
          bgcolor: alpha(statusColor, isDark ? 0.2 : 0.14),
          color: statusColor,
          border: `1px solid ${alpha(statusColor, 0.32)}`,
        }}
      />
      {paymentMethods.length > 0 ? (
        <Stack spacing={0.15}>
          {paymentMethods.map((method, index) => (
            <Typography
              key={`${method.label}-${index}`}
              variant="caption"
              sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.primary' }}
            >
              {method.label}
              {showMultipleMethodAmounts ? ` · ${formatCurrency(method.amount)}` : ''}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      )}
    </Stack>
  );
}

function OrderDrawerSection({ icon: Icon, title, children, isDark, sx = {} }) {
  return (
    <Paper elevation={0} sx={getPosOrderDrawerSectionPaperSx(isDark, { p: 1.35, mb: 0, ...sx })}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.05 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: isDark ? alpha(POS_ACCENT, 0.16) : alpha(POS_ACCENT, 0.1),
            border: `1px solid ${alpha(POS_ACCENT, isDark ? 0.32 : 0.22)}`,
          }}
        >
          <Icon sx={{ fontSize: 17, color: POS_ACCENT }} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
}

function OrderDrawerInfoTile({ label, value, icon: Icon, isDark }) {
  return (
    <Box
      sx={{
        p: 1.05,
        borderRadius: 1.6,
        height: '100%',
        bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha(POS_ACCENT, 0.04),
        border: `1px solid ${isDark ? alpha(POS_ACCENT, 0.16) : alpha(POS_ACCENT, 0.12)}`,
      }}
    >
      <Stack direction="row" spacing={0.85} alignItems="flex-start">
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.25,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: isDark ? alpha(POS_ACCENT, 0.14) : alpha(POS_ACCENT, 0.1),
          }}
        >
          <Icon sx={{ fontSize: 15, color: POS_ACCENT }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.2, lineHeight: 1.35, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function OrderDrawerMetricPill({ label, value, color, isDark }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 0.95,
        borderRadius: 1.6,
        bgcolor: isDark ? alpha(color, 0.12) : alpha(color, 0.08),
        border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, color, mt: 0.15, lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

const getPaymentChipPalette = (paymentKey, isDark) => {
  const key = String(paymentKey || '').trim().toLowerCase();
  if (key === 'partial') {
    return {
      bgcolor: isDark ? alpha('#f59e0b', 0.24) : alpha('#f59e0b', 0.16),
      color: isDark ? '#fde68a' : '#92400e',
      borderColor: alpha('#f59e0b', 0.45),
    };
  }
  if (key === 'due') {
    return {
      bgcolor: isDark ? alpha('#f59e0b', 0.24) : alpha('#f59e0b', 0.18),
      color: isDark ? '#fcd34d' : '#b45309',
      borderColor: alpha('#f59e0b', 0.45),
    };
  }
  if (key === 'cash') {
    return {
      bgcolor: isDark ? alpha('#22c55e', 0.24) : alpha('#22c55e', 0.16),
      color: isDark ? '#bbf7d0' : '#166534',
      borderColor: alpha('#22c55e', 0.42),
    };
  }
  if (key === 'upi') {
    return {
      bgcolor: isDark ? alpha('#6366f1', 0.24) : alpha('#6366f1', 0.16),
      color: isDark ? '#c7d2fe' : '#3730a3',
      borderColor: alpha('#6366f1', 0.44),
    };
  }
  if (key === 'card' || key === 'credit_card' || key === 'debit_card') {
    return {
      bgcolor: isDark ? alpha('#0ea5e9', 0.24) : alpha('#0ea5e9', 0.16),
      color: isDark ? '#bae6fd' : '#075985',
      borderColor: alpha('#0ea5e9', 0.44),
    };
  }
  if (key === 'bank_transfer' || key === 'net_banking') {
    return {
      bgcolor: isDark ? alpha('#14b8a6', 0.24) : alpha('#14b8a6', 0.16),
      color: isDark ? '#99f6e4' : '#115e59',
      borderColor: alpha('#14b8a6', 0.44),
    };
  }
  if (key === 'wallet' || key === 'online') {
    return {
      bgcolor: isDark ? alpha('#8b5cf6', 0.24) : alpha('#8b5cf6', 0.16),
      color: isDark ? '#ddd6fe' : '#6d28d9',
      borderColor: alpha('#8b5cf6', 0.44),
    };
  }
  if (key === 'cheque') {
    return {
      bgcolor: isDark ? alpha('#f97316', 0.24) : alpha('#f97316', 0.16),
      color: isDark ? '#fdba74' : '#9a3412',
      borderColor: alpha('#f97316', 0.44),
    };
  }
  return {
    bgcolor: isDark ? alpha('#94a3b8', 0.24) : alpha('#64748b', 0.14),
    color: isDark ? '#cbd5e1' : '#334155',
    borderColor: isDark ? alpha('#94a3b8', 0.4) : alpha('#64748b', 0.35),
  };
};

const getPaymentStatusChipPalette = (statusKey, isDark) => {
  const key = String(statusKey || '').trim().toLowerCase();
  if (key === 'partial') {
    return {
      bgcolor: isDark ? alpha('#f59e0b', 0.24) : alpha('#f59e0b', 0.16),
      color: isDark ? '#fde68a' : '#92400e',
      borderColor: alpha('#f59e0b', 0.45),
    };
  }
  if (key === 'due') {
    return {
      bgcolor: isDark ? alpha('#ef4444', 0.22) : alpha('#ef4444', 0.14),
      color: isDark ? '#fecaca' : '#991b1b',
      borderColor: alpha('#ef4444', 0.45),
    };
  }
  return {
    bgcolor: isDark ? alpha('#22c55e', 0.24) : alpha('#22c55e', 0.16),
    color: isDark ? '#bbf7d0' : '#166534',
    borderColor: alpha('#22c55e', 0.42),
  };
};

const getPaymentHistoryEntryStatusMeta = (status, isDark) => {
  const key = String(status || '').trim().toLowerCase();
  if (key === 'failed') {
    return {
      label: 'Failed',
      color: isDark ? '#fecaca' : '#b91c1c',
      bgcolor: isDark ? alpha('#ef4444', 0.22) : alpha('#ef4444', 0.12),
      borderColor: alpha('#ef4444', 0.42),
    };
  }
  if (key === 'refunded') {
    return {
      label: 'Refunded',
      color: isDark ? '#ddd6fe' : '#6d28d9',
      bgcolor: isDark ? alpha('#8b5cf6', 0.22) : alpha('#8b5cf6', 0.12),
      borderColor: alpha('#8b5cf6', 0.42),
    };
  }
  return {
    label: 'Captured',
    color: isDark ? '#bbf7d0' : '#166534',
    bgcolor: isDark ? alpha('#22c55e', 0.22) : alpha('#22c55e', 0.12),
    borderColor: alpha('#22c55e', 0.42),
  };
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatOrderNumber = (orderNumber) => String(orderNumber || '').replace(/^POS-/i, '') || '—';
const ORDER_ACTIVE_PROGRESS_STATUSES = new Set(['draft', 'placed', 'preparing']);

const getValidTimestamp = (value) => {
  const timestamp = new Date(value || 0).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= 0) return null;
  return timestamp;
};

const normalizeOrderQueueTimers = (queueTimers) => {
  let rows = queueTimers;
  if (typeof rows === 'string') {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => row && typeof row === 'object');
};

const formatDurationFromMinutes = (minutesValue) => {
  const minutes = Math.max(0, Math.round(Number(minutesValue || 0)));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours <= 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

const getOrderTimingInsight = (order, nowTimestamp = Date.now()) => {
  const targetFromOrder = Math.max(0, Number(order?.preparation_time_minutes || 0));
  const queueTimers = normalizeOrderQueueTimers(order?.queue_timers);
  const targetFromTimers = queueTimers.reduce((sum, timer) => {
    const targetMinutes = Math.max(0, Number(timer?.target_minutes || 0));
    return sum + targetMinutes;
  }, 0);
  const targetMinutes = Math.max(1, targetFromTimers || targetFromOrder || 30);
  const createdTimestamp = getValidTimestamp(order?.created_at);
  const updatedTimestamp = getValidTimestamp(order?.updated_at) || nowTimestamp;
  const isActive = ORDER_ACTIVE_PROGRESS_STATUSES.has(order?.status);
  const fallbackStart = createdTimestamp || updatedTimestamp;
  const fallbackEnd = isActive ? nowTimestamp : updatedTimestamp;

  const actualMsFromTimers = queueTimers.reduce((sum, timer) => {
    const startTimestamp = getValidTimestamp(timer?.started_at) || fallbackStart;
    const endTimestamp = getValidTimestamp(timer?.ended_at) || (isActive ? nowTimestamp : updatedTimestamp);
    if (!startTimestamp || !endTimestamp || endTimestamp <= startTimestamp) return sum;
    return sum + (endTimestamp - startTimestamp);
  }, 0);

  const actualMs = actualMsFromTimers > 0
    ? actualMsFromTimers
    : Math.max(0, (fallbackEnd || nowTimestamp) - (fallbackStart || nowTimestamp));
  const actualMinutes = Math.max(0, actualMs / 60000);
  const varianceMinutes = actualMinutes - targetMinutes;
  const delayMinutes = Math.max(0, varianceMinutes);
  const ratio = targetMinutes > 0 ? actualMinutes / targetMinutes : 0;
  const progressPercent = Math.min(100, Math.max(0, ratio * 100));
  const overrunPercent = ratio > 1 ? Math.min(100, (ratio - 1) * 100) : 0;

  let criticalityLabel = 'On Track';
  let criticalityColor = '#16a34a';
  if (ratio >= 1.5) {
    criticalityLabel = 'Critical Delay';
    criticalityColor = '#b91c1c';
  } else if (ratio >= 1.2) {
    criticalityLabel = 'High Delay';
    criticalityColor = '#ea580c';
  } else if (ratio >= 1) {
    criticalityLabel = 'Slight Delay';
    criticalityColor = '#f59e0b';
  } else if (ratio >= 0.8) {
    criticalityLabel = 'Near SLA';
    criticalityColor = '#ca8a04';
  }

  return {
    targetMinutes,
    actualMinutes,
    delayMinutes,
    varianceMinutes,
    ratio,
    progressPercent,
    overrunPercent,
    criticalityLabel,
    criticalityColor,
    serveLabel: isActive ? 'Elapsed' : 'Actual Serve',
    isActive,
    targetLabel: formatDurationFromMinutes(targetMinutes),
    actualLabel: formatDurationFromMinutes(actualMinutes),
    delayLabel: formatDurationFromMinutes(delayMinutes),
  };
};

const getCriticalityGroupMeta = (timing) => {
  const ratio = Number(timing?.ratio || 0);
  if (ratio >= 1.5) {
    return { key: 'critical', label: 'Critical', color: '#b91c1c' };
  }
  if (ratio >= 1.2) {
    return { key: 'delayed', label: 'Delayed', color: '#ea580c' };
  }
  if (ratio >= 0.8) {
    return { key: 'watch', label: 'Watch', color: '#ca8a04' };
  }
  return { key: 'on_track', label: 'On Track', color: '#16a34a' };
};

const getOrderItemRows = (order) => {
  if (Array.isArray(order?.items)) return order.items;
  if (Array.isArray(order?.order_items)) return order.order_items;
  return [];
};

const getOrderFinancialSummary = (order) => {
  const subtotal = Number(order?.subtotal ?? order?.sub_total ?? order?.gross_amount ?? 0);
  const discountType = String(order?.discount_type || '').toLowerCase();
  const discountValue = Number(order?.discount_value || 0);
  const discountAmountFromPayload = Number(order?.discount_amount || 0);
  const derivedDiscountAmount = discountType === 'percentage'
    ? (subtotal * discountValue) / 100
    : discountType === 'amount'
      ? discountValue
      : 0;
  const discountAmount = discountAmountFromPayload || derivedDiscountAmount;
  const deliveryCharge = Number(order?.delivery_charge || 0);
  const containerCharge = Number(order?.container_charge || 0);

  const rawTaxBreakdown = order?.tax_breakdown;
  const taxBreakdown = Array.isArray(rawTaxBreakdown)
    ? rawTaxBreakdown
    : (typeof rawTaxBreakdown === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(rawTaxBreakdown);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : []);

  // Prefer sum of rounded tax lines so Total matches CGST + SGST on screen.
  const taxFromBreakdown = taxBreakdown.reduce((sum, row) => {
    if (!row || row.enabled === false) return sum;
    const amount = Math.round(Number(row.amount || 0) * 100) / 100;
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const taxAmount = taxBreakdown.length > 0
    ? Math.round(taxFromBreakdown * 100) / 100
    : Number(order?.tax_amount ?? 0);

  const taxableSubtotal = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
  const computedTotal = Math.round(
    (taxableSubtotal + taxAmount + deliveryCharge + containerCharge) * 100,
  ) / 100;
  const storedTotal = Number(order?.total_amount || 0);
  const total = taxBreakdown.length > 0 ? computedTotal : storedTotal;

  return {
    subtotal,
    discountAmount,
    discountType,
    discountValue,
    taxAmount,
    deliveryCharge,
    containerCharge,
    total,
  };
};

const getOrderAuditEventMeta = (eventType, activity = '') => {
  const normalizedActivity = String(activity || '').toLowerCase();
  if (normalizedActivity === 'order_merged' || normalizedActivity === 'merge_source_closed') {
    return { label: 'Order Merged', color: '#a855f7' };
  }
  if (normalizedActivity === 'target_changed') {
    return { label: 'Room/Table Changed', color: '#14b8a6' };
  }
  if (normalizedActivity === 'kot_created') {
    return { label: 'KOT Created', color: '#f59e0b' };
  }
  if (normalizedActivity === 'kot_updated' || normalizedActivity === 'kot_marked') {
    return { label: 'KOT Updated', color: '#f97316' };
  }
  if (normalizedActivity === 'payment_captured') {
    return { label: 'Payment Captured', color: '#22c55e' };
  }
  if (normalizedActivity === 'order_cancelled') {
    return { label: 'Order Cancelled', color: '#ef4444' };
  }
  if (normalizedActivity === 'items_updated') {
    return { label: 'Items Updated', color: '#0ea5e9' };
  }

  const normalizedEvent = String(eventType || '').toLowerCase();
  if (normalizedEvent === 'created') return { label: 'Created', color: '#22c55e' };
  if (normalizedEvent === 'status_changed') return { label: 'Status Changed', color: '#f59e0b' };
  return { label: 'Updated', color: '#38bdf8' };
};

const buildOrderEditFormFromOrder = (order) => {
  const orderItems = getOrderItemRows(order)
    .filter((item) => Number(item?.menu_item || 0) > 0)
    .map((item) => ({
      menu_item: Number(item.menu_item),
      item_name: item.item_name || item.menu_item_name || 'Item',
      quantity: Math.max(1, Number(item.quantity || 1)),
      special_note: String(item.special_note || ''),
    }));
  return {
    order_type: String(order?.order_type || 'dine_in'),
    status: String(order?.status || 'placed'),
    customer_name: String(order?.customer_name || ''),
    customer_phone_country_code: String(order?.customer_phone_country_code || '+91'),
    customer_phone: String(order?.customer_phone || ''),
    payment_collection_mode: String(order?.payment_collection_mode || 'collect_now'),
    payment_method: String(order?.payment_method || ''),
    preparation_time_minutes: Math.max(5, Number(order?.preparation_time_minutes || 30)),
    notes: String(order?.notes || ''),
    order_items: orderItems,
  };
};

const getTabFromSearch = (search = '') => {
  const params = new URLSearchParams(search);
  const tabValue = params.get('tab');
  if (TAB_OPTIONS.includes(tabValue)) {
    return tabValue;
  }
  return TAB_VALUES.DASHBOARD;
};

const PosHeroChipSkeleton = () => (
  <Skeleton variant="rounded" width={98} height={24} sx={{ borderRadius: 999 }} />
);

const POS_HERO_CHIP_PALETTES = {
  orders: {
    bg: { dark: 'rgba(99,102,241,0.2)', light: 'rgba(99,102,241,0.14)' },
    color: { dark: '#c7d2fe', light: '#3730a3' },
  },
  sales: {
    bg: { dark: 'rgba(76,225,182,0.22)', light: 'rgba(20,184,166,0.16)' },
    color: { dark: '#ccfbf1', light: '#0f766e' },
  },
  tax: {
    bg: { dark: 'rgba(245,158,11,0.22)', light: 'rgba(245,158,11,0.16)' },
    color: { dark: '#fde68a', light: '#92400e' },
  },
  due: {
    bg: { dark: 'rgba(249,115,22,0.22)', light: 'rgba(249,115,22,0.16)' },
    color: { dark: '#fed7aa', light: '#9a3412' },
  },
  rooms: {
    bg: { dark: 'rgba(14,165,233,0.22)', light: 'rgba(14,165,233,0.14)' },
    color: { dark: '#bae6fd', light: '#075985' },
  },
  tables: {
    bg: { dark: 'rgba(168,85,247,0.22)', light: 'rgba(168,85,247,0.14)' },
    color: { dark: '#e9d5ff', light: '#6b21a8' },
  },
  menu: {
    bg: { dark: 'rgba(76,225,182,0.22)', light: 'rgba(20,184,166,0.16)' },
    color: { dark: '#ccfbf1', light: '#0f766e' },
  },
};

const renderPosHeroMetricChips = (
  activeTab,
  isDark,
  isBootstrapDataLoading,
  dashboardAnalytics,
  tabMetrics,
  formatCurrency,
) => {
  if (isBootstrapDataLoading) {
    return (
      <>
        <PosHeroChipSkeleton />
        <PosHeroChipSkeleton />
        <PosHeroChipSkeleton />
      </>
    );
  }

  const chipConfigsByTab = {
    [TAB_VALUES.DASHBOARD]: [
      { label: `Orders ${dashboardAnalytics.totalOrders}`, palette: POS_HERO_CHIP_PALETTES.orders },
      { label: `Sales ${formatCurrency(dashboardAnalytics.totalSales)}`, palette: POS_HERO_CHIP_PALETTES.sales },
      { label: `Tax ${formatCurrency(dashboardAnalytics.totalTax)}`, palette: POS_HERO_CHIP_PALETTES.tax },
    ],
    [TAB_VALUES.ORDERS]: [
      { label: `Orders ${tabMetrics.ordersCount}`, palette: POS_HERO_CHIP_PALETTES.orders },
      { label: `Sales ${formatCurrency(tabMetrics.ordersSales)}`, palette: POS_HERO_CHIP_PALETTES.sales },
      { label: `Due ${formatCurrency(tabMetrics.ordersDue)}`, palette: POS_HERO_CHIP_PALETTES.due },
    ],
    [TAB_VALUES.ROOM]: [
      { label: `Rooms ${tabMetrics.roomsCount}`, palette: POS_HERO_CHIP_PALETTES.rooms },
      { label: `Enabled ${tabMetrics.enabledRoomsCount}`, palette: POS_HERO_CHIP_PALETTES.sales },
      { label: tabMetrics.roomOrdersEnabled ? 'Room Orders On' : 'Room Orders Off', palette: POS_HERO_CHIP_PALETTES.menu },
    ],
    [TAB_VALUES.TABLE]: [
      { label: `Tables ${tabMetrics.tablesCount}`, palette: POS_HERO_CHIP_PALETTES.tables },
      { label: `Capacity ${tabMetrics.tablesCapacity}`, palette: POS_HERO_CHIP_PALETTES.orders },
      { label: `Active ${tabMetrics.activeTablesCount}`, palette: POS_HERO_CHIP_PALETTES.sales },
    ],
    [TAB_VALUES.MENU]: [
      { label: `Items ${tabMetrics.menuItemsCount}`, palette: POS_HERO_CHIP_PALETTES.menu },
      { label: `Categories ${tabMetrics.categoriesCount}`, palette: POS_HERO_CHIP_PALETTES.orders },
      { label: `Variations ${tabMetrics.variationsCount}`, palette: POS_HERO_CHIP_PALETTES.tax },
    ],
  };

  const chipConfigs = chipConfigsByTab[activeTab] || chipConfigsByTab[TAB_VALUES.DASHBOARD];

  return (
    <>
      {chipConfigs.map((chip) => (
        <Chip
          key={chip.label}
          size="small"
          label={chip.label}
          sx={getPosHeroChipSx(isDark, chip.palette)}
        />
      ))}
    </>
  );
};

const PosCardGridSkeleton = ({ count = 6, height = 88 }) => (
  <Grid container spacing={1}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={`pos-card-grid-skeleton-${index}`}>
        <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />
      </Grid>
    ))}
  </Grid>
);

const PosTableCardsSkeleton = ({ count = 6 }) => (
  <Grid container spacing={1}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={6} sm={4} key={`pos-table-card-skeleton-${index}`}>
        <Skeleton variant="rounded" height={112} sx={{ borderRadius: 2 }} />
      </Grid>
    ))}
  </Grid>
);

const PosVariationTableSkeleton = ({ rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <TableRow key={`pos-variation-skeleton-${rowIndex}`}>
        {Array.from({ length: 7 }).map((__, cellIndex) => (
          <TableCell key={`pos-variation-skeleton-${rowIndex}-${cellIndex}`}>
            <Skeleton variant="text" width={cellIndex === 0 ? '72%' : '58%'} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const PosCategoryCardsSkeleton = ({ count = 4 }) => (
  <Grid container spacing={1}>
    {Array.from({ length: count }).map((_, index) => (
      <Grid item xs={12} md={6} key={`pos-category-skeleton-${index}`}>
        <Skeleton variant="rounded" height={148} sx={{ borderRadius: 2 }} />
      </Grid>
    ))}
  </Grid>
);

const DashboardMetricCard = ({ title, value, subtitle, icon: Icon, isDark, accent, isLoading = false }) => (
  <Card
    elevation={0}
    sx={{
      width: '100%',
      height: '100%',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
      position: 'relative',
      background: isDark
        ? `linear-gradient(155deg, ${alpha(accent, 0.14)} 0%, rgba(28,28,34,0.98) 42%, #16161b 100%)`
        : `linear-gradient(155deg, ${alpha(accent, 0.22)} 0%, #ffffff 40%, #f4fbf8 100%)`,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.35)})`,
      },
    }}
  >
    <CardContent
      sx={{
        pt: 2.2,
        pb: 1.8,
        px: 2.2,
        '&:last-child': { pb: 1.8 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
        <Box sx={{ minWidth: 0, pr: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.04}>
            {title}
          </Typography>
          {isLoading ? (
            <Box sx={{ mt: 0.65 }}>
              <Skeleton variant="text" width={110} height={40} />
              <Skeleton variant="text" width={86} height={22} sx={{ mt: 0.4 }} />
            </Box>
          ) : (
            <>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, lineHeight: 1.2, letterSpacing: -0.4 }}>
                {value}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.9, fontWeight: 700 }}>
                {subtitle}
              </Typography>
            </>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDark ? alpha(accent, 0.16) : alpha(accent, 0.2),
            color: accent,
          }}
        >
          {Icon ? <Icon sx={{ fontSize: 24 }} /> : null}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const ConfigCardHeader = ({ icon: Icon, title, subtitle, isDark, action = null }) => {
  const tokens = getPosThemeColors(isDark);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <Box sx={getPosConfigHeaderIconSx(isDark)}>
        <Icon sx={{ fontSize: 26, color: ACCENT_COLOR }} />
      </Box>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: tokens.text }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.subtleText, mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>
      {action ? <Box sx={{ ml: 'auto', flexShrink: 0 }}>{action}</Box> : null}
    </Box>
  );
};

const DashboardSectionHeader = ({ icon: Icon, title, subtitle, isDark, accent = ACCENT_COLOR }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0 }}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '14px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${alpha(accent, 0.38)}`,
        background: isDark
          ? `linear-gradient(140deg, ${alpha(accent, 0.2)} 0%, rgba(255,255,255,0.03) 100%)`
          : `linear-gradient(140deg, ${alpha(accent, 0.18)} 0%, rgba(255,255,255,0.9) 100%)`,
      }}
    >
      <Icon sx={{ fontSize: 23, color: accent }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" fontWeight={800} sx={{ color: accent, letterSpacing: 0.12 }}>
        {title}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.3 }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const sortCategories = (rows = []) =>
  [...rows].sort((a, b) => {
    const firstSort = Number(a?.sort_order ?? 0);
    const secondSort = Number(b?.sort_order ?? 0);
    if (firstSort !== secondSort) return firstSort - secondSort;
    return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' });
  });

const PosRestaurantDashboard = () => {
  const canViewPosAudit = can(PERMISSIONS.POS_AUDIT_VIEW);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageToasts, setPageToasts] = useState([]);
  const [menuSectionTab, setMenuSectionTab] = useState(MENU_SECTION_VALUES.ITEMS);
  const [aiMenuUploaderOpen, setAiMenuUploaderOpen] = useState(false);
  const [dashboardPdfExporting, setDashboardPdfExporting] = useState(false);
  const [dashboardRange, setDashboardRange] = useState('today');
  const [dashboardDateAnchorEl, setDashboardDateAnchorEl] = useState(null);
  const [isCustomDateDialogOpen, setIsCustomDateDialogOpen] = useState(false);
  const [customDashboardRange, setCustomDashboardRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    startDate.setDate(startDate.getDate() - 6);
    return {
      startDate: formatDateInputValue(startDate),
      endDate: formatDateInputValue(today),
    };
  });
  const [customDashboardRangeDraft, setCustomDashboardRangeDraft] = useState(() => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    startDate.setDate(startDate.getDate() - 6);
    return {
      startDate: formatDateInputValue(startDate),
      endDate: formatDateInputValue(today),
    };
  });
  const [data, setData] = useState({
    tables: [],
    categories: [],
    menu_variations: [],
    menu_items: [],
    recent_orders: [],
    rooms: [],
    settings: {
      enable_room_order: true,
      gst_percentage: '0.00',
    },
  });
  const [settingsForm, setSettingsForm] = useState({
    enable_room_order: true,
    gst_percentage: '0.00',
  });
  const [tableForm, setTableForm] = useState({
    name: '',
    capacity: 4,
    extraInformation: '',
  });
  const [configuredRoomsSort, setConfiguredRoomsSort] = useState('room_number_asc');
  const [configuredTablesSort, setConfiguredTablesSort] = useState('name_asc');
  const [tableDeleteDialog, setTableDeleteDialog] = useState({
    open: false,
    tableId: null,
    tableName: '',
  });
  const [qrPreviewDialog, setQrPreviewDialog] = useState({
    open: false,
    loading: false,
    targetType: '',
    targetId: null,
    targetDisplay: '',
    targetMeta: {},
    landingUrl: '',
    qrDataUrl: '',
  });
  // Cached property branding for the downloadable QR stand artwork only.
  const qrStandBrandingRef = useRef(null);
  const [isDeletingTable, setIsDeletingTable] = useState(false);
  const [categoryDeleteDialog, setCategoryDeleteDialog] = useState({
    open: false,
    categoryId: null,
    categoryName: '',
  });
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    parent: '',
  });
  const [categoryDragState, setCategoryDragState] = useState({
    draggingId: null,
    draggingType: null,
    sourceParentId: null,
    overKey: null,
  });
  const [isSavingCategoryOrder, setIsSavingCategoryOrder] = useState(false);
  const [categoryDrawerContext, setCategoryDrawerContext] = useState({
    mode: 'parent',
    parentId: null,
    parentName: '',
  });
  const [menuItemForm, setMenuItemForm] = useState({
    name: '',
    short_code: '',
    description: '',
    category: '',
    price: '',
    dietary: 'veg',
    gst_type: 'services',
    enable_variation: false,
    order_room_service: true,
    order_pick_up: true,
    order_dine_in: true,
    is_available: true,
  });
  const [menuItemVariationRows, setMenuItemVariationRows] = useState([{ variation_id: '', price: '' }]);
  const [menuItemImageFile, setMenuItemImageFile] = useState(null);
  const [menuItemImagePreview, setMenuItemImagePreview] = useState('');
  const [editingMenuItemId, setEditingMenuItemId] = useState(null);
  const [menuItemViewDialog, setMenuItemViewDialog] = useState({
    open: false,
    menuItem: null,
  });
  const [menuItemDeleteDialog, setMenuItemDeleteDialog] = useState({
    open: false,
    menuItemId: null,
    menuItemName: '',
  });
  const [isDeletingMenuItem, setIsDeletingMenuItem] = useState(false);
  const [variationForm, setVariationForm] = useState({
    name: '',
    online_display_name: '',
    department_name: '',
    is_active: true,
  });
  const [editingVariationId, setEditingVariationId] = useState(null);
  const [variationDeleteDialog, setVariationDeleteDialog] = useState({
    open: false,
    variationId: null,
    variationName: '',
  });
  const [isDeletingVariation, setIsDeletingVariation] = useState(false);
  const [menuDrawer, setMenuDrawer] = useState({ open: false, type: MENU_SECTION_VALUES.ITEMS });
  const [menuDrawerSubmitting, setMenuDrawerSubmitting] = useState(false);
  const [menuItemFilters, setMenuItemFilters] = useState({
    search: '',
    category: '',
    dietary: '',
    status: '',
  });
  const [menuItemSortConfig, setMenuItemSortConfig] = useState({ column: 'name', direction: 'asc' });
  const [menuItemsPage, setMenuItemsPage] = useState(0);
  const [menuItemsRowsPerPage, setMenuItemsRowsPerPage] = useState(10);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [ordersFilters, setOrdersFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    status: '',
    order_type: '',
    criticality: '',
    payment_status: [],
    payment_method: [],
  });
  const [ordersSortConfig, setOrdersSortConfig] = useState({ column: 'created_at', direction: 'desc' });
  const [ordersDateRangeAnchorEl, setOrdersDateRangeAnchorEl] = useState(null);
  const [ordersPagination, setOrdersPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [orderViewDialog, setOrderViewDialog] = useState({
    open: false,
    order: null,
  });
  const [orderInvoiceDialog, setOrderInvoiceDialog] = useState({
    open: false,
    order: null,
  });
  const [thermalInvoicePreview, setThermalInvoicePreview] = useState({
    open: false,
    order: null,
    html: '',
    printing: false,
  });
  const [orderPaymentDrawer, setOrderPaymentDrawer] = useState({
    open: false,
    order: null,
  });
  const [orderDrawerMode, setOrderDrawerMode] = useState('view');
  const [orderViewLoading, setOrderViewLoading] = useState(false);
  const [orderAuditLogs, setOrderAuditLogs] = useState([]);
  const [orderAuditLoading, setOrderAuditLoading] = useState(false);
  const [orderAuditError, setOrderAuditError] = useState('');
  const [orderAuditDrawerOpen, setOrderAuditDrawerOpen] = useState(false);
  const [orderEditForm, setOrderEditForm] = useState(null);
  const [orderEditDraftItem, setOrderEditDraftItem] = useState({
    menu_item: '',
    quantity: 1,
    special_note: '',
  });
  const [orderEditSaving, setOrderEditSaving] = useState(false);
  const [placeOrderEditor, setPlaceOrderEditor] = useState({
    open: false,
    orderId: null,
  });

  const isDark = currentTheme === 'dark';
  const posThemeColors = getPosThemeColors(isDark);
  const pageShellSx = getPosPageShellSx(isDark ? 'dark' : 'light');
  const isDashboardDateMenuOpen = Boolean(dashboardDateAnchorEl);
  const selectedDashboardDateOption = useMemo(
    () => DASHBOARD_DATE_OPTIONS.find((option) => option.value === dashboardRange) || DASHBOARD_DATE_OPTIONS[0],
    [dashboardRange]
  );
  const selectedDashboardDateLabel = useMemo(() => {
    if (dashboardRange !== 'custom') return selectedDashboardDateOption.label;
    const { startDate, endDate } = customDashboardRange;
    if (!startDate || !endDate) return 'Custom Date';
    return `${formatSalesTrendDateLabel(startDate)} - ${formatSalesTrendDateLabel(endDate)}`;
  }, [dashboardRange, selectedDashboardDateOption.label, customDashboardRange]);
  const SelectedDashboardDateIcon = DASHBOARD_DATE_ICON_MAP[selectedDashboardDateOption.value] || CalendarMonthIcon;
  const shouldShowDashboardSkeleton = activeTab === TAB_VALUES.DASHBOARD && (loading || isRefreshing);
  const isBootstrapDataLoading = loading;
  const activeHeader = TAB_HEADER_CONFIG[activeTab] || TAB_HEADER_CONFIG[TAB_VALUES.DASHBOARD];
  const activeMenuDrawerType = menuDrawer.type || MENU_SECTION_VALUES.ITEMS;
  const menuDrawerMeta = useMemo(() => {
    if (activeMenuDrawerType === MENU_SECTION_VALUES.CATEGORIES) {
      const isChildMode = categoryDrawerContext.mode === 'child';
      return {
        icon: CategoryIcon,
        title: isChildMode ? 'Add Child Category' : 'Add Category',
        subtitle: isChildMode
          ? `Create child category under ${categoryDrawerContext.parentName || 'selected parent'}`
          : 'Create a parent category',
        actionLabel: isChildMode ? 'Save Child Category' : 'Save Category',
      };
    }
    if (activeMenuDrawerType === MENU_SECTION_VALUES.VARIATIONS) {
      const isEditMode = Boolean(editingVariationId);
      return {
        icon: TuneIcon,
        title: isEditMode ? 'Edit Variation' : 'Add Variation',
        subtitle: 'Create and manage menu variation setup',
        actionLabel: isEditMode ? 'Save Changes' : 'Add Variation',
      };
    }
    const isEditMode = Boolean(editingMenuItemId);
    return {
      icon: RestaurantMenuIcon,
      title: isEditMode ? 'Edit Menu Item' : 'Add Menu Item',
      subtitle: isEditMode ? 'Update menu item details and pricing' : 'Create and publish menu items',
      actionLabel: isEditMode ? 'Save Changes' : 'Save Menu Item',
    };
  }, [
    activeMenuDrawerType,
    categoryDrawerContext.mode,
    categoryDrawerContext.parentName,
    editingVariationId,
    editingMenuItemId,
  ]);
  const ActiveMenuDrawerIcon = menuDrawerMeta.icon;

  useEffect(() => {
    const cleanup = watchThemeChanges((theme) => setCurrentTheme(theme));
    return cleanup;
  }, []);

  useEffect(() => {
    if (!menuItemImageFile) return;
    const objectUrl = URL.createObjectURL(menuItemImageFile);
    setMenuItemImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [menuItemImageFile]);

  const setToastMessage = useCallback((severity, message) => {
    if (message == null || message === '') return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const createdAt = Date.now();
    setPageToasts((prev) => [...prev, { id, message: String(message), severity, createdAt }]);
    window.setTimeout(() => {
      setPageToasts((prev) => prev.filter((t) => t.id !== id));
    }, 7000);
  }, []);

  const dismissPageToast = useCallback((id) => {
    setPageToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleSelectDashboardRange = useCallback(
    (value) => {
      if (value === 'custom') {
        setCustomDashboardRangeDraft(customDashboardRange);
        setIsCustomDateDialogOpen(true);
      } else {
        setDashboardRange(value);
      }
      setDashboardDateAnchorEl(null);
    },
    [customDashboardRange]
  );

  const handleApplyCustomDashboardRange = useCallback(() => {
    const startDate = customDashboardRangeDraft.startDate;
    const endDate = customDashboardRangeDraft.endDate;
    if (!startDate || !endDate) {
      setToastMessage('warning', 'Please select both From and To dates.');
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setToastMessage('warning', 'Invalid date selected for custom range.');
      return;
    }
    if (start > end) {
      setToastMessage('warning', 'From date should be before or equal to To date.');
      return;
    }
    setCustomDashboardRange({ startDate, endDate });
    setDashboardRange('custom');
    setIsCustomDateDialogOpen(false);
  }, [customDashboardRangeDraft.endDate, customDashboardRangeDraft.startDate, setToastMessage]);

  const loadOrders = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setOrdersLoading(true);
      setOrdersError('');
    }
    try {
      const payload = await getPosOrders({ view: 'merged_qr_sessions' });
      const rows = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload?.Data) ? payload.Data : []));
      setOrders(rows);
    } catch (err) {
      if (!silent) {
        setOrdersError(err?.userMessage || 'Unable to load POS orders.');
      }
    } finally {
      if (!silent) {
        setOrdersLoading(false);
      }
    }
  }, []);

  const handleOrderInvoiceDownload = useCallback(
    (order) => {
      const orderId = Number(order?.id || 0);
      if (!orderId) {
        setToastMessage('warning', 'Order reference missing, unable to open invoice.');
        return;
      }
      setOrderInvoiceDialog({ open: true, order });
    },
    [setToastMessage]
  );

  const closeOrderInvoiceDialog = useCallback(() => {
    setOrderInvoiceDialog({ open: false, order: null });
  }, []);

  const openThermalInvoicePreview = useCallback((order) => {
    if (!order?.id && !order?.order_number) {
      setToastMessage('warning', 'Order reference missing, unable to open invoice preview.');
      return;
    }
    const financial = getOrderFinancialSummary(order);
    const html = buildRestaurantReceiptHtml(order, 'bill', {
      autoPrintOnLoad: false,
      showPreviewToolbar: false,
    }, {
      hotel: data?.hotel || data?.selected_hotel || {},
      menuItems: data?.menu_items || [],
      menuVariations: data?.menu_variations || [],
      targetLabel: order?.room_number || order?.table_name || '',
      orderTypeLabel: getOrderTypeLabel(order?.order_type),
      customerName: order?.customer_name || '',
      customerPhone: order?.customer_phone || '',
      subtotal: financial.subtotal,
      discountAmount: financial.discountAmount,
      deliveryCharge: financial.deliveryCharge,
      containerCharge: financial.containerCharge,
      grandTotal: financial.total,
      taxBreakdown: Array.isArray(order?.tax_breakdown)
        ? order.tax_breakdown
        : (typeof order?.tax_breakdown === 'string'
          ? (() => {
              try {
                const parsed = JSON.parse(order.tax_breakdown);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            })()
          : []),
      notes: order?.notes || '',
    });
    setThermalInvoicePreview({
      open: true,
      order,
      html,
      printing: false,
    });
  }, [data?.hotel, data?.menu_items, data?.menu_variations, data?.selected_hotel, setToastMessage]);

  const closeThermalInvoicePreview = useCallback(() => {
    setThermalInvoicePreview({ open: false, order: null, html: '', printing: false });
  }, []);

  const handleThermalInvoicePrint = useCallback(async () => {
    const html = thermalInvoicePreview.html;
    const order = thermalInvoicePreview.order;
    if (!html) {
      setToastMessage('warning', 'Invoice preview is empty.');
      return;
    }
    setThermalInvoicePreview((prev) => ({ ...prev, printing: true }));
    try {
      const invoiceTitle = `Invoice - ${formatReceiptOrderNumber(order?.order_number || order?.id || 'draft')}`;
      const result = await printRestaurantReceiptHtml(html, invoiceTitle);
      if (!result?.opened) {
        if (result?.reason === 'printer_not_validated') {
          setToastMessage('warning', 'Please validate the printer before printing.');
          openPrinterValidationDialog();
        } else if (result?.reason === 'print_failed') {
          setToastMessage('error', `Print failed: ${result.error || 'check printer'}`);
        } else {
          setToastMessage('warning', 'Print was blocked. Allow popups and try again.');
        }
        return;
      }
      setToastMessage('success', 'Invoice sent to printer.');
    } finally {
      setThermalInvoicePreview((prev) => ({ ...prev, printing: false }));
    }
  }, [setToastMessage, thermalInvoicePreview.html, thermalInvoicePreview.order]);

  const handleThermalInvoiceDownload = useCallback(() => {
    const html = thermalInvoicePreview.html;
    const order = thermalInvoicePreview.order;
    if (!html) {
      setToastMessage('warning', 'Invoice preview is empty.');
      return;
    }
    const orderRef = formatReceiptOrderNumber(order?.order_number || order?.id || 'invoice');
    const ok = downloadRestaurantReceiptHtml(html, `Invoice-${orderRef}.html`);
    if (!ok) {
      setToastMessage('error', 'Unable to download invoice.');
      return;
    }
    setToastMessage('success', 'Invoice downloaded.');
  }, [setToastMessage, thermalInvoicePreview.html, thermalInvoicePreview.order]);

  const openOrderPaymentDrawer = useCallback((order) => {
    const orderId = Number(order?.id || 0);
    if (!orderId) {
      setToastMessage('warning', 'Order reference missing, unable to manage payments.');
      return;
    }
    setOrderPaymentDrawer({ open: true, order });
  }, [setToastMessage]);

  const closeOrderPaymentDrawer = useCallback(() => {
    setOrderPaymentDrawer({ open: false, order: null });
  }, []);

  const handleOrderPaymentsChanged = useCallback(() => {
    loadOrders({ silent: true });
  }, [loadOrders]);

  const openPlaceOrderEditor = useCallback((order) => {
    if (order?.is_qr_table_session_group) {
      setToastMessage('info', 'QR table session groups are edited from the individual live queue orders.');
      return;
    }
    const orderId = Number(order?.id || 0);
    if (!orderId) {
      setToastMessage('warning', 'Order reference missing, unable to edit.');
      return;
    }
    setPlaceOrderEditor({ open: true, orderId });
  }, [setToastMessage]);

  const closePlaceOrderEditor = useCallback(() => {
    setPlaceOrderEditor({ open: false, orderId: null });
  }, []);

  const handlePlaceOrderEditorSaved = useCallback(() => {
    loadOrders();
  }, [loadOrders]);


  const openOrderViewDialog = useCallback(async (order, mode = 'view') => {
    setOrderAuditLogs([]);
    setOrderAuditError('');
    setOrderAuditLoading(false);
    setOrderDrawerMode(mode === 'edit' ? 'edit' : 'view');
    setOrderEditSaving(false);
    setOrderEditDraftItem({ menu_item: '', quantity: 1, special_note: '' });
    setOrderAuditDrawerOpen(false);
    if (!order?.id) {
      setOrderViewLoading(false);
      setOrderViewDialog({ open: true, order });
      setOrderEditForm(buildOrderEditFormFromOrder(order || {}));
      return;
    }
    setOrderViewLoading(true);
    setOrderAuditLoading(Boolean(canViewPosAudit));
    setOrderViewDialog({ open: true, order });
    setOrderEditForm(buildOrderEditFormFromOrder(order));
    try {
      const requests = [getPosOrderDetail(order.id)];
      if (canViewPosAudit) {
        requests.push(getPosOrderAuditLogs(order.id));
      }
      const [detailResult, auditResult] = await Promise.allSettled(requests);
      if (detailResult.status === 'fulfilled') {
        const detailedOrder = detailResult.value || order;
        setOrderViewDialog({ open: true, order: detailedOrder });
        setOrderEditForm(buildOrderEditFormFromOrder(detailedOrder));
      }
      if (canViewPosAudit && auditResult) {
        if (auditResult.status === 'fulfilled') {
          const rows = Array.isArray(auditResult.value)
            ? auditResult.value
            : (Array.isArray(auditResult.value?.results) ? auditResult.value.results : []);
          setOrderAuditLogs(rows);
          setOrderAuditError('');
        } else {
          const err = auditResult.reason;
          setOrderAuditLogs([]);
          setOrderAuditError(
            err?.response?.data?.error
            || err?.userMessage
            || 'Unable to load audit timeline for this order.'
          );
        }
      }
    } catch {
      // Keep already available row data if detail fetch fails.
    } finally {
      setOrderViewLoading(false);
      setOrderAuditLoading(false);
    }
  }, [canViewPosAudit]);
  const closeOrderViewPanel = useCallback(() => {
    setOrderViewDialog({ open: false, order: null });
    setOrderDrawerMode('view');
    setOrderViewLoading(false);
    setOrderAuditLogs([]);
    setOrderAuditError('');
    setOrderAuditLoading(false);
    setOrderEditForm(null);
    setOrderEditDraftItem({ menu_item: '', quantity: 1, special_note: '' });
    setOrderEditSaving(false);
    setOrderAuditDrawerOpen(false);
  }, []);

  const refreshOrderAuditTimeline = useCallback(async (orderId = null) => {
    const resolvedOrderId = Number(orderId || orderViewDialog.order?.id || 0);
    if (!resolvedOrderId || !canViewPosAudit) return;
    setOrderAuditLoading(true);
    setOrderAuditError('');
    try {
      const logs = await getPosOrderAuditLogs(resolvedOrderId);
      const rows = Array.isArray(logs) ? logs : (Array.isArray(logs?.results) ? logs.results : []);
      setOrderAuditLogs(rows);
    } catch (err) {
      setOrderAuditLogs([]);
      setOrderAuditError(
        err?.response?.data?.error
        || err?.userMessage
        || 'Unable to refresh audit timeline.'
      );
      setToastMessage('error', err?.userMessage || 'Unable to refresh audit timeline.');
    } finally {
      setOrderAuditLoading(false);
    }
  }, [canViewPosAudit, orderViewDialog.order?.id, setToastMessage]);

  const handleChangeOrderEditField = useCallback((field, value) => {
    setOrderEditForm((prev) => ({ ...(prev || {}), [field]: value }));
  }, []);

  const handleChangeOrderEditItem = useCallback((index, patch) => {
    setOrderEditForm((prev) => {
      const nextRows = Array.isArray(prev?.order_items) ? [...prev.order_items] : [];
      nextRows[index] = { ...nextRows[index], ...patch };
      return { ...(prev || {}), order_items: nextRows };
    });
  }, []);

  const handleRemoveOrderEditItem = useCallback((index) => {
    setOrderEditForm((prev) => {
      const nextRows = Array.isArray(prev?.order_items) ? prev.order_items.filter((_, idx) => idx !== index) : [];
      return { ...(prev || {}), order_items: nextRows };
    });
  }, []);

  const handleAddOrderEditItem = useCallback(() => {
    const menuItemId = Number(orderEditDraftItem.menu_item || 0);
    const quantity = Math.max(1, Number(orderEditDraftItem.quantity || 1));
    if (!menuItemId) {
      setToastMessage('warning', 'Select a menu item to add.');
      return;
    }
    const selectedMenuItem = (data.menu_items || []).find((item) => Number(item.id) === menuItemId);
    setOrderEditForm((prev) => ({
      ...(prev || {}),
      order_items: [
        ...(Array.isArray(prev?.order_items) ? prev.order_items : []),
        {
          menu_item: menuItemId,
          item_name: selectedMenuItem?.name || `Item #${menuItemId}`,
          quantity,
          special_note: String(orderEditDraftItem.special_note || ''),
        },
      ],
    }));
    setOrderEditDraftItem({ menu_item: '', quantity: 1, special_note: '' });
  }, [orderEditDraftItem.menu_item, orderEditDraftItem.quantity, orderEditDraftItem.special_note, data.menu_items, setToastMessage]);

  const handleSaveOrderEdits = useCallback(async () => {
    const orderId = Number(orderViewDialog.order?.id || 0);
    if (!orderId) {
      setToastMessage('error', 'Unable to update order.');
      return;
    }
    const orderItems = (Array.isArray(orderEditForm?.order_items) ? orderEditForm.order_items : [])
      .filter((row) => Number(row?.menu_item || 0) > 0 && Number(row?.quantity || 0) > 0)
      .map((row) => ({
        menu_item: Number(row.menu_item),
        quantity: Number(row.quantity),
        special_note: String(row.special_note || '').trim(),
      }));
    if (orderItems.length === 0) {
      setToastMessage('warning', 'Add at least one order item.');
      return;
    }
    const status = String(orderEditForm?.status || 'placed');
    const paymentCollectionMode = String(orderEditForm?.payment_collection_mode || 'collect_now');
    const paymentMethod = String(orderEditForm?.payment_method || '').trim();
    if (status === 'completed' && paymentCollectionMode === 'collect_now' && !paymentMethod) {
      setToastMessage('warning', 'Payment method is required when payment mode is Collect now.');
      return;
    }
    const payload = {
      order_type: String(orderEditForm?.order_type || 'dine_in'),
      status,
      customer_name: String(orderEditForm?.customer_name || '').trim(),
      customer_phone_country_code: String(orderEditForm?.customer_phone_country_code || '+91').trim() || '+91',
      customer_phone: String(orderEditForm?.customer_phone || '').trim(),
      payment_collection_mode: paymentCollectionMode,
      payment_method: paymentMethod,
      preparation_time_minutes: Math.max(5, Number(orderEditForm?.preparation_time_minutes || 30)),
      notes: String(orderEditForm?.notes || ''),
      order_items: orderItems,
    };
    setOrderEditSaving(true);
    try {
      const updatedOrder = await updatePosOrder(orderId, payload);
      setOrderViewDialog({ open: true, order: updatedOrder || orderViewDialog.order });
      setOrderEditForm(buildOrderEditFormFromOrder(updatedOrder || orderViewDialog.order || {}));
      setOrderDrawerMode('view');
      setToastMessage('success', `Order ${formatOrderNumber(updatedOrder?.order_number)} updated successfully.`);
      setOrders((prev) =>
        prev.map((row) => (Number(row.id) === Number(updatedOrder?.id) ? { ...row, ...updatedOrder } : row))
      );
      loadOrders();
      if (canViewPosAudit) {
        const logs = await getPosOrderAuditLogs(orderId);
        const logRows = Array.isArray(logs) ? logs : (Array.isArray(logs?.results) ? logs.results : []);
        setOrderAuditLogs(logRows);
      }
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to update order.');
    } finally {
      setOrderEditSaving(false);
    }
  }, [canViewPosAudit, orderViewDialog.order, orderEditForm, setToastMessage, loadOrders]);

  const loadData = useCallback(async ({ initial = false } = {}) => {
    if (initial) {
      setLoading(true);
      setError(null);
    } else {
      setIsRefreshing(true);
    }
    try {
      const payload = await getPosRestaurantBootstrap();
      const nextData = {
        tables: payload.tables || [],
        categories: payload.categories || [],
        menu_variations: payload.menu_variations || [],
        menu_items: payload.menu_items || [],
        recent_orders: payload.recent_orders || [],
        rooms: payload.rooms || [],
        settings: payload.settings || {
          enable_room_order: true,
          gst_percentage: '0.00',
        },
      };
      setData(nextData);
      setSettingsForm({
        enable_room_order: Boolean(nextData.settings.enable_room_order),
        gst_percentage: String(nextData.settings.gst_percentage ?? '0.00'),
      });
    } catch (err) {
      if (initial) {
        setError(err);
      } else {
        setToastMessage('error', err?.userMessage || 'Unable to refresh dashboard data.');
      }
    } finally {
      if (initial) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData({ initial: true });
    loadOrders({ silent: true });
  }, [loadData, loadOrders]);

  useEffect(() => {
    const nextTab = getTabFromSearch(location.search);
    setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [location.search]);

  useEffect(() => {
    if (activeTab === TAB_VALUES.ORDERS) {
      loadOrders();
    }
  }, [activeTab, loadOrders]);

  useEffect(() => {
    if (activeTab !== TAB_VALUES.ORDERS) return undefined;
    const intervalId = window.setInterval(() => {
      loadOrders({ silent: true });
    }, 9000);
    return () => window.clearInterval(intervalId);
  }, [activeTab, loadOrders]);

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    const params = new URLSearchParams(location.search);
    if (value === TAB_VALUES.DASHBOARD) {
      params.delete('tab');
    } else {
      params.set('tab', value);
    }
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  };

  const categoryLabelMap = useMemo(() => {
    const idToCategory = new Map(data.categories.map((category) => [category.id, category]));
    const labelMap = new Map();
    data.categories.forEach((category) => {
      const parent = category.parent ? idToCategory.get(category.parent) : null;
      const label = parent ? `${parent.name} / ${category.name}` : category.name;
      labelMap.set(category.id, label);
    });
    return labelMap;
  }, [data.categories]);
  const variationLabelMap = useMemo(
    () =>
      new Map(
        (data.menu_variations || []).map((variation) => [
          Number(variation.id),
          variation.online_display_name || variation.name || `Variation #${variation.id}`,
        ])
      ),
    [data.menu_variations]
  );
  const menuCategoryFilterOptions = useMemo(() => {
    const options = (data.categories || [])
      .map((category) => ({
        value: String(category.id),
        label: categoryLabelMap.get(category.id) || category.name || `Category #${category.id}`,
      }))
      .sort((first, second) => first.label.localeCompare(second.label, undefined, { sensitivity: 'base' }));
    return [{ value: 'uncategorized', label: 'Uncategorized' }, ...options];
  }, [data.categories, categoryLabelMap]);
  const handleMenuFilterChange = useCallback((field, value) => {
    setMenuItemFilters((prev) => ({ ...prev, [field]: value }));
    setMenuItemsPage(0);
  }, []);
  const handleMenuSortChange = useCallback((nextSortConfig) => {
    setMenuItemSortConfig(nextSortConfig);
    setMenuItemsPage(0);
  }, []);
  const handleMenuPageChange = useCallback((nextPage) => {
    setMenuItemsPage(nextPage);
  }, []);
  const handleMenuPerPageChange = useCallback((nextPerPage) => {
    setMenuItemsRowsPerPage(nextPerPage);
    setMenuItemsPage(0);
  }, []);
  const menuItemFilteredRows = useMemo(() => {
    const query = menuItemFilters.search.trim().toLowerCase();
    const rows = data.menu_items || [];
    return rows.filter((item) => {
      const normalizedCategory = item?.category ? String(item.category) : 'uncategorized';
      const normalizedDietary = String(item?.dietary || (item?.is_veg ? 'veg' : 'non_veg') || '').toLowerCase();
      const normalizedStatus = item?.is_available ? 'available' : 'unavailable';
      if (menuItemFilters.category && menuItemFilters.category !== normalizedCategory) return false;
      if (menuItemFilters.dietary && menuItemFilters.dietary !== normalizedDietary) return false;
      if (menuItemFilters.status && menuItemFilters.status !== normalizedStatus) return false;
      if (!query) return true;
      return String(item?.name || '').toLowerCase().includes(query);
    });
  }, [data.menu_items, menuItemFilters]);
  const menuItemsTableRows = useMemo(() => {
    const rows = [...menuItemFilteredRows];
    const { column, direction } = menuItemSortConfig;
    const getSortValue = (item) => {
      if (column === 'name') return item.name || '';
      if (column === 'short_code') return item.short_code || '';
      if (column === 'category')
        return (item.category ? categoryLabelMap.get(item.category) : item.category_name) || 'Uncategorized';
      if (column === 'price') return Number(item.price || 0);
      if (column === 'dietary') return item.dietary_label || (item.is_veg ? 'Veg' : 'Non-Veg');
      if (column === 'gst') return item.gst_type_label || item.gst_type || 'Services';
      if (column === 'status') return item.is_available ? 1 : 0;
      if (column === 'description') return item.description || '';
      return item.name || '';
    };
    rows.sort((firstItem, secondItem) => {
      const firstValue = getSortValue(firstItem);
      const secondValue = getSortValue(secondItem);
      let compareValue = 0;
      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        compareValue = firstValue - secondValue;
      } else {
        compareValue = String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: 'base' });
      }
      return direction === 'desc' ? compareValue * -1 : compareValue;
    });
    return rows;
  }, [menuItemFilteredRows, menuItemSortConfig, categoryLabelMap]);
  const paginatedMenuItems = useMemo(() => {
    const startIndex = menuItemsPage * menuItemsRowsPerPage;
    return menuItemsTableRows.slice(startIndex, startIndex + menuItemsRowsPerPage);
  }, [menuItemsTableRows, menuItemsPage, menuItemsRowsPerPage]);
  const menuItemsPagination = useMemo(
    () => ({
      page: menuItemsPage,
      perPage: menuItemsRowsPerPage,
      totalRecords: menuItemsTableRows.length,
    }),
    [menuItemsPage, menuItemsRowsPerPage, menuItemsTableRows.length]
  );
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(menuItemsTableRows.length / menuItemsRowsPerPage));
    if (menuItemsPage > totalPages - 1) {
      setMenuItemsPage(Math.max(0, totalPages - 1));
    }
  }, [menuItemsPage, menuItemsRowsPerPage, menuItemsTableRows.length]);

  const ordersWithComputedMeta = useMemo(
    () =>
      (orders || []).map((order) => {
        const timing = getOrderTimingInsight(order);
        const criticality = getCriticalityGroupMeta(timing);
        const sourceLabel = getOrderSourceLabel(order);
        const orderTypeLabel = getOrderTypeLabel(order.order_type);
        const paymentLabel = getOrderPaymentLabel(order);
        const paymentStatusLabel = getOrderPaymentStatusLabel(order);
        const normalizedOrderType = normalizeOrderTypeForDashboard(order.order_type);
        const createdAtTimestamp = new Date(order.created_at || 0).getTime();
        const orderDateKey = getLocalDateKey(order.created_at || '');
        const itemsCount = Number(order?.items?.length || 0);
        const searchableText = [
          order.order_number,
          sourceLabel,
          orderTypeLabel,
          order.status,
          paymentLabel,
          paymentStatusLabel,
        ]
          .map((value) => String(value || '').toLowerCase())
          .join(' ');
        return {
          ...order,
          _timing: timing,
          _criticality: criticality,
          _criticalityKey: criticality.key,
          _sourceLabel: sourceLabel,
          _orderTypeLabel: orderTypeLabel,
          _paymentLabel: paymentLabel,
          _paymentKey: String(paymentLabel || '').trim().toLowerCase().replace(/\s+/g, '_'),
          _paymentStatusLabel: paymentStatusLabel,
          _paymentStatusKey: String(paymentStatusLabel || '').trim().toLowerCase().replace(/\s+/g, '_'),
          _normalizedOrderType: normalizedOrderType,
          _itemsCount: itemsCount,
          _createdAtTimestamp: Number.isFinite(createdAtTimestamp) ? createdAtTimestamp : 0,
          _orderDateKey: orderDateKey,
          _searchableText: searchableText,
        };
      }),
    [orders]
  );

  const filteredOrders = useMemo(() => {
    const query = ordersFilters.search.trim().toLowerCase();
    return ordersWithComputedMeta.filter((order) => {
      if (ordersFilters.start_date && order._orderDateKey < ordersFilters.start_date) return false;
      if (ordersFilters.end_date && order._orderDateKey > ordersFilters.end_date) return false;
      if (ordersFilters.status && order.status !== ordersFilters.status) return false;
      if (ordersFilters.order_type && order._normalizedOrderType !== ordersFilters.order_type) return false;
      if (ordersFilters.criticality && order._criticalityKey !== ordersFilters.criticality) return false;
      if (ordersFilters.payment_status?.length) {
        const paymentStatusKey = resolvePosOrderPaymentDisplay(order).paymentStatus;
        if (!ordersFilters.payment_status.includes(paymentStatusKey)) return false;
      }
      if (ordersFilters.payment_method?.length) {
        const methodKeys = getOrderPaymentMethodKeys(order);
        if (!methodKeys.some((methodKey) => ordersFilters.payment_method.includes(methodKey))) return false;
      }
      if (!query) return true;
      return order._searchableText.includes(query);
    });
  }, [ordersWithComputedMeta, ordersFilters]);

  /** Summary scope ignores payment-method filter so method chips stay visible while filtering. */
  const ordersSummaryScope = useMemo(() => {
    const query = ordersFilters.search.trim().toLowerCase();
    return ordersWithComputedMeta.filter((order) => {
      if (ordersFilters.start_date && order._orderDateKey < ordersFilters.start_date) return false;
      if (ordersFilters.end_date && order._orderDateKey > ordersFilters.end_date) return false;
      if (ordersFilters.status && order.status !== ordersFilters.status) return false;
      if (ordersFilters.order_type && order._normalizedOrderType !== ordersFilters.order_type) return false;
      if (ordersFilters.criticality && order._criticalityKey !== ordersFilters.criticality) return false;
      if (ordersFilters.payment_status?.length) {
        const paymentStatusKey = resolvePosOrderPaymentDisplay(order).paymentStatus;
        if (!ordersFilters.payment_status.includes(paymentStatusKey)) return false;
      }
      if (!query) return true;
      return order._searchableText.includes(query);
    });
  }, [ordersWithComputedMeta, ordersFilters]);

  const ordersPaymentSummary = useMemo(() => {
    const paymentMethodTotals = new Map();
    let totalAmount = 0;
    let collectedAmount = 0;
    let billableOrderCount = 0;
    let cancelledAmount = 0;
    let cancelledCount = 0;
    const selectedMethodKeys = (Array.isArray(ordersFilters.payment_method) ? ordersFilters.payment_method : [])
      .map((key) => normalizePaymentMethodKey(key))
      .filter(Boolean);
    const hasMethodFilter = selectedMethodKeys.length > 0;
    const selectedMethodSet = new Set(selectedMethodKeys);

    filteredOrders.forEach((order) => {
      const payment = resolvePosOrderPaymentDisplay(order);
      if (['cancelled', 'canceled'].includes(String(order?.status || '').trim().toLowerCase())) {
        cancelledCount += 1;
        cancelledAmount += payment.totalAmount;
        return;
      }
      billableOrderCount += 1;
      if (hasMethodFilter) {
        const methodAmount = resolvePosOrderPaymentMethods(order, payment)
          .filter((method) => selectedMethodSet.has(method.key))
          .reduce((sum, method) => sum + method.amount, 0);
        totalAmount += methodAmount;
        collectedAmount += methodAmount;
      } else {
        totalAmount += payment.totalAmount;
        collectedAmount += payment.totalPaid;
      }
    });

    ordersSummaryScope.forEach((order) => {
      if (['cancelled', 'canceled'].includes(String(order?.status || '').trim().toLowerCase())) return;
      const payment = resolvePosOrderPaymentDisplay(order);
      resolvePosOrderPaymentMethods(order, payment).forEach((method) => {
        const existing = paymentMethodTotals.get(method.key) || {
          key: method.key,
          label: method.label,
          amount: 0,
        };
        existing.amount = roundBookingMoney(existing.amount + method.amount);
        paymentMethodTotals.set(method.key, existing);
      });
    });

    const safeTotal = roundBookingMoney(totalAmount);
    const safeCollected = roundBookingMoney(collectedAmount);
    const dueAmount = hasMethodFilter
      ? 0
      : roundBookingMoney(Math.max(0, safeTotal - safeCollected));
    const collectedShare = safeTotal > 0 ? Math.min(100, (safeCollected / safeTotal) * 100) : 0;
    const totalLabel = !hasMethodFilter
      ? 'Total'
      : selectedMethodKeys.length === 1
        ? `${formatPaymentMethodLabel(selectedMethodKeys[0])} collected`
        : 'Method collected';

    return {
      orderCount: billableOrderCount,
      totalAmount: safeTotal,
      collectedAmount: safeCollected,
      dueAmount,
      cancelledCount,
      cancelledAmount: roundBookingMoney(cancelledAmount),
      collectedShare,
      hasMethodFilter,
      totalLabel,
      paymentMethods: Array.from(paymentMethodTotals.values()).sort(
        (first, second) => second.amount - first.amount
      ),
    };
  }, [filteredOrders, ordersFilters.payment_method, ordersSummaryScope]);

  const toggleOrdersPaymentMethodFilter = useCallback((methodKey) => {
    const key = normalizePaymentMethodKey(methodKey);
    if (!key) return;
    setOrdersFilters((prev) => {
      const current = Array.isArray(prev.payment_method) ? prev.payment_method : [];
      const next = current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key];
      return { ...prev, payment_method: next };
    });
    setOrdersPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const clearOrdersPaymentMethodFilter = useCallback(() => {
    setOrdersFilters((prev) => ({ ...prev, payment_method: [] }));
    setOrdersPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const sortedOrders = useMemo(() => {
    const rows = [...filteredOrders];
    const { column, direction } = ordersSortConfig;
    const getValue = (order) => {
      if (column === 'order_number') return String(order.order_number || '');
      if (column === 'created_at') return Number(order._createdAtTimestamp || 0);
      if (column === 'order_type') return `${String(order._orderTypeLabel || '')} ${String(order._sourceLabel || '')}`;
      if (column === 'payment') return Number(order.total_amount || 0);
      if (column === 'payment_method') return getPaymentStatusLabel(resolvePosOrderPaymentDisplay(order).paymentStatus);
      if (column === 'status') return String(order.status || '');
      return Number(order._createdAtTimestamp || 0);
    };
    rows.sort((first, second) => {
      const firstValue = getValue(first);
      const secondValue = getValue(second);
      let compareValue = 0;
      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        compareValue = firstValue - secondValue;
      } else {
        compareValue = String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: 'base' });
      }
      return direction === 'desc' ? compareValue * -1 : compareValue;
    });
    return rows;
  }, [filteredOrders, ordersSortConfig]);

  useEffect(() => {
    setOrdersPagination((prev) => {
      const totalRecords = sortedOrders.length;
      const maxPage = Math.max(0, Math.ceil(totalRecords / prev.perPage) - 1);
      const safePage = Math.min(prev.page, maxPage);
      if (prev.totalRecords === totalRecords && prev.page === safePage) return prev;
      return {
        ...prev,
        totalRecords,
        page: safePage,
      };
    });
  }, [sortedOrders.length]);

  const paginatedOrders = useMemo(() => {
    const startIndex = ordersPagination.page * ordersPagination.perPage;
    return sortedOrders.slice(startIndex, startIndex + ordersPagination.perPage);
  }, [sortedOrders, ordersPagination.page, ordersPagination.perPage]);

  const handleOrdersFilterChange = useCallback((field, value) => {
    setOrdersFilters((prev) => ({ ...prev, [field]: value }));
    setOrdersPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const ordersDateRangeLabel = useMemo(() => {
    const dateFormat = getHotelDateFormat();
    const start = ordersFilters.start_date
      ? moment(ordersFilters.start_date, 'YYYY-MM-DD')
      : null;
    const end = ordersFilters.end_date
      ? moment(ordersFilters.end_date, 'YYYY-MM-DD')
      : null;
    const startLabel = start?.isValid() ? start.format(dateFormat) : '';
    const endLabel = end?.isValid() ? end.format(dateFormat) : '';
    if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
    if (startLabel) return `${startLabel} – …`;
    if (endLabel) return `… – ${endLabel}`;
    return '';
  }, [ordersFilters.start_date, ordersFilters.end_date]);

  const clearOrdersDateRangeFilter = useCallback((event) => {
    event?.stopPropagation?.();
    setOrdersFilters((prev) => ({ ...prev, start_date: '', end_date: '' }));
    setOrdersPagination((prev) => ({ ...prev, page: 0 }));
    setOrdersDateRangeAnchorEl(null);
  }, []);

  const handleOrdersSortChange = useCallback((nextSortConfig) => {
    setOrdersSortConfig(nextSortConfig);
    setOrdersPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleOrdersPageChange = useCallback((nextPage) => {
    setOrdersPagination((prev) => ({ ...prev, page: nextPage }));
  }, []);

  const handleOrdersPerPageChange = useCallback((nextPerPage) => {
    setOrdersPagination((prev) => ({ ...prev, perPage: nextPerPage, page: 0 }));
  }, []);

  const orderTableColumns = useMemo(
    () => [
      {
        field: 'order_number',
        label: 'Order #',
        sortable: true,
        render: (value, row) => (
          <Button
            variant="text"
            size="small"
            onClick={() => openOrderViewDialog(row)}
            sx={{ textTransform: 'none', p: 0, minWidth: 0, fontWeight: 800 }}
          >
            {formatOrderNumber(value)}
          </Button>
        ),
      },
      {
        field: 'created_at',
        label: 'Order Date',
        sortable: true,
        render: (value) => formatDateTime(value),
      },
      {
        field: 'order_type',
        label: 'Type / Source',
        sortable: true,
        render: (value, row) => (
          <Stack spacing={0.1}>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {row?._orderTypeLabel || getOrderTypeLabel(value)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
              {row?._sourceLabel || getOrderSourceLabel(row)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'sla_timing',
        label: 'SLA vs Serve',
        sortable: false,
        render: (_, row) => {
          const timing = row?._timing || getOrderTimingInsight(row);
          const criticalityGroup = row?._criticality || getCriticalityGroupMeta(timing);
          return (
            <Box sx={{ minWidth: 210, maxWidth: 250 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.35 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  SLA {timing.targetLabel}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: timing.criticalityColor }}>
                  {timing.serveLabel} {timing.actualLabel}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.4 }}>
                <Chip
                  size="small"
                  label={criticalityGroup.label}
                  sx={{
                    height: 20,
                    fontWeight: 800,
                    bgcolor: alpha(criticalityGroup.color, 0.16),
                    color: criticalityGroup.color,
                    '& .MuiChip-label': { px: 0.8, fontSize: 11 },
                  }}
                />
                <Typography variant="caption" sx={{ color: timing.criticalityColor, fontWeight: 700 }}>
                  {timing.delayMinutes > 0 ? `Delay ${timing.delayLabel}` : timing.criticalityLabel}
                </Typography>
              </Stack>
              <Box sx={{ position: 'relative' }}>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.16),
                    '& .MuiLinearProgress-bar': { display: 'none' },
                  }}
                />
                <LinearProgress
                  variant="determinate"
                  value={timing.progressPercent}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: 'transparent',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      backgroundColor: timing.criticalityColor,
                    },
                  }}
                />
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'status',
        label: 'Status',
        sortable: true,
        render: (value) => {
          const statusMeta = getOrderStatusMeta(value);
          return (
            <Chip
              size="small"
              label={statusMeta.label}
              sx={{
                bgcolor: `${statusMeta.color}22`,
                color: statusMeta.color,
                fontWeight: 700,
              }}
            />
          );
        },
      },
      {
        field: 'payment',
        label: 'Payment',
        sortable: true,
        render: (_, row) => <PosOrderFinanceCell row={row} />,
      },
      {
        field: 'payment_method',
        label: 'Payment Method',
        sortable: true,
        render: (_, row) => <PosOrderPaymentMethodCell row={row} isDark={isDark} />,
      },
      {
        field: 'actions',
        label: 'Actions',
        sortable: false,
        render: (_, row) => {
          const orderId = Number(row?.id || 0);
          const isQrTableSessionGroup = Boolean(row?.is_qr_table_session_group);
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <IconButton
                size="small"
                title={orderId ? 'Open restaurant invoice' : 'Order reference required'}
                onClick={() => handleOrderInvoiceDownload(row)}
                disabled={!orderId}
                sx={{
                  color: orderId ? (isDark ? '#ffd166' : '#a16207') : (isDark ? '#64748b' : '#94a3b8'),
                  bgcolor: orderId
                    ? (isDark ? 'rgba(250,204,21,0.15)' : 'rgba(250,204,21,0.18)')
                    : (isDark ? 'rgba(100,116,139,0.12)' : 'rgba(148,163,184,0.14)'),
                  border: '1px solid',
                  borderColor: orderId
                    ? (isDark ? 'rgba(250,204,21,0.45)' : 'rgba(202,138,4,0.4)')
                    : (isDark ? 'rgba(100,116,139,0.3)' : 'rgba(148,163,184,0.34)'),
                }}
              >
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            <IconButton
              size="small"
              title="View details"
              onClick={() => openOrderViewDialog(row)}
              sx={{
                color: isDark ? '#e9f1ff' : '#1f2d45',
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(31,45,69,0.08)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(31,45,69,0.15)',
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              title="Manage payments"
              onClick={() => openOrderPaymentDrawer(row)}
              disabled={!orderId}
              sx={{
                color: orderId ? (isDark ? '#bbf7d0' : '#15803d') : (isDark ? '#64748b' : '#94a3b8'),
                bgcolor: orderId
                  ? (isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.1)')
                  : (isDark ? 'rgba(100,116,139,0.12)' : 'rgba(148,163,184,0.14)'),
                border: '1px solid',
                borderColor: orderId
                  ? (isDark ? 'rgba(34,197,94,0.35)' : 'rgba(21,128,61,0.28)')
                  : (isDark ? 'rgba(100,116,139,0.3)' : 'rgba(148,163,184,0.34)'),
              }}
            >
              <PaymentsIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              title={isQrTableSessionGroup ? 'QR table session groups are edited through individual queue orders' : 'Edit order'}
              onClick={() => openPlaceOrderEditor(row)}
              disabled={isQrTableSessionGroup}
              sx={{
                color: isQrTableSessionGroup
                  ? (isDark ? '#64748b' : '#94a3b8')
                  : (isDark ? '#c9f7eb' : '#0f766e'),
                bgcolor: isQrTableSessionGroup
                  ? (isDark ? 'rgba(100,116,139,0.12)' : 'rgba(148,163,184,0.14)')
                  : (isDark ? 'rgba(76,225,182,0.1)' : 'rgba(20,184,166,0.1)'),
                border: '1px solid',
                borderColor: isQrTableSessionGroup
                  ? (isDark ? 'rgba(100,116,139,0.3)' : 'rgba(148,163,184,0.34)')
                  : (isDark ? 'rgba(76,225,182,0.3)' : 'rgba(20,184,166,0.24)'),
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            </Stack>
          );
        },
      },
    ],
    [handleOrderInvoiceDownload, isDark, openOrderPaymentDrawer, openOrderViewDialog, openPlaceOrderEditor]
  );

  const orderFilterRowCells = useMemo(
    () =>
      orderTableColumns.map((column) => {
        if (column.field === 'order_number') {
          return (
            <FormTextField
              key={column.field}
              size="small"
              label="Order #"
              value={ordersFilters.search}
              onChange={(e) => handleOrdersFilterChange('search', e.target.value)}
              sx={{ minWidth: 130 }}
            />
          );
        }
        if (column.field === 'order_type') {
          return (
            <FormSelect
              key={column.field}
              label=""
              value={ordersFilters.order_type}
              onChange={(e) => handleOrdersFilterChange('order_type', e.target.value)}
              displayEmpty
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="dine_in">Dine In</MenuItem>
              <MenuItem value="room_service">Room Service</MenuItem>
              <MenuItem value="takeaway">Pick Up / Delivery</MenuItem>
            </FormSelect>
          );
        }
        if (column.field === 'created_at') {
          const hasDateRange = Boolean(ordersFilters.start_date || ordersFilters.end_date);
          return (
            <Box key={column.field}>
              <TextField
                size="small"
                label="Date range"
                placeholder="Select range"
                value={ordersDateRangeLabel}
                onClick={(event) => setOrdersDateRangeAnchorEl(event.currentTarget)}
                inputProps={{ readOnly: true }}
                sx={{
                  minWidth: 130,
                  width: '100%',
                  maxWidth: 160,
                  '& .MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {hasDateRange ? (
                        <IconButton
                          size="small"
                          aria-label="Clear date range"
                          onClick={clearOrdersDateRangeFilter}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Popover
                open={Boolean(ordersDateRangeAnchorEl)}
                anchorEl={ordersDateRangeAnchorEl}
                onClose={() => setOrdersDateRangeAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Stack spacing={1.5} sx={{ p: 2, minWidth: 260 }}>
                    <DatePicker
                      format={getHotelDateFormat()}
                      value={ordersFilters.start_date ? moment(ordersFilters.start_date, 'YYYY-MM-DD') : null}
                      onChange={(nextValue) =>
                        handleOrdersFilterChange(
                          'start_date',
                          nextValue && nextValue.isValid && nextValue.isValid()
                            ? nextValue.format('YYYY-MM-DD')
                            : ''
                        )}
                      slotProps={{
                        textField: {
                          size: 'small',
                          label: 'From',
                          fullWidth: true,
                        },
                      }}
                    />
                    <DatePicker
                      format={getHotelDateFormat()}
                      value={ordersFilters.end_date ? moment(ordersFilters.end_date, 'YYYY-MM-DD') : null}
                      minDate={
                        ordersFilters.start_date
                          ? moment(ordersFilters.start_date, 'YYYY-MM-DD')
                          : undefined
                      }
                      onChange={(nextValue) =>
                        handleOrdersFilterChange(
                          'end_date',
                          nextValue && nextValue.isValid && nextValue.isValid()
                            ? nextValue.format('YYYY-MM-DD')
                            : ''
                        )}
                      slotProps={{
                        textField: {
                          size: 'small',
                          label: 'To',
                          fullWidth: true,
                        },
                      }}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={clearOrdersDateRangeFilter}>
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setOrdersDateRangeAnchorEl(null)}
                        sx={{ bgcolor: ACCENT_COLOR, color: '#04120e', '&:hover': { bgcolor: ACCENT_COLOR } }}
                      >
                        Done
                      </Button>
                    </Stack>
                  </Stack>
                </LocalizationProvider>
              </Popover>
            </Box>
          );
        }
        if (column.field === 'status') {
          return (
            <FormSelect
              key={column.field}
              label=""
              value={ordersFilters.status}
              onChange={(e) => handleOrdersFilterChange('status', e.target.value)}
              displayEmpty
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="placed">Placed</MenuItem>
              <MenuItem value="preparing">Preparing</MenuItem>
              <MenuItem value="served">Served</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </FormSelect>
          );
        }
        if (column.field === 'sla_timing') {
          return (
            <FormSelect
              key={column.field}
              label=""
              value={ordersFilters.criticality}
              onChange={(e) => handleOrdersFilterChange('criticality', e.target.value)}
              displayEmpty
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All Criticality</MenuItem>
              <MenuItem value="on_track">On Track</MenuItem>
              <MenuItem value="watch">Watch</MenuItem>
              <MenuItem value="delayed">Delayed</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </FormSelect>
          );
        }
        if (column.field === 'payment') {
          return (
            <Box key={column.field} onClick={(event) => event.stopPropagation()}>
              <PosOrdersCheckboxMultiFilter
                label="Payment"
                emptyLabel="All"
                options={POS_PAYMENT_STATUS_FILTER_OPTIONS}
                value={ordersFilters.payment_status}
                onChange={(nextValue) => handleOrdersFilterChange('payment_status', nextValue)}
                isDark={isDark}
                minWidth={140}
              />
            </Box>
          );
        }
        if (column.field === 'payment_method') {
          return (
            <Box key={column.field} onClick={(event) => event.stopPropagation()}>
              <PosOrdersCheckboxMultiFilter
                label="Method"
                emptyLabel="All"
                options={POS_PAYMENT_METHOD_FILTER_OPTIONS}
                value={ordersFilters.payment_method}
                onChange={(nextValue) => handleOrdersFilterChange('payment_method', nextValue)}
                isDark={isDark}
                minWidth={140}
              />
            </Box>
          );
        }
        if (column.field === 'actions') {
          return <div key={column.field} />;
        }
        return <div key={column.field} />;
      }),
    [orderTableColumns, ordersFilters, handleOrdersFilterChange, isDark, ordersDateRangeLabel, ordersDateRangeAnchorEl, clearOrdersDateRangeFilter]
  );

  const parentCategories = useMemo(
    () => sortCategories(data.categories.filter((category) => !category.parent)),
    [data.categories]
  );
  const childCategoriesByParent = useMemo(() => {
    const grouped = new Map();
    data.categories.forEach((category) => {
      if (!category.parent) return;
      const current = grouped.get(category.parent) || [];
      current.push(category);
      grouped.set(category.parent, sortCategories(current));
    });
    return grouped;
  }, [data.categories]);
  const selectableMenuCategories = useMemo(
    () => sortCategories(data.categories.filter((category) => Boolean(category.parent))),
    [data.categories]
  );

  const dashboardOrders = useMemo(() => {
    const sourceOrders = orders || [];
    if (dashboardRange === 'all') {
      return sourceOrders;
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(startOfToday);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfLast7 = new Date(startOfToday);
    startOfLast7.setDate(startOfLast7.getDate() - 6);
    const startOfLast30 = new Date(startOfToday);
    startOfLast30.setDate(startOfLast30.getDate() - 29);
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const customStart = customDashboardRange.startDate ? new Date(customDashboardRange.startDate) : null;
    const customEnd = customDashboardRange.endDate ? new Date(customDashboardRange.endDate) : null;
    const hasValidCustomRange =
      customStart &&
      customEnd &&
      !Number.isNaN(customStart.getTime()) &&
      !Number.isNaN(customEnd.getTime());
    const customStartBoundary = hasValidCustomRange
      ? new Date(customStart.getFullYear(), customStart.getMonth(), customStart.getDate())
      : null;
    const customEndExclusive = hasValidCustomRange
      ? new Date(customEnd.getFullYear(), customEnd.getMonth(), customEnd.getDate() + 1)
      : null;

    return sourceOrders.filter((order) => {
      if (!order?.created_at) return false;
      const createdAt = new Date(order.created_at);
      if (Number.isNaN(createdAt.getTime())) return false;
      if (dashboardRange === 'today') {
        return createdAt >= startOfToday && createdAt < startOfTomorrow;
      }
      if (dashboardRange === 'yesterday') {
        return createdAt >= startOfYesterday && createdAt < startOfToday;
      }
      if (dashboardRange === 'last7') {
        return createdAt >= startOfLast7;
      }
      if (dashboardRange === 'last30') {
        return createdAt >= startOfLast30;
      }
      if (dashboardRange === 'thisMonth') {
        return createdAt >= startOfThisMonth && createdAt < startOfNextMonth;
      }
      if (dashboardRange === 'lastMonth') {
        return createdAt >= startOfLastMonth && createdAt < startOfThisMonth;
      }
      if (dashboardRange === 'custom') {
        if (!customStartBoundary || !customEndExclusive) return false;
        return createdAt >= customStartBoundary && createdAt < customEndExclusive;
      }
      return true;
    });
  }, [orders, dashboardRange, customDashboardRange.endDate, customDashboardRange.startDate]);

  const dashboardAnalytics = useMemo(() => {
    const sumBy = (orders, predicate, field = 'total_amount') =>
      orders
        .filter(predicate)
        .reduce((sum, order) => sum + Number(order?.[field] || 0), 0);

    const isCancelledOrder = (order) => ['cancelled', 'canceled'].includes(
      String(order?.status || '').trim().toLowerCase()
    );
    const nonCancelledDashboardOrders = dashboardOrders.filter((order) => !isCancelledOrder(order));
    const orderSalesAmount = (order) => resolvePosOrderPaymentDisplay(order).totalAmount;
    const sumSales = (list) => list.reduce((sum, order) => sum + orderSalesAmount(order), 0);

    const totalSales = sumSales(nonCancelledDashboardOrders);
    const totalOrders = nonCancelledDashboardOrders.length;
    const dineInOrders = nonCancelledDashboardOrders.filter(
      (order) => normalizeOrderTypeForDashboard(order.order_type) === 'dine_in'
    );
    const pickUpOrders = nonCancelledDashboardOrders.filter(
      (order) => normalizeOrderTypeForDashboard(order.order_type) === 'takeaway'
    );
    const deliveryOrders = nonCancelledDashboardOrders.filter(
      (order) => normalizeOrderTypeForDashboard(order.order_type) === 'room_service'
    );
    const cancelledOrders = dashboardOrders.filter((order) => isCancelledOrder(order));
    const pendingOrders = nonCancelledDashboardOrders.filter((order) =>
      ['draft', 'placed', 'preparing'].includes(order.status)
    );

    const dineInSales = sumSales(dineInOrders);
    const pickUpSales = sumSales(pickUpOrders);
    const deliverySales = sumSales(deliveryOrders);
    const cancelledValue = sumSales(cancelledOrders);
    const pendingValue = sumSales(pendingOrders);
    const totalTax = sumBy(nonCancelledDashboardOrders, () => true, 'tax_amount');

    const isDateWiseTrend = ['last7', 'last30', 'thisMonth', 'lastMonth', 'custom'].includes(dashboardRange);
    const salesTrendData = isDateWiseTrend
      ? (() => {
          const today = new Date();
          const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          let rangeStart = new Date(startOfToday);
          let rangeEnd = new Date(startOfToday);

          if (dashboardRange === 'last7') {
            rangeStart.setDate(rangeStart.getDate() - 6);
          } else if (dashboardRange === 'last30') {
            rangeStart.setDate(rangeStart.getDate() - 29);
          } else if (dashboardRange === 'thisMonth') {
            rangeStart = new Date(today.getFullYear(), today.getMonth(), 1);
          } else if (dashboardRange === 'lastMonth') {
            rangeStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            rangeEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          } else if (dashboardRange === 'custom') {
            const customStart = customDashboardRange.startDate ? new Date(customDashboardRange.startDate) : null;
            const customEnd = customDashboardRange.endDate ? new Date(customDashboardRange.endDate) : null;
            if (
              customStart &&
              customEnd &&
              !Number.isNaN(customStart.getTime()) &&
              !Number.isNaN(customEnd.getTime())
            ) {
              rangeStart = new Date(customStart.getFullYear(), customStart.getMonth(), customStart.getDate());
              rangeEnd = new Date(customEnd.getFullYear(), customEnd.getMonth(), customEnd.getDate());
            }
          }

          if (rangeStart > rangeEnd) {
            const swap = rangeStart;
            rangeStart = rangeEnd;
            rangeEnd = swap;
          }

          const daysCount =
            Math.max(
              1,
              Math.round((rangeEnd.getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000)) + 1
            );

          const rows = Array.from({ length: daysCount }, (_, index) => {
            const date = new Date(rangeStart);
            date.setDate(rangeStart.getDate() + index);
            return {
              slot: formatSalesTrendDateLabel(date),
              fullLabel: formatSalesTrendDateLabel(date, true),
              dateKey: getLocalDateKey(date),
              dineIn: 0,
              pickUp: 0,
              delivery: 0,
              total: 0,
            };
          });

          const rowsByDateKey = new Map(rows.map((row) => [row.dateKey, row]));
          nonCancelledDashboardOrders.forEach((order) => {
            const createdAt = order?.created_at ? new Date(order.created_at) : null;
            if (!createdAt || Number.isNaN(createdAt.getTime())) return;
            const bucket = rowsByDateKey.get(getLocalDateKey(createdAt));
            if (!bucket) return;
            const amount = orderSalesAmount(order);
            const normalizedOrderType = normalizeOrderTypeForDashboard(order.order_type);
            if (normalizedOrderType === 'dine_in') bucket.dineIn += amount;
            if (normalizedOrderType === 'takeaway') bucket.pickUp += amount;
            if (normalizedOrderType === 'room_service') bucket.delivery += amount;
            bucket.total += amount;
          });
          return rows;
        })()
      : (() => {
          const rows = SLOT_CONFIG.map((slot) => ({
            slot: slot.shortLabel || slot.label,
            fullLabel: slot.label,
            from: slot.from,
            to: slot.to,
            dineIn: 0,
            pickUp: 0,
            delivery: 0,
            total: 0,
          }));

          nonCancelledDashboardOrders.forEach((order) => {
            const createdAt = order?.created_at ? new Date(order.created_at) : null;
            if (!createdAt || Number.isNaN(createdAt.getTime())) return;
            const hour = createdAt.getHours();
            const bucket = rows.find((row) => hour >= row.from && hour < row.to);
            if (!bucket) return;
            const amount = orderSalesAmount(order);
            const normalizedOrderType = normalizeOrderTypeForDashboard(order.order_type);
            if (normalizedOrderType === 'dine_in') bucket.dineIn += amount;
            if (normalizedOrderType === 'takeaway') bucket.pickUp += amount;
            if (normalizedOrderType === 'room_service') bucket.delivery += amount;
            bucket.total += amount;
          });
          return rows;
        })();

    const paymentAmountByMethod = new Map();
    const appendPaymentAmount = (methodValue, amountValue) => {
      const amount = Number(amountValue || 0);
      if (!Number.isFinite(amount) || amount <= 0.005) return;
      const methodKey = normalizePaymentMethodKey(methodValue) || 'na';
      paymentAmountByMethod.set(methodKey, Number((Number(paymentAmountByMethod.get(methodKey) || 0) + amount).toFixed(2)));
    };

    nonCancelledDashboardOrders.forEach((order) => {
      const paymentEntries = Array.isArray(order?.payments) ? order.payments : [];
      const capturedEntries = paymentEntries
        .filter((entry) => String(entry?.status || '').trim().toLowerCase() === 'captured')
        .map((entry) => ({
          method: entry?.payment_method,
          amount: Number(entry?.amount || 0),
        }))
        .filter((entry) => Number.isFinite(entry.amount) && entry.amount > 0.005);

      if (capturedEntries.length > 0) {
        capturedEntries.forEach((entry) => appendPaymentAmount(entry.method, entry.amount));
        return;
      }

      const totalAmount = Number(order?.total_amount || 0);
      const dueAmount = Math.max(0, Number(order?.due_amount || 0));
      const partialPaidAmount = Math.max(0, Number(order?.partial_paid_amount || 0));
      const paidByBalance = Math.max(0, totalAmount - dueAmount);
      const paymentStatus = String(order?.payment_status || '').trim().toLowerCase();
      const fallbackPaidAmount = paymentStatus === 'due'
        ? 0
        : Math.max(partialPaidAmount, paidByBalance);
      appendPaymentAmount(order?.payment_method, fallbackPaidAmount);
    });

    const paymentRows = Array.from(paymentAmountByMethod.entries())
      .map(([methodKey, amount], index) => ({
        key: methodKey,
        label: formatPaymentMethodLabel(methodKey),
        amount: Number(Number(amount || 0).toFixed(2)),
        color: getPaymentMethodChartColor(methodKey, index),
      }))
      .filter((row) => row.amount > 0)
      .sort((first, second) => second.amount - first.amount);
    const paymentTotal = paymentRows.reduce((sum, row) => sum + row.amount, 0);

    return {
      totalSales,
      totalOrders,
      dineInSales,
      dineInCount: dineInOrders.length,
      pickUpSales,
      pickUpCount: pickUpOrders.length,
      deliverySales,
      deliveryCount: deliveryOrders.length,
      cancelledCount: cancelledOrders.length,
      cancelledValue,
      pendingCount: pendingOrders.length,
      pendingValue,
      totalTax,
      salesTrendData,
      salesTrendMode: isDateWiseTrend ? 'date' : 'slot',
      paymentRows,
      paymentTotal,
    };
  }, [dashboardOrders, dashboardRange, customDashboardRange.endDate, customDashboardRange.startDate]);

  const posHeroTabMetrics = useMemo(() => {
    const rooms = data.rooms || [];
    const tables = data.tables || [];
    const enabledRoomsCount = rooms.filter((room) => Boolean(room.is_enabled_for_order)).length;
    const activeTablesCount = tables.filter((table) => Boolean(table.is_active)).length;
    const tablesCapacity = tables.reduce((sum, table) => sum + Math.max(0, Number(table?.capacity || 0)), 0);

    return {
      ordersCount: ordersPaymentSummary.orderCount,
      ordersSales: ordersPaymentSummary.totalAmount,
      ordersDue: ordersPaymentSummary.dueAmount,
      roomsCount: rooms.length,
      enabledRoomsCount,
      roomOrdersEnabled: Boolean(settingsForm.enable_room_order),
      tablesCount: tables.length,
      activeTablesCount,
      tablesCapacity,
      menuItemsCount: (data.menu_items || []).length,
      categoriesCount: (data.categories || []).length,
      variationsCount: (data.variations || []).length,
    };
  }, [ordersPaymentSummary, data.rooms, data.tables, data.menu_items, data.categories, data.variations, settingsForm.enable_room_order]);

  const sortedConfiguredTables = useMemo(() => {
    const tables = [...(data.tables || [])];
    return tables.sort((first, second) => {
      if (configuredTablesSort === 'capacity_asc' || configuredTablesSort === 'capacity_desc') {
        const difference = Number(first?.capacity || 0) - Number(second?.capacity || 0);
        if (difference !== 0) {
          return configuredTablesSort === 'capacity_desc' ? -difference : difference;
        }
      }

      const comparison = String(first?.name || '').localeCompare(
        String(second?.name || ''),
        undefined,
        { numeric: true, sensitivity: 'base' },
      );
      return configuredTablesSort === 'name_desc' ? -comparison : comparison;
    });
  }, [configuredTablesSort, data.tables]);

  const sortedConfiguredRooms = useMemo(() => {
    const rooms = [...(data.rooms || [])];
    return rooms.sort((first, second) => {
      const sortByFloor = configuredRoomsSort.startsWith('floor');
      const sortByRoomType = configuredRoomsSort.startsWith('room_type');
      const firstValue = sortByFloor
        ? String(first?.floor_label || first?.floor_name || '')
        : (sortByRoomType
          ? String(first?.room_type_name || '')
          : String(first?.room_number || ''));
      const secondValue = sortByFloor
        ? String(second?.floor_label || second?.floor_name || '')
        : (sortByRoomType
          ? String(second?.room_type_name || '')
          : String(second?.room_number || ''));
      const comparison = firstValue.localeCompare(secondValue, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return configuredRoomsSort.endsWith('_desc') ? -comparison : comparison;
    });
  }, [configuredRoomsSort, data.rooms]);

  const salesChartData = useMemo(
    () =>
      dashboardAnalytics.salesTrendData.map((trend) => ({
        slot: trend.slot,
        fullLabel: trend.fullLabel,
        total: Number(trend.total.toFixed(2)),
        dineIn: Number(trend.dineIn.toFixed(2)),
        pickUp: Number(trend.pickUp.toFixed(2)),
        delivery: Number(trend.delivery.toFixed(2)),
      })),
    [dashboardAnalytics.salesTrendData]
  );

  const slaPerformanceChartData = useMemo(() => {
    const isDateMode = dashboardAnalytics.salesTrendMode === 'date';
    const baseRows = (dashboardAnalytics.salesTrendData || []).map((row, index) => ({
      slot: row?.slot || `Slot ${index + 1}`,
      fullLabel: row?.fullLabel || row?.slot || `Slot ${index + 1}`,
      dateKey: row?.dateKey || '',
      from: Number(row?.from ?? -1),
      to: Number(row?.to ?? -1),
      orderCount: 0,
      totalTarget: 0,
      totalActual: 0,
      totalDelay: 0,
      onTimeCount: 0,
    }));

    if (baseRows.length === 0) return [];

    const rowsByDateKey = isDateMode
      ? new Map(baseRows.map((row) => [String(row.dateKey || ''), row]))
      : null;

    dashboardOrders.forEach((order) => {
      const createdAt = order?.created_at ? new Date(order.created_at) : null;
      if (!createdAt || Number.isNaN(createdAt.getTime())) return;
      const timing = getOrderTimingInsight(order);
      const targetMinutes = Math.max(0, Number(timing?.targetMinutes || 0));
      const actualMinutes = Math.max(0, Number(timing?.actualMinutes || 0));
      const delayMinutes = Math.max(0, Number(timing?.delayMinutes || 0));
      let bucket = null;
      if (isDateMode) {
        bucket = rowsByDateKey?.get(getLocalDateKey(createdAt)) || null;
      } else {
        const hour = createdAt.getHours();
        bucket = baseRows.find((row) => hour >= row.from && hour < row.to) || null;
      }
      if (!bucket) return;
      bucket.orderCount += 1;
      bucket.totalTarget += targetMinutes;
      bucket.totalActual += actualMinutes;
      bucket.totalDelay += delayMinutes;
      if (delayMinutes <= 0.05) {
        bucket.onTimeCount += 1;
      }
    });

    return baseRows.map((row) => {
      const denominator = row.orderCount > 0 ? row.orderCount : 1;
      const targetAvg = row.orderCount > 0 ? row.totalTarget / denominator : 0;
      const actualAvg = row.orderCount > 0 ? row.totalActual / denominator : 0;
      const delayAvg = row.orderCount > 0 ? row.totalDelay / denominator : 0;
      const onTimeRate = row.orderCount > 0 ? (row.onTimeCount / denominator) * 100 : 0;
      return {
        slot: row.slot,
        fullLabel: row.fullLabel,
        orderCount: row.orderCount,
        targetAvg: Number(targetAvg.toFixed(2)),
        actualAvg: Number(actualAvg.toFixed(2)),
        delayAvg: Number(delayAvg.toFixed(2)),
        onTimeRate: Number(onTimeRate.toFixed(2)),
      };
    });
  }, [dashboardAnalytics.salesTrendData, dashboardAnalytics.salesTrendMode, dashboardOrders]);

  const slaPerformanceSummary = useMemo(() => {
    const measuredOrders = slaPerformanceChartData.reduce((sum, row) => sum + Number(row.orderCount || 0), 0);
    if (measuredOrders <= 0) {
      return {
        measuredOrders: 0,
        avgTargetMinutes: 0,
        avgActualMinutes: 0,
        avgDelayMinutes: 0,
        onTimeRate: 0,
      };
    }
    const weightedTarget = slaPerformanceChartData.reduce(
      (sum, row) => sum + (Number(row.targetAvg || 0) * Number(row.orderCount || 0)),
      0
    );
    const weightedActual = slaPerformanceChartData.reduce(
      (sum, row) => sum + (Number(row.actualAvg || 0) * Number(row.orderCount || 0)),
      0
    );
    const weightedDelay = slaPerformanceChartData.reduce(
      (sum, row) => sum + (Number(row.delayAvg || 0) * Number(row.orderCount || 0)),
      0
    );
    const weightedOnTime = slaPerformanceChartData.reduce(
      (sum, row) => sum + (Number(row.onTimeRate || 0) * Number(row.orderCount || 0)),
      0
    );

    return {
      measuredOrders,
      avgTargetMinutes: Number((weightedTarget / measuredOrders).toFixed(2)),
      avgActualMinutes: Number((weightedActual / measuredOrders).toFixed(2)),
      avgDelayMinutes: Number((weightedDelay / measuredOrders).toFixed(2)),
      onTimeRate: Number((weightedOnTime / measuredOrders).toFixed(2)),
    };
  }, [slaPerformanceChartData]);

  const paymentPieData = useMemo(
    () =>
      dashboardAnalytics.paymentRows
        .filter((row) => row.amount > 0)
        .map((row, index) => ({
          id: index,
          value: Number(row.amount.toFixed(2)),
          label: row.label,
          color: row.color,
        })),
    [dashboardAnalytics.paymentRows]
  );

  const paymentPieSeriesData = useMemo(
    () =>
      paymentPieData.length > 0
        ? paymentPieData
        : [{ id: 0, value: 1, label: 'No payment data', color: '#607d8b' }],
    [paymentPieData]
  );
  const paymentPieTotalAmount = useMemo(
    () => paymentPieData.reduce((sum, row) => sum + Number(row?.value || 0), 0),
    [paymentPieData]
  );
  const paymentMethodMixRows = useMemo(() => {
    if (paymentPieData.length === 0) {
      return [{
        id: 'no-payment-data',
        label: 'No payment data',
        value: 0,
        color: '#607d8b',
        sharePercent: 0,
      }];
    }
    const denominator = paymentPieTotalAmount > 0 ? paymentPieTotalAmount : 1;
    return paymentPieData.map((row) => ({
      id: row.id,
      label: row.label,
      value: Number(row.value || 0),
      color: row.color,
      sharePercent: Number(((Number(row.value || 0) / denominator) * 100).toFixed(1)),
    }));
  }, [paymentPieData, paymentPieTotalAmount]);

  const leakageBarData = useMemo(
    () => [
      { metric: 'Bills Modified', value: 0 },
      { metric: 'Bills Re-Printed', value: 0 },
      { metric: 'Bills Waived Off', value: Math.abs(Number(dashboardAnalytics.cancelledValue || 0)) },
      { metric: 'KOT Cancelled', value: Number(dashboardAnalytics.cancelledCount || 0) },
      { metric: 'KOT Modified', value: 0 },
      { metric: 'KOT Not Used', value: Number(dashboardAnalytics.pendingCount || 0) },
      { metric: 'KOT Shifted', value: 0 },
    ],
    [dashboardAnalytics.cancelledValue, dashboardAnalytics.cancelledCount, dashboardAnalytics.pendingCount]
  );

  const leakageBarMax = useMemo(
    () => Math.max(1, ...leakageBarData.map((row) => Number(row.value || 0))),
    [leakageBarData]
  );

  const topSellingMenuData = useMemo(() => {
    const aggregate = {};
    dashboardOrders.forEach((order) => {
      if (['cancelled', 'canceled'].includes(String(order?.status || '').trim().toLowerCase())) return;
      (order?.items || []).forEach((item) => {
        const menuItemId = Number(item?.menu_item || item?.menu_item_id || 0);
        const itemName = String(item?.menu_item_name || item?.item_name || 'Unknown Item').trim() || 'Unknown Item';
        const aggregateKey = menuItemId > 0 ? `id:${menuItemId}` : `name:${itemName.toLowerCase()}`;
        const quantity = Number(item?.quantity || 0);
        const lineTotal = Number(item?.line_total || Number(item?.unit_price || 0) * quantity || 0);
        if (!aggregate[aggregateKey]) {
          aggregate[aggregateKey] = { item: itemName, sales: 0, quantity: 0 };
        }
        // Keep base menu label when available so variations collapse into one menu row.
        if (item?.menu_item_name) {
          aggregate[aggregateKey].item = String(item.menu_item_name).trim() || aggregate[aggregateKey].item;
        }
        aggregate[aggregateKey].sales += lineTotal;
        aggregate[aggregateKey].quantity += quantity;
      });
    });
    return Object.values(aggregate)
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 8)
      .map((row) => ({
        ...row,
        sales: Number(row.sales.toFixed(2)),
        quantity: Number(row.quantity.toFixed(2)),
      }));
  }, [dashboardOrders]);

  const topSellingMax = useMemo(
    () => Math.max(1, ...topSellingMenuData.map((row) => Number(row.sales || 0))),
    [topSellingMenuData]
  );

  const handleDownloadDashboardPdf = () => {
    if (isRefreshing || loading) return;
    setDashboardPdfExporting(true);
    try {
      downloadPdfMultiSection(
        'POS Dashboard Report',
        `Range: ${selectedDashboardDateLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')}`,
        [
          {
            title: 'Key Metrics',
            columns: [
              { key: 'metric', label: 'Metric' },
              { key: 'value', label: 'Value' },
              { key: 'count', label: 'Count' },
            ],
            rows: [
              { metric: 'Total Sales', value: formatCurrency(dashboardAnalytics.totalSales), count: dashboardAnalytics.totalOrders },
              { metric: 'Dine In', value: formatCurrency(dashboardAnalytics.dineInSales), count: dashboardAnalytics.dineInCount },
              { metric: 'Pick Up / Delivery', value: formatCurrency(dashboardAnalytics.pickUpSales), count: dashboardAnalytics.pickUpCount },
              { metric: 'Room Service', value: formatCurrency(dashboardAnalytics.deliverySales), count: dashboardAnalytics.deliveryCount },
              { metric: 'Cancelled', value: formatCurrency(dashboardAnalytics.cancelledValue), count: dashboardAnalytics.cancelledCount },
              { metric: 'Pending', value: formatCurrency(dashboardAnalytics.pendingValue), count: dashboardAnalytics.pendingCount },
            ],
          },
          {
            title: 'Sales Trends',
            columns: [
              { key: 'slot', label: 'Date / Slot' },
              { key: 'total', label: 'Total' },
              { key: 'dineIn', label: 'Dine In' },
              { key: 'pickUp', label: 'Pick Up / Delivery' },
              { key: 'delivery', label: 'Room Service' },
            ],
            rows: salesChartData.map((row) => ({
              slot: row.fullLabel || row.slot,
              total: formatCurrency(row.total),
              dineIn: formatCurrency(row.dineIn),
              pickUp: formatCurrency(row.pickUp),
              delivery: formatCurrency(row.delivery),
            })),
          },
          {
            title: 'Payment Methods',
            columns: [
              { key: 'method', label: 'Method' },
              { key: 'amount', label: 'Amount' },
            ],
            rows: dashboardAnalytics.paymentRows.map((row) => ({
              method: row.label,
              amount: formatCurrency(row.amount),
            })),
          },
          {
            title: 'Top Selling Menu',
            columns: [
              { key: 'item', label: 'Item' },
              { key: 'sales', label: 'Sales' },
              { key: 'quantity', label: 'Quantity' },
            ],
            rows: topSellingMenuData.map((row) => ({
              item: row.name,
              sales: formatCurrency(row.sales),
              quantity: row.quantity,
            })),
          },
        ],
        `pos-dashboard-${dashboardRange}-${moment().format('YYYY-MM-DD-HHmm')}.pdf`,
        { landscape: true, fontSize: 7 }
      );
    } finally {
      setDashboardPdfExporting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const payload = {
        enable_room_order: Boolean(settingsForm.enable_room_order),
        gst_percentage: Number(settingsForm.gst_percentage || 0),
      };
      const updated = await updatePosRestaurantSettings(payload);
      setData((prev) => ({ ...prev, settings: updated }));
      setToastMessage('success', 'POS settings updated.');
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to update POS settings.');
    }
  };

  const handleSaveRoomConfig = async () => {
    try {
      const enabledRoomIds = data.rooms
        .filter((room) => room.is_enabled_for_order)
        .map((room) => room.id);
      const updatedConfigs = await updatePosRoomConfigs(enabledRoomIds);
      const enabledSet = new Set(
        updatedConfigs.filter((row) => row.is_enabled_for_order).map((row) => row.room)
      );
      setData((prev) => ({
        ...prev,
        rooms: prev.rooms.map((room) => ({
          ...room,
          is_enabled_for_order: enabledSet.has(room.id),
        })),
      }));
      setToastMessage('success', 'Room billing configuration saved.');
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to update room configuration.');
    }
  };

  const handleCreateTable = async () => {
    if (!tableForm.name.trim()) {
      setToastMessage('error', 'Table name is required.');
      return;
    }
    try {
      await createPosTable({
        name: tableForm.name.trim(),
        capacity: Number(tableForm.capacity || 1),
        extra_information: tableForm.extraInformation.trim(),
        status: 'available',
        is_active: true,
      });
      setTableForm({ name: '', capacity: 4, extraInformation: '' });
      setToastMessage('success', 'Table added.');
      await loadData();
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to add table.');
    }
  };

  const handleToggleTableActive = async (tableId, nextActive) => {
    const previousTables = data.tables;
    setData((prev) => ({
      ...prev,
      tables: prev.tables.map((table) =>
        table.id === tableId ? { ...table, is_active: nextActive } : table
      ),
    }));
    try {
      await updatePosTable(tableId, { is_active: nextActive });
      setToastMessage('success', nextActive ? 'Table enabled.' : 'Table disabled.');
    } catch (err) {
      setData((prev) => ({ ...prev, tables: previousTables }));
      setToastMessage('error', err?.userMessage || 'Failed to update table status.');
    }
  };

  const handleDeleteTable = async (tableId, tableName) => {
    if (!tableId) return;
    const previousTables = data.tables;
    setIsDeletingTable(true);
    setData((prev) => ({
      ...prev,
      tables: prev.tables.filter((table) => table.id !== tableId),
    }));
    try {
      await deletePosTable(tableId);
      setToastMessage('success', 'Table deleted.');
      setTableDeleteDialog({ open: false, tableId: null, tableName: '' });
    } catch (err) {
      setData((prev) => ({ ...prev, tables: previousTables }));
      setToastMessage('error', err?.userMessage || 'Failed to delete table.');
    } finally {
      setIsDeletingTable(false);
    }
  };

  const getQrFilename = useCallback((targetType, targetMeta = {}, targetId) => {
    const baseLabel = targetType === 'room'
      ? `room-${targetMeta?.room_number || targetId || 'target'}`
      : `table-${targetMeta?.name || targetId || 'target'}`;
    const safeLabel = String(baseLabel)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/-+/g, '-');
    return `pos-${safeLabel || 'qr'}.png`;
  }, []);

  const handleOpenQrPreview = useCallback(async (targetType, targetRow) => {
    const resolvedType = String(targetType || '').trim().toLowerCase();
    const targetId = Number(targetRow?.id || 0);
    if (!targetId || !['table', 'room'].includes(resolvedType)) {
      setToastMessage('error', 'Unable to generate QR for this item.');
      return;
    }

    setQrPreviewDialog({
      open: true,
      loading: true,
      targetType: resolvedType,
      targetId,
      targetDisplay: '',
      targetMeta: {},
      landingUrl: '',
      qrDataUrl: '',
    });

    try {
      const payload = await getPosQrContext({ targetType: resolvedType, targetId });
      const landingUrl = String(payload?.landing_url || '');
      if (!landingUrl) throw new Error('Invalid QR payload.');
      const qrDataUrl = await QRCode.toDataURL(landingUrl, {
        width: 640,
        margin: 1,
        color: {
          dark: '#10233d',
          light: '#eef6ff',
        },
      });
      setQrPreviewDialog({
        open: true,
        loading: false,
        targetType: resolvedType,
        targetId,
        targetDisplay: String(payload?.target_display || ''),
        targetMeta: payload?.target_meta || {},
        landingUrl,
        qrDataUrl,
      });
    } catch (err) {
      setQrPreviewDialog((prev) => ({ ...prev, loading: false }));
      setToastMessage('error', err?.userMessage || 'Failed to generate QR preview.');
    }
  }, [setToastMessage]);

  // Branding used only to compose the downloaded QR stand artwork (cached per session).
  const loadQrStandBranding = useCallback(async () => {
    if (qrStandBrandingRef.current) return qrStandBrandingRef.current;

    const contextHotel = (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel) || {};
    const branding = {
      hotelName: String(contextHotel?.name || '').trim() || 'RoomNexa Hotel',
      hotelLocation: [contextHotel?.city, contextHotel?.country].filter(Boolean).join(', '),
      logoUrl: '',
      websiteText: '',
      contactText: '',
    };

    const [basicDetails, brandingProfile] = await Promise.allSettled([
      getHotelBasicDetails(),
      getPosQrBrandingProfile(),
    ]);

    if (basicDetails.status === 'fulfilled' && basicDetails.value) {
      const details = basicDetails.value;
      branding.hotelName = String(details?.name || '').trim() || branding.hotelName;
      const locality = [details?.city, contextHotel?.country].filter(Boolean).join(', ');
      branding.hotelLocation = locality || branding.hotelLocation;
      const mobiles = Array.isArray(details?.mobile_numbers) ? details.mobile_numbers : [];
      const firstMobile = mobiles
        .map((entry) => String(entry?.number || entry || '').trim())
        .find(Boolean);
      branding.contactText = firstMobile
        || String(details?.whatsapp_number || '').trim()
        || String(details?.landline_number || '').trim();
    }

    if (brandingProfile.status === 'fulfilled' && brandingProfile.value) {
      const profile = brandingProfile.value;
      branding.logoUrl = String(profile?.logo_url || '').trim();
      const slug = String(profile?.nexastay_slug || '').trim();
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const isLocalHost = host === 'localhost' || host === '127.0.0.1';
      if (slug && !isLocalHost) {
        branding.websiteText = `${slug}.roomnexa.com`;
      } else if (typeof window !== 'undefined') {
        branding.websiteText = window.location.host;
      }
    } else if (typeof window !== 'undefined') {
      branding.websiteText = window.location.host;
    }

    qrStandBrandingRef.current = branding;
    return branding;
  }, []);

  const handleDownloadQr = useCallback(async () => {
    if (!qrPreviewDialog.qrDataUrl) {
      setToastMessage('warning', 'QR preview is not ready yet.');
      return;
    }

    // Downloaded file only: wrap the generated QR inside the print-ready stand template.
    let downloadHref = qrPreviewDialog.qrDataUrl;
    try {
      const branding = await loadQrStandBranding();
      const isRoom = qrPreviewDialog.targetType === 'room';
      const targetLabel = isRoom
        ? `Room ${qrPreviewDialog.targetMeta?.room_number || qrPreviewDialog.targetId || ''}`.trim()
        : `Table ${String(qrPreviewDialog.targetDisplay || qrPreviewDialog.targetId || '').replace(/^table\s*/i, '')}`.trim();
      const targetCaption = isRoom
        ? String(qrPreviewDialog.targetMeta?.room_type_name || qrPreviewDialog.targetMeta?.floor_name || '').trim()
        : (Number(qrPreviewDialog.targetMeta?.capacity || 0) > 0
          ? `${Number(qrPreviewDialog.targetMeta.capacity)} seats`
          : '');

      const standDataUrl = await buildQrStandArtwork({
        qrDataUrl: qrPreviewDialog.qrDataUrl,
        hotelName: branding.hotelName,
        hotelLocation: branding.hotelLocation,
        logoUrl: branding.logoUrl,
        targetLabel,
        targetCaption,
        ctaText: 'Scan to View Menu & Order Instantly',
        helperText: isRoom
          ? 'Order from your room — no app, no waiting'
          : 'Open your camera, scan the code, and order in seconds',
        websiteText: branding.websiteText,
        contactText: branding.contactText,
      });
      if (standDataUrl) downloadHref = standDataUrl;
    } catch (err) {
      downloadHref = qrPreviewDialog.qrDataUrl;
    }

    const anchor = document.createElement('a');
    anchor.href = downloadHref;
    anchor.download = getQrFilename(
      qrPreviewDialog.targetType,
      qrPreviewDialog.targetMeta,
      qrPreviewDialog.targetId
    );
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setToastMessage('success', 'QR downloaded successfully.');
  }, [
    getQrFilename,
    loadQrStandBranding,
    qrPreviewDialog.qrDataUrl,
    qrPreviewDialog.targetDisplay,
    qrPreviewDialog.targetId,
    qrPreviewDialog.targetMeta,
    qrPreviewDialog.targetType,
    setToastMessage,
  ]);

  const qrUiDetails = useMemo(() => {
    const isRoom = qrPreviewDialog.targetType === 'room';
    const targetLabel = isRoom
      ? `Room ${qrPreviewDialog.targetMeta?.room_number || qrPreviewDialog.targetId || '-'}`
      : `Table ${qrPreviewDialog.targetDisplay || qrPreviewDialog.targetId || '-'}`;
    const usageLabel = isRoom ? 'Room Service Ordering' : 'Dine-in Ordering';
    const contextLabel = isRoom
      ? (qrPreviewDialog.targetMeta?.room_type_name || qrPreviewDialog.targetMeta?.floor_name || 'Guest room access')
      : `${qrPreviewDialog.targetMeta?.capacity || 0} seat capacity`;
    return {
      targetLabel,
      usageLabel,
      contextLabel,
      linkReady: Boolean(String(qrPreviewDialog.landingUrl || '').trim()),
    };
  }, [
    qrPreviewDialog.landingUrl,
    qrPreviewDialog.targetDisplay,
    qrPreviewDialog.targetId,
    qrPreviewDialog.targetMeta,
    qrPreviewDialog.targetType,
  ]);

  const handleCopyQrLandingUrl = useCallback(async () => {
    const landingUrl = String(qrPreviewDialog.landingUrl || '').trim();
    if (!landingUrl) {
      setToastMessage('warning', 'Order link is not available yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(landingUrl);
      setToastMessage('success', 'Order link copied.');
    } catch (err) {
      setToastMessage('error', 'Failed to copy order link.');
    }
  }, [qrPreviewDialog.landingUrl, setToastMessage]);

  const handleOpenQrLandingUrl = useCallback(() => {
    const landingUrl = String(qrPreviewDialog.landingUrl || '').trim();
    if (!landingUrl) {
      setToastMessage('warning', 'Order link is not available yet.');
      return;
    }
    window.open(landingUrl, '_blank', 'noopener,noreferrer');
  }, [qrPreviewDialog.landingUrl, setToastMessage]);

  const handleToggleCategoryActive = async (categoryId, nextActive) => {
    const previousCategories = data.categories;
    setData((prev) => ({
      ...prev,
      categories: prev.categories.map((category) =>
        category.id === categoryId ? { ...category, is_active: nextActive } : category
      ),
    }));
    try {
      await updatePosMenuCategory(categoryId, { is_active: nextActive });
      setToastMessage('success', nextActive ? 'Category enabled.' : 'Category disabled.');
    } catch (err) {
      setData((prev) => ({ ...prev, categories: previousCategories }));
      setToastMessage('error', err?.userMessage || 'Failed to update category status.');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!categoryId) return;
    setIsDeletingCategory(true);
    try {
      await deletePosMenuCategory(categoryId);
      await loadData();
      setToastMessage('success', 'Category removed.');
      setCategoryDeleteDialog({ open: false, categoryId: null, categoryName: '' });
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to remove category.');
    } finally {
      setIsDeletingCategory(false);
    }
  };

  const handlePersistCategoryOrdering = async (updates, successMessage) => {
    const updateEntries = Object.entries(updates || {});
    if (updateEntries.length === 0) return;
    const previousCategories = data.categories;
    setIsSavingCategoryOrder(true);
    setData((prev) => ({
      ...prev,
      categories: prev.categories.map((category) =>
        updates[category.id] ? { ...category, ...updates[category.id] } : category
      ),
    }));
    try {
      await Promise.all(
        updateEntries.map(([id, payload]) => updatePosMenuCategory(Number(id), payload))
      );
      setToastMessage('success', successMessage);
    } catch (err) {
      setData((prev) => ({ ...prev, categories: previousCategories }));
      setToastMessage('error', err?.userMessage || 'Failed to update category order.');
    } finally {
      setIsSavingCategoryOrder(false);
    }
  };

  const handleParentDragStart = (parentCategoryId) => {
    if (isSavingCategoryOrder) return;
    setCategoryDragState({
      draggingId: parentCategoryId,
      draggingType: 'parent',
      sourceParentId: null,
      overKey: null,
    });
  };

  const handleParentDrop = async (targetParentId = null) => {
    if (categoryDragState.draggingType !== 'parent' || !categoryDragState.draggingId) {
      return;
    }
    const orderedParents = [...parentCategories];
    const sourceIndex = orderedParents.findIndex((row) => row.id === categoryDragState.draggingId);
    if (sourceIndex < 0) {
      setCategoryDragState({ draggingId: null, draggingType: null, sourceParentId: null, overKey: null });
      return;
    }
    const [movedParent] = orderedParents.splice(sourceIndex, 1);
    const targetIndex =
      targetParentId == null
        ? orderedParents.length
        : orderedParents.findIndex((row) => row.id === targetParentId);
    if (targetIndex < 0) {
      orderedParents.push(movedParent);
    } else {
      orderedParents.splice(targetIndex, 0, movedParent);
    }

    const orderingUpdates = {};
    orderedParents.forEach((parentCategory, index) => {
      orderingUpdates[parentCategory.id] = { sort_order: index + 1, parent: null };
    });
    await handlePersistCategoryOrdering(orderingUpdates, 'Parent category order updated.');
    setCategoryDragState({ draggingId: null, draggingType: null, sourceParentId: null, overKey: null });
  };

  const handleChildDragStart = (childCategoryId, parentId) => {
    if (isSavingCategoryOrder) return;
    setCategoryDragState({
      draggingId: childCategoryId,
      draggingType: 'child',
      sourceParentId: parentId,
      overKey: null,
    });
  };

  const handleChildDrop = async (targetParentId, targetChildId = null) => {
    if (categoryDragState.draggingType !== 'child' || !categoryDragState.draggingId) {
      return;
    }
    const sourceParentId = categoryDragState.sourceParentId;
    const sourceChildren = [...(childCategoriesByParent.get(sourceParentId) || [])];
    const movingChildIndex = sourceChildren.findIndex((row) => row.id === categoryDragState.draggingId);
    if (movingChildIndex < 0) {
      setCategoryDragState({ draggingId: null, draggingType: null, sourceParentId: null, overKey: null });
      return;
    }
    const [movingChild] = sourceChildren.splice(movingChildIndex, 1);

    const targetChildren =
      sourceParentId === targetParentId
        ? sourceChildren
        : [...(childCategoriesByParent.get(targetParentId) || [])];

    let insertIndex = targetChildren.length;
    if (targetChildId != null) {
      const foundIndex = targetChildren.findIndex((row) => row.id === targetChildId);
      if (foundIndex >= 0) {
        insertIndex = foundIndex;
      }
    }
    targetChildren.splice(insertIndex, 0, movingChild);

    const orderingUpdates = {};
    sourceChildren.forEach((childCategory, index) => {
      orderingUpdates[childCategory.id] = { sort_order: index + 1, parent: sourceParentId };
    });
    targetChildren.forEach((childCategory, index) => {
      orderingUpdates[childCategory.id] = { sort_order: index + 1, parent: targetParentId };
    });

    await handlePersistCategoryOrdering(orderingUpdates, 'Category arrangement updated.');
    setCategoryDragState({ draggingId: null, draggingType: null, sourceParentId: null, overKey: null });
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      setToastMessage('error', 'Category name is required.');
      return false;
    }
    try {
      await createPosMenuCategory({
        name: categoryForm.name.trim(),
        parent: categoryForm.parent ? Number(categoryForm.parent) : null,
        is_active: true,
      });
      setCategoryForm({ name: '', parent: '' });
      setToastMessage('success', 'Menu category added.');
      await loadData();
      return true;
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to add category.');
      return false;
    }
  };

  const handleCreateMenuItem = async () => {
    if (!menuItemForm.name.trim()) {
      setToastMessage('error', 'Menu item name is required.');
      return false;
    }
    if (!menuItemForm.short_code.trim()) {
      setToastMessage('error', 'Short code is required.');
      return false;
    }
    if (!menuItemForm.category) {
      setToastMessage('error', 'Category is required.');
      return false;
    }
    const selectedCategory = data.categories.find((category) => Number(category.id) === Number(menuItemForm.category));
    if (!selectedCategory?.parent) {
      setToastMessage('error', 'Please select a sub category (Parent / Child).');
      return false;
    }
    if (!menuItemForm.enable_variation && (!menuItemForm.price || Number(menuItemForm.price) <= 0)) {
      setToastMessage('error', 'Enter valid menu item price.');
      return false;
    }
    const orderTypes = [
      menuItemForm.order_room_service ? 'room_service' : null,
      menuItemForm.order_pick_up ? 'pick_up' : null,
      menuItemForm.order_dine_in ? 'dine_in' : null,
    ].filter(Boolean);
    if (orderTypes.length === 0) {
      setToastMessage('error', 'Select at least one order type.');
      return false;
    }
    const variationConfig = menuItemForm.enable_variation
      ? menuItemVariationRows
          .filter((row) => row.variation_id)
          .map((row) => ({
            variation_id: Number(row.variation_id),
            price: Number(row.price || 0),
          }))
      : [];
    const isEditMode = Boolean(editingMenuItemId);
    try {
      const normalizedMenuPrice = menuItemForm.enable_variation ? 0 : Number(menuItemForm.price);      const payload = {
        name: menuItemForm.name.trim(),
        short_code: menuItemForm.short_code.trim(),
        description: menuItemForm.description.trim(),
        category: menuItemForm.category ? Number(menuItemForm.category) : null,
        price: normalizedMenuPrice,
        dietary: menuItemForm.dietary,
        gst_type: menuItemForm.gst_type,
        order_types: orderTypes,
        variation_config: variationConfig,
        is_veg: menuItemForm.dietary === 'veg',
        is_available: Boolean(menuItemForm.is_available),
      };
      if (menuItemImageFile) {
        const formData = new FormData();
        formData.append('name', payload.name);
        formData.append('short_code', payload.short_code);
        formData.append('description', payload.description);
        if (payload.category != null && payload.category !== '') {
          formData.append('category', String(payload.category));
        }
        formData.append('price', String(payload.price));
        formData.append('dietary', payload.dietary);
        formData.append('gst_type', payload.gst_type);
        formData.append('order_types', JSON.stringify(payload.order_types));
        formData.append('variation_config', JSON.stringify(payload.variation_config));
        formData.append('is_veg', payload.is_veg ? 'true' : 'false');
        formData.append('is_available', payload.is_available ? 'true' : 'false');
        formData.append('menu_image', menuItemImageFile);
        if (isEditMode) {
          await updatePosMenuItem(editingMenuItemId, formData);
        } else {
          await createPosMenuItem(formData);
        }
      } else {
        if (isEditMode) {
          await updatePosMenuItem(editingMenuItemId, payload);
        } else {
          await createPosMenuItem(payload);
        }
      }
      setMenuItemForm({
        name: '',
        short_code: '',
        description: '',
        category: '',
        price: '',
        dietary: 'veg',
        gst_type: 'services',
        enable_variation: false,
        order_room_service: true,
        order_pick_up: true,
        order_dine_in: true,
        is_available: true,
      });
      setEditingMenuItemId(null);
      setMenuItemVariationRows([{ variation_id: '', price: '' }]);
      setMenuItemImageFile(null);
      setMenuItemImagePreview('');
      setToastMessage('success', isEditMode ? 'Menu item updated.' : 'Menu item added.');
      await loadData();
      return true;
    } catch (err) {
      setToastMessage('error', err?.userMessage || `Failed to ${isEditMode ? 'update' : 'add'} menu item.`);
      return false;
    }
  };

  const handleSaveVariation = async () => {
    if (!variationForm.name.trim()) {
      setToastMessage('error', 'Variation name is required.');
      return false;
    }
    if (!variationForm.department_name) {
      setToastMessage('error', 'Department name is required.');
      return false;
    }
    try {
      const payload = {
        name: variationForm.name.trim(),
        online_display_name: variationForm.online_display_name.trim(),
        department_name: variationForm.department_name,
        description: '',
        additional_price: 0,
        is_active: Boolean(variationForm.is_active),
      };
      if (editingVariationId) {
        await updatePosMenuVariation(editingVariationId, payload);
      } else {
        await createPosMenuVariation(payload);
      }
      setVariationForm({
        name: '',
        online_display_name: '',
        department_name: '',
        is_active: true,
      });
      setEditingVariationId(null);
      setToastMessage('success', editingVariationId ? 'Variation updated.' : 'Variation added.');
      await loadData();
      return true;
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to save variation.');
      return false;
    }
  };

  const handleAddMenuItemVariationRow = () => {
    setMenuItemVariationRows((prev) => {
      if (!menuItemForm.enable_variation) return prev;
      const lastRow = prev[prev.length - 1];
      if (!lastRow?.variation_id) return prev;
      return [...prev, { variation_id: '', price: '' }];
    });
  };

  const handleRemoveMenuItemVariationRow = (rowIndex) => {
    setMenuItemVariationRows((prev) => {
      if (prev.length <= 1) return [{ variation_id: '', price: '' }];
      return prev.filter((_, index) => index !== rowIndex);
    });
  };

  const handleChangeMenuItemVariationRow = (rowIndex, field, value) => {
    setMenuItemVariationRows((prev) =>
      prev.map((row, index) => (index === rowIndex ? { ...row, [field]: value } : row))
    );
  };

  const handleMenuItemImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setMenuItemImageFile(null);
      setMenuItemImagePreview('');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setMenuItemImageFile(null);
      setMenuItemImagePreview('');
      setToastMessage('error', 'Please choose a valid image file.');
      return;
    }
    setMenuItemImageFile(file);
  };

  const handleOpenMenuDrawer = (type, options = {}) => {
    if (type === MENU_SECTION_VALUES.CATEGORIES) {
      const parentCategory = options.parentCategory || null;
      if (parentCategory) {
        setCategoryDrawerContext({
          mode: 'child',
          parentId: parentCategory.id,
          parentName: parentCategory.name || '',
        });
        setCategoryForm({ name: '', parent: String(parentCategory.id) });
      } else {
        setCategoryDrawerContext({ mode: 'parent', parentId: null, parentName: '' });
        setCategoryForm({ name: '', parent: '' });
      }
    }
    if (type === MENU_SECTION_VALUES.VARIATIONS) {
      const variation = options.variation || null;
      if (variation) {
        setEditingVariationId(variation.id);
        setVariationForm({
          name: variation.name || '',
          online_display_name: variation.online_display_name || '',
          department_name: variation.department_name || '',
          is_active: Boolean(variation.is_active),
        });
      } else {
        setEditingVariationId(null);
        setVariationForm({
          name: '',
          online_display_name: '',
          department_name: '',
          is_active: true,
        });
      }
    }
    if (type === MENU_SECTION_VALUES.ITEMS) {
      const menuItem = options.menuItem || null;
      if (menuItem) {
        const configuredOrderTypes = Array.isArray(menuItem.order_types) ? menuItem.order_types : [];
        const orderTypeSet = new Set(configuredOrderTypes.map((type) => String(type || '').toLowerCase()));
        const hasConfiguredOrderTypes = orderTypeSet.size > 0;
        const configuredVariationRows = Array.isArray(menuItem.variation_config)
          ? menuItem.variation_config
              .filter((row) => row && row.variation_id)
              .map((row) => ({
                variation_id: String(row.variation_id),
                price: String(row.price ?? ''),
              }))
          : [];
        setEditingMenuItemId(menuItem.id);
        setMenuItemForm({
          name: menuItem.name || '',
          short_code: menuItem.short_code || '',
          description: menuItem.description || '',
          category: menuItem.category ? String(menuItem.category) : '',
          price: configuredVariationRows.length > 0
            ? '0'
            : (menuItem.price != null ? String(menuItem.price) : ''),
          dietary: menuItem.dietary || (menuItem.is_veg ? 'veg' : 'non_veg'),
          gst_type: menuItem.gst_type || 'services',
          enable_variation: configuredVariationRows.length > 0,
          order_room_service: hasConfiguredOrderTypes
            ? orderTypeSet.has('room_service') || orderTypeSet.has('delivery')
            : true,
          order_pick_up: hasConfiguredOrderTypes
            ? orderTypeSet.has('pick_up') || orderTypeSet.has('takeaway')
            : true,
          order_dine_in: hasConfiguredOrderTypes ? orderTypeSet.has('dine_in') : true,
          is_available: Boolean(menuItem.is_available),
        });
        setMenuItemVariationRows(
          configuredVariationRows.length > 0 ? configuredVariationRows : [{ variation_id: '', price: '' }]
        );
        setMenuItemImageFile(null);
        setMenuItemImagePreview(menuItem.menu_image_url || menuItem.menu_image || '');
      } else {
        setEditingMenuItemId(null);
        setMenuItemForm({
          name: '',
          short_code: '',
          description: '',
          category: '',
          price: '',
          dietary: 'veg',
          gst_type: 'services',
          enable_variation: false,
          order_room_service: true,
          order_pick_up: true,
          order_dine_in: true,
          is_available: true,
        });
        setMenuItemVariationRows([{ variation_id: '', price: '' }]);
        setMenuItemImageFile(null);
        setMenuItemImagePreview('');
      }
    }
    setMenuDrawer({ open: true, type });
  };

  const handleCloseMenuDrawer = () => {
    if (menuDrawerSubmitting) return;
    if (activeMenuDrawerType === MENU_SECTION_VALUES.VARIATIONS) {
      setEditingVariationId(null);
    }
    if (activeMenuDrawerType === MENU_SECTION_VALUES.ITEMS) {
      setEditingMenuItemId(null);
      setMenuItemImageFile(null);
      setMenuItemImagePreview('');
    }
    setMenuDrawer((prev) => ({ ...prev, open: false }));
  };

  const handleSaveMenuDrawer = async () => {
    if (menuDrawerSubmitting) return;
    setMenuDrawerSubmitting(true);
    try {
      let saved = false;
      if (activeMenuDrawerType === MENU_SECTION_VALUES.CATEGORIES) {
        saved = await handleCreateCategory();
      } else if (activeMenuDrawerType === MENU_SECTION_VALUES.VARIATIONS) {
        saved = await handleSaveVariation();
      } else {
        saved = await handleCreateMenuItem();
      }
      if (saved) {
        setMenuDrawer((prev) => ({ ...prev, open: false }));
      }
    } finally {
      setMenuDrawerSubmitting(false);
    }
  };

  const handleDeleteVariation = async (variationId) => {
    if (!variationId) return;
    setIsDeletingVariation(true);
    try {
      await deletePosMenuVariation(variationId);
      await loadData();
      setVariationDeleteDialog({ open: false, variationId: null, variationName: '' });
      setToastMessage('success', 'Variation removed.');
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to remove variation.');
    } finally {
      setIsDeletingVariation(false);
    }
  };

  const handleDeleteMenuItem = async (menuItemId) => {
    if (!menuItemId) return;
    setIsDeletingMenuItem(true);
    try {
      await deletePosMenuItem(menuItemId);
      await loadData();
      setMenuItemDeleteDialog({ open: false, menuItemId: null, menuItemName: '' });
      setToastMessage('success', 'Menu item removed.');
    } catch (err) {
      setToastMessage('error', err?.userMessage || 'Failed to remove menu item.');
    } finally {
      setIsDeletingMenuItem(false);
    }
  };

  const handleToggleInventory = async (menuItemId, nextValue) => {
    const prevItems = data.menu_items;
    setData((prev) => ({
      ...prev,
      menu_items: prev.menu_items.map((item) =>
        item.id === menuItemId ? { ...item, is_available: nextValue } : item
      ),
    }));
    try {
      await updatePosMenuItem(menuItemId, { is_available: nextValue });
      setToastMessage('success', 'Inventory updated.');
    } catch (err) {
      setData((prev) => ({ ...prev, menu_items: prevItems }));
      setToastMessage('error', err?.userMessage || 'Failed to update inventory.');
    }
  };

  const menuTableColumns = useMemo(
    () => [
      {
        field: 'image',
        label: 'Image',
        sortable: false,
        render: (_, item) =>
          (item.menu_image_url || item.menu_image) ? (
            <Box
              component="img"
              src={item.menu_image_url || item.menu_image}
              alt={item.name || 'menu'}
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.2,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.16)',
              }}
            />
          ) : (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.2,
                border: '1px dashed',
                borderColor: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.2)',
                display: 'grid',
                placeItems: 'center',
                color: isDark ? '#93a4ba' : '#6f8199',
              }}
            >
              <RestaurantMenuIcon sx={{ fontSize: 18 }} />
            </Box>
          ),
      },
      {
        field: 'name',
        label: 'Name',
        sortable: true,
        render: (value) => <Typography sx={{ fontWeight: 700 }}>{value || '—'}</Typography>,
      },
      {
        field: 'category',
        label: 'Category',
        sortable: true,
        render: (_, item) =>
          (item.category ? categoryLabelMap.get(item.category) : item.category_name) || 'Uncategorized',
      },
      {
        field: 'price',
        label: 'Price',
        sortable: true,
        render: (value) => formatCurrency(value),
      },
      {
        field: 'dietary',
        label: 'Dietary',
        sortable: true,
        render: (_, item) => item.dietary_label || (item.is_veg ? 'Veg' : 'Non-Veg'),
      },
      {
        field: 'gst',
        label: 'GST',
        sortable: true,
        render: (_, item) => item.gst_type_label || 'Services',
      },
      {
        field: 'variation',
        label: 'Variation',
        sortable: false,
        render: (_, item) =>
          Array.isArray(item.variation_config) && item.variation_config.length > 0 ? (
            <Stack direction="row" spacing={0.45} useFlexGap flexWrap="wrap">
              {item.variation_config
                .filter((row) => row && row.variation_id)
                .map((row) => {
                  const variationLabel =
                    variationLabelMap.get(Number(row.variation_id)) ||
                    `Variation #${row.variation_id}`;
                  const extraPrice = Number(row.price || 0);
                  return (
                    <Chip
                      key={`${item.id}-variation-${row.variation_id}`}
                      size="small"
                      label={
                        extraPrice > 0
                          ? `${variationLabel} (+${formatCurrency(extraPrice)})`
                          : variationLabel
                      }
                    />
                  );
                })}
            </Stack>
          ) : (
            '—'
          ),
      },
      {
        field: 'status',
        label: 'Status',
        sortable: true,
        render: (_, item) => (
          <Chip
            size="small"
            color={item.is_available ? 'success' : 'default'}
            label={item.is_available ? 'Available' : 'Unavailable'}
          />
        ),
      },
      {
        field: 'actions',
        label: 'Actions',
        sortable: false,
        render: (_, item) => (
          <Stack direction="row" spacing={0.35} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
            <Switch
              size="small"
              checked={Boolean(item.is_available)}
              onChange={(event) => handleToggleInventory(item.id, event.target.checked)}
              inputProps={{ 'aria-label': 'toggle menu item status' }}
              sx={{ ml: 0.1, mr: 0.15 }}
            />
            <IconButton
              size="small"
              title="View"
              onClick={() => setMenuItemViewDialog({ open: true, menuItem: item })}
              sx={{
                width: 30,
                height: 30,
                color: isDark ? '#e9f1ff' : '#1f2d45',
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(31,45,69,0.08)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(31,45,69,0.15)',
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 18.5 }} />
            </IconButton>
            <IconButton
              size="small"
              title="Edit"
              onClick={() =>
                handleOpenMenuDrawer(MENU_SECTION_VALUES.ITEMS, {
                  menuItem: item,
                })
              }
              sx={{
                width: 30,
                height: 30,
                color: isDark ? '#8cc1ff' : '#2b66ad',
                bgcolor: isDark ? 'rgba(76,149,255,0.12)' : 'rgba(43,102,173,0.1)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(140,193,255,0.34)' : 'rgba(43,102,173,0.22)',
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              title="Delete"
              onClick={() =>
                setMenuItemDeleteDialog({
                  open: true,
                  menuItemId: item.id,
                  menuItemName: item.name || '',
                })
              }
              sx={{
                width: 30,
                height: 30,
                color: isDark ? '#ff9ea8' : '#d45b5b',
                bgcolor: isDark ? 'rgba(255,112,128,0.11)' : 'rgba(212,91,91,0.1)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,158,168,0.3)' : 'rgba(212,91,91,0.22)',
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18.5 }} />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [categoryLabelMap, handleOpenMenuDrawer, handleToggleInventory, isDark, variationLabelMap]
  );

  const menuFilterRowCells = useMemo(
    () =>
      menuTableColumns.map((column) => {
        if (column.field === 'name') {
          return (
            <FormTextField
              key={column.field}
              size="small"
              label="Name"
              value={menuItemFilters.search}
              onChange={(event) => handleMenuFilterChange('search', event.target.value)}
              sx={{ minWidth: 140 }}
            />
          );
        }
        if (column.field === 'category') {
          return (
            <FormSelect
              key={column.field}
              label=""
              value={menuItemFilters.category}
              onChange={(event) => handleMenuFilterChange('category', event.target.value)}
              displayEmpty
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {menuCategoryFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </FormSelect>
          );
        }
        if (column.field === 'dietary') {
          return (
            <FormSelect
              key={column.field}
              label=""
              value={menuItemFilters.dietary}
              onChange={(event) => handleMenuFilterChange('dietary', event.target.value)}
              displayEmpty
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All Dietary</MenuItem>
              {MENU_DIETARY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </FormSelect>
          );
        }
        if (column.field === 'status') {
          return (
            <FormSelect
              key={column.field}
              label=""
              value={menuItemFilters.status}
              onChange={(event) => handleMenuFilterChange('status', event.target.value)}
              displayEmpty
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="unavailable">Unavailable</MenuItem>
            </FormSelect>
          );
        }
        return <div key={column.field} />;
      }),
    [menuTableColumns, menuItemFilters, handleMenuFilterChange, menuCategoryFilterOptions]
  );

  if (error) {
    return (
      <ErrorState
        error={error}
        statusCode={error?.statusCode}
        onRetry={() => loadData({ initial: true })}
        showHomeButton={true}
      />
    );
  }

  return (
    <Box sx={pageShellSx}>
      <PageToastStack toasts={pageToasts} onDismiss={dismissPageToast} />
      <PrinterValidationBanner />

      <Box
        sx={{
          mb: 1.5,
          pb: 1,
          ...getPosSectionDividerSx(isDark),
        }}
      >
        <Breadcrumbs label="Back" fallbackPath="/dashboard/" isDark={isDark} sx={{ mb: 0 }} />
      </Box>

      <Paper elevation={0} sx={getPosHeroPaperSx(isDark)}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 0.85, md: 1.1 }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Stack direction="row" spacing={1.35} alignItems="flex-start" sx={getPosHeroContentSx()}>
            <Box
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                mt: 0.15,
                borderRadius: 2,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                bgcolor: isDark ? 'rgba(76,225,182,0.22)' : 'rgba(20,184,166,0.16)',
                border: `1px solid ${isDark ? 'rgba(76,225,182,0.46)' : 'rgba(20,184,166,0.28)'}`,
                boxShadow: isDark ? '0 10px 26px rgba(0,0,0,0.22)' : '0 10px 24px rgba(20,184,166,0.14)',
              }}
            >
              <LocalDiningIcon sx={{ color: ACCENT_COLOR, fontSize: { xs: 22, sm: 25 } }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, pb: 0.85 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: -0.3,
                  color: posThemeColors.text,
                  fontSize: { xs: '1.35rem', sm: '1.65rem' },
                }}
              >
                {activeHeader.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ...getPosHeroSubtitleSx(isDark),
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {activeHeader.subtitle}
              </Typography>
              <Stack
                direction="row"
                spacing={0.55}
                useFlexGap
                flexWrap="wrap"
                sx={getPosHeroChipRowSx()}
              >
                {renderPosHeroMetricChips(
                  activeTab,
                  isDark,
                  isBootstrapDataLoading,
                  dashboardAnalytics,
                  posHeroTabMetrics,
                  formatCurrency,
                )}
              </Stack>
            </Box>
          </Stack>
          <Box
            sx={getPosTabsShellSx(isDark, {
              minWidth: { md: 620 },
              alignSelf: { md: 'center' },
            })}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="standard"
              sx={getPosMainTabsSx(isDark)}
            >
              <Tab
                value={TAB_VALUES.DASHBOARD}
                icon={<DashboardCustomizeIcon />}
                iconPosition="start"
                label="Dashboard"
              />
              <Tab value={TAB_VALUES.ORDERS} icon={<ReceiptLongIcon />} iconPosition="start" label="Orders" />
              <Tab value={TAB_VALUES.ROOM} icon={<HotelIcon />} iconPosition="start" label="Room" />
              <Tab value={TAB_VALUES.TABLE} icon={<TableRestaurantIcon />} iconPosition="start" label="Table" />
              <Tab value={TAB_VALUES.MENU} icon={<RestaurantMenuIcon />} iconPosition="start" label="Menu" />
            </Tabs>
          </Box>
        </Stack>
      </Paper>

      {activeTab === TAB_VALUES.DASHBOARD && (
        <Stack spacing={2}>
          <Paper elevation={0} sx={{ ...getDashboardCardSx(isDark), p: 0, overflow: 'hidden' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.2}
              sx={{ px: 2, py: 1.2, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.3 }} component="h2">
                  POS dashboard insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                  Sales, leakage, payments and top-selling menu analytics in one view.
                </Typography>
              </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfOutlinedIcon />}
                    onClick={handleDownloadDashboardPdf}
                    disabled={isRefreshing || loading || dashboardPdfExporting}
                    sx={{
                      minWidth: 132,
                      px: 1.6,
                      py: 0.95,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 800,
                      letterSpacing: 0.2,
                      color: isDark ? '#f4f7fb' : '#172033',
                      borderColor: isDark ? alpha('#ffffff', 0.14) : alpha('#101828', 0.16),
                      bgcolor: isDark ? alpha('#ffffff', 0.045) : '#ffffff',
                      boxShadow: isDark ? '0 10px 24px rgba(8, 12, 24, 0.22)' : '0 10px 24px rgba(16, 24, 40, 0.08)',
                      '& .MuiButton-startIcon': { mr: 0.9 },
                      '&:hover': {
                        borderColor: alpha(ACCENT_COLOR, 0.7),
                        bgcolor: isDark ? alpha(ACCENT_COLOR, 0.12) : alpha(ACCENT_COLOR, 0.08),
                      },
                    }}
                  >
                    {dashboardPdfExporting ? 'Exporting...' : 'PDF Report'}
                  </Button>
                  <Button
                    onClick={(event) => setDashboardDateAnchorEl(event.currentTarget)}
                    disabled={isRefreshing}
                    endIcon={<KeyboardArrowDownIcon sx={{ color: '#02221b' }} />}
                    startIcon={<SelectedDashboardDateIcon sx={{ color: '#02221b' }} />}
                    sx={{
                      minWidth: 175,
                      px: 1.9,
                      py: 0.95,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 800,
                      letterSpacing: 0.2,
                      color: '#02221b',
                      justifyContent: 'space-between',
                      background: 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)',
                      border: '1px solid rgba(88, 242, 206, 0.85)',
                      boxShadow: '0 10px 26px rgba(36, 226, 178, 0.28)',
                      '& .MuiButton-startIcon': { mr: 1 },
                      '& .MuiButton-endIcon': { ml: 1 },
                      '&:hover': {
                        background: 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)',
                      },
                      '&.Mui-disabled': {
                        color: '#02221b',
                        opacity: 0.7,
                      },
                    }}
                  >
                    {selectedDashboardDateLabel}
                  </Button>
                  <Menu
                    anchorEl={dashboardDateAnchorEl}
                    open={isDashboardDateMenuOpen}
                    onClose={() => setDashboardDateAnchorEl(null)}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 230,
                        borderRadius: '12px',
                        border: `1px solid ${alpha(ACCENT_COLOR, 0.22)}`,
                        background: isDark
                          ? 'linear-gradient(180deg, #242937 0%, #1d2130 100%)'
                          : 'linear-gradient(180deg, #293041 0%, #1f2432 100%)',
                        boxShadow: '0 18px 34px rgba(8, 12, 24, 0.45)',
                        p: 0.55,
                      },
                    }}
                    MenuListProps={{ sx: { py: 0.2 } }}
                  >
                    {DASHBOARD_DATE_OPTIONS.map((option) => {
                      const OptionIcon = DASHBOARD_DATE_ICON_MAP[option.value] || CalendarMonthIcon;
                      const isSelected = option.value === dashboardRange;
                      return (
                        <MenuItem
                          key={option.value}
                          selected={isSelected}
                          onClick={() => handleSelectDashboardRange(option.value)}
                          sx={{
                            my: 0.35,
                            mx: 0.35,
                            px: 1.1,
                            py: 0.95,
                            borderRadius: '10px',
                            color: '#f2f7ff',
                            fontWeight: isSelected ? 800 : 700,
                            gap: 1.2,
                            '& .MuiListItemIcon-root': {
                              minWidth: 30,
                              color: ACCENT_COLOR,
                            },
                            '&:hover': {
                              backgroundColor: alpha(ACCENT_COLOR, 0.16),
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(ACCENT_COLOR, 0.24),
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: alpha(ACCENT_COLOR, 0.28),
                            },
                          }}
                        >
                          <Box component={OptionIcon} sx={{ fontSize: 21 }} />
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                  <Dialog
                    open={isCustomDateDialogOpen}
                    onClose={() => setIsCustomDateDialogOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                      sx: {
                        borderRadius: 2.3,
                        border: `1px solid ${alpha(ACCENT_COLOR, 0.26)}`,
                        background: isDark
                          ? 'linear-gradient(180deg, #1f2533 0%, #181d2b 100%)'
                          : 'linear-gradient(180deg, #ffffff 0%, #f7fffc 100%)',
                      },
                    }}
                  >
                    <DialogTitle sx={{ pb: 1.1, fontWeight: 800 }}>Custom Date Range</DialogTitle>
                    <DialogContent sx={{ pt: 0.8 }}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Stack spacing={1.4}>
                          <DatePicker
                            label="From Date"
                            format={getHotelDateFormat()}
                            value={
                              customDashboardRangeDraft.startDate
                                ? moment(customDashboardRangeDraft.startDate, 'YYYY-MM-DD')
                                : null
                            }
                            maxDate={
                              customDashboardRangeDraft.endDate
                                ? moment(customDashboardRangeDraft.endDate, 'YYYY-MM-DD')
                                : undefined
                            }
                            onChange={(nextValue) =>
                              setCustomDashboardRangeDraft((prev) => ({
                                ...prev,
                                startDate:
                                  nextValue && nextValue.isValid && nextValue.isValid()
                                    ? nextValue.format('YYYY-MM-DD')
                                    : '',
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                              },
                            }}
                          />
                          <DatePicker
                            label="To Date"
                            format={getHotelDateFormat()}
                            value={
                              customDashboardRangeDraft.endDate
                                ? moment(customDashboardRangeDraft.endDate, 'YYYY-MM-DD')
                                : null
                            }
                            minDate={
                              customDashboardRangeDraft.startDate
                                ? moment(customDashboardRangeDraft.startDate, 'YYYY-MM-DD')
                                : undefined
                            }
                            onChange={(nextValue) =>
                              setCustomDashboardRangeDraft((prev) => ({
                                ...prev,
                                endDate:
                                  nextValue && nextValue.isValid && nextValue.isValid()
                                    ? nextValue.format('YYYY-MM-DD')
                                    : '',
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                              },
                            }}
                          />
                        </Stack>
                      </LocalizationProvider>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.2 }}>
                      <Button
                        onClick={() => setIsCustomDateDialogOpen(false)}
                        variant="text"
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleApplyCustomDashboardRange}
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 800,
                          borderRadius: 1.7,
                          background: 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)',
                          color: '#02221b',
                          '&:hover': {
                            background: 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)',
                          },
                        }}
                      >
                        Apply
                      </Button>
                    </DialogActions>
                  </Dialog>
                <IconButton
                  onClick={() => {
                    loadData();
                    loadOrders({ silent: true });
                  }}
                  disabled={isRefreshing}
                  size="small"
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <AutorenewIcon
                    sx={{
                      animation: isRefreshing ? 'dashboard-refresh-spin 0.9s linear infinite' : 'none',
                      '@keyframes dashboard-refresh-spin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>

            <Box
              sx={{
                p: 2,
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {[
                {
                  title: 'Total Sales',
                  amount: dashboardAnalytics.totalSales,
                  orders: dashboardAnalytics.totalOrders,
                  tone: '#f5484b',
                  icon: DashboardCustomizeIcon,
                },
                {
                  title: 'Restaurant',
                  amount: dashboardAnalytics.dineInSales,
                  orders: dashboardAnalytics.dineInCount,
                  tone: '#3388f2',
                  icon: LocalDiningIcon,
                },
                {
                  title: 'Room Service',
                  amount: dashboardAnalytics.deliverySales,
                  orders: dashboardAnalytics.deliveryCount,
                  tone: '#5cab1f',
                  icon: HotelIcon,
                },
                {
                  title: 'Pick Up / Delivery',
                  amount: dashboardAnalytics.pickUpSales,
                  orders: dashboardAnalytics.pickUpCount,
                  tone: '#18c6d8',
                  icon: TakeoutDiningIcon,
                },
                {
                  title: 'Cancelled',
                  amount: dashboardAnalytics.cancelledValue,
                  orders: dashboardAnalytics.cancelledCount,
                  tone: '#ef4444',
                  icon: WarningAmberIcon,
                },
              ].map((item) => (
                <DashboardMetricCard
                  key={item.title}
                  title={item.title}
                  value={formatCurrency(item.amount)}
                  subtitle={`${item.orders} Orders`}
                  icon={item.icon}
                  isDark={isDark}
                  accent={item.tone}
                  isLoading={shouldShowDashboardSkeleton}
                />
              ))}
            </Box>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 3fr) minmax(0, 1fr)' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            <Box>
              <Paper
                elevation={0}
                sx={{
                  ...getDashboardCardSx(isDark),
                  p: { xs: 1.2, sm: 1.4 },
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  border: `1px solid ${isDark ? alpha('#3388f2', 0.34) : alpha('#3388f2', 0.26)}`,
                  background: isDark
                    ? 'linear-gradient(140deg, rgba(15,21,33,0.98) 0%, rgba(20,26,41,0.98) 52%, rgba(17,24,38,0.98) 100%)'
                    : 'linear-gradient(140deg, #ffffff 0%, #f4f9ff 52%, #f6fcfb 100%)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    right: -58,
                    top: -64,
                    width: 170,
                    height: 170,
                    borderRadius: '50%',
                    bgcolor: isDark ? alpha('#3388f2', 0.18) : alpha('#3388f2', 0.14),
                    filter: 'blur(2px)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: -44,
                    bottom: -78,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    bgcolor: isDark ? alpha('#18c6d8', 0.14) : alpha('#18c6d8', 0.1),
                  }}
                />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ position: 'relative', zIndex: 1 }}
                >
                  <DashboardSectionHeader
                    icon={QueryStatsIcon}
                    title="Sales trends"
                    subtitle={
                      dashboardAnalytics.salesTrendMode === 'date'
                        ? 'Date-wise trend for total and order channels'
                        : 'Time-slot trend for total and order channels'
                    }
                    isDark={isDark}
                    accent="#3388f2"
                  />
                  <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                    {[
                      { label: 'Total Sales', color: '#ff6b6b' },
                      { label: 'Restaurant', color: '#3388f2' },
                      { label: 'Room Service', color: '#5cab1f' },
                      { label: 'Pick Up / Delivery', color: '#18c6d8' },
                    ].map((legendItem) => (
                      <Chip
                        key={legendItem.label}
                        size="small"
                        label={legendItem.label}
                        sx={{
                          fontWeight: 700,
                          borderRadius: 1.5,
                          border: `1px solid ${alpha(legendItem.color, 0.34)}`,
                          bgcolor: isDark ? alpha(legendItem.color, 0.16) : alpha(legendItem.color, 0.12),
                          color: isDark ? '#e7efff' : '#334155',
                          '& .MuiChip-label': { px: 0.9 },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    ...getMiniCardSx(isDark, '#3388f2'),
                    p: { xs: 0.9, sm: 1.25 },
                    mt: 1,
                    borderRadius: 2.2,
                    border: `1px solid ${isDark ? alpha('#3388f2', 0.28) : alpha('#3388f2', 0.2)}`,
                    background: isDark
                      ? 'linear-gradient(160deg, rgba(14,19,30,0.86) 0%, rgba(16,23,36,0.9) 100%)'
                      : 'linear-gradient(160deg, #ffffff 0%, #f8fbff 100%)',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {shouldShowDashboardSkeleton ? (
                    <Skeleton variant="rounded" height={340} sx={{ borderRadius: 2 }} />
                  ) : (
                    <BarChart
                      dataset={salesChartData}
                      height={350}
                      margin={{
                        top: 16,
                        right: 18,
                        left: 8,
                        bottom: dashboardAnalytics.salesTrendMode === 'date' ? 40 : 32,
                      }}
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'slot',
                          categoryGapRatio: dashboardAnalytics.salesTrendMode === 'date' ? 0.2 : 0.32,
                          tickLabelStyle: {
                            fill: isDark ? '#d9e3f0' : '#455468',
                            fontSize: dashboardAnalytics.salesTrendData.length > 20 ? 9 : 11,
                            fontWeight: 800,
                          },
                        },
                      ]}
                      yAxis={[
                        {
                          tickLabelStyle: { fill: isDark ? '#d9e3f0' : '#455468', fontSize: 11, fontWeight: 700 },
                          valueFormatter: (value) => formatCurrency(value),
                        },
                      ]}
                      series={[
                        {
                          dataKey: 'total',
                          label: 'Total Sales',
                          color: '#ff6b6b',
                          borderRadius: 9,
                          valueFormatter: (value) => formatCurrency(value),
                        },
                        {
                          dataKey: 'dineIn',
                          label: 'Restaurant',
                          color: '#3388f2',
                          borderRadius: 9,
                          valueFormatter: (value) => formatCurrency(value),
                        },
                        {
                          dataKey: 'delivery',
                          label: 'Room Service',
                          color: '#5cab1f',
                          borderRadius: 9,
                          valueFormatter: (value) => formatCurrency(value),
                        },
                        {
                          dataKey: 'pickUp',
                          label: 'Pick Up / Delivery',
                          color: '#18c6d8',
                          borderRadius: 9,
                          valueFormatter: (value) => formatCurrency(value),
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                      grid={{ horizontal: true, vertical: false }}
                      sx={{
                        '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
                          stroke: isDark ? 'rgba(179,193,214,0.32)' : 'rgba(15,23,42,0.2)',
                        },
                        '& .MuiChartsGrid-line': {
                          stroke: isDark ? 'rgba(179,193,214,0.18)' : 'rgba(15,23,42,0.08)',
                          strokeDasharray: '4 5',
                        },
                        '& .MuiChartsAxis-tickLabel': {
                          fontWeight: 700,
                        },
                        '& .MuiBarElement-root': {
                          opacity: 0.95,
                          transition: 'all .22s ease',
                          filter: isDark ? 'drop-shadow(0 4px 8px rgba(3,8,20,0.35))' : 'drop-shadow(0 3px 7px rgba(15,23,42,0.18))',
                        },
                        '& .MuiBarElement-root:hover': {
                          opacity: 1,
                          filter: isDark ? 'drop-shadow(0 7px 12px rgba(8,15,35,0.5))' : 'drop-shadow(0 6px 10px rgba(15,23,42,0.28))',
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Box>

            <Box>
              <Paper
                elevation={0}
                sx={{
                  ...getDashboardCardSx(isDark),
                  p: { xs: 1.2, sm: 1.35 },
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  border: `1px solid ${isDark ? alpha('#ff8a65', 0.34) : alpha('#ff8a65', 0.26)}`,
                  background: isDark
                    ? 'linear-gradient(145deg, rgba(24,18,22,0.98) 0%, rgba(26,22,30,0.98) 52%, rgba(23,19,28,0.98) 100%)'
                    : 'linear-gradient(145deg, #ffffff 0%, #fff7f3 52%, #fffaf7 100%)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    right: -54,
                    top: -60,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    bgcolor: isDark ? alpha('#ff8a65', 0.18) : alpha('#ff8a65', 0.14),
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: -44,
                    bottom: -72,
                    width: 130,
                    height: 130,
                    borderRadius: '50%',
                    bgcolor: isDark ? alpha('#f97316', 0.15) : alpha('#f97316', 0.12),
                  }}
                />
                <Box sx={{ mb: 1 }}>
                  <DashboardSectionHeader
                    icon={WarningAmberIcon}
                    title="Revenue leakage"
                    subtitle="Bills and KOT indicators"
                    isDark={isDark}
                    accent="#ff8a65"
                  />
                </Box>

                <Box
                  sx={{
                    ...getMiniCardSx(isDark, '#ff8a65'),
                    mt: 0.9,
                    p: 1,
                    borderRadius: 2.1,
                    border: `1px solid ${isDark ? alpha('#ff8a65', 0.24) : alpha('#ff8a65', 0.18)}`,
                    background: isDark
                      ? 'linear-gradient(155deg, rgba(34,21,25,0.72) 0%, rgba(25,20,29,0.9) 100%)'
                      : 'linear-gradient(155deg, #ffffff 0%, #fff7f4 100%)',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {shouldShowDashboardSkeleton ? (
                    <Skeleton variant="rounded" height={360} sx={{ borderRadius: 2 }} />
                  ) : (
                    <BarChart
                      dataset={leakageBarData}
                      layout="horizontal"
                      height={360}
                      margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                      xAxis={[
                        {
                          min: 0,
                          max: leakageBarMax,
                          tickLabelStyle: { fill: isDark ? '#c4d0dd' : '#4e5c70', fontSize: 10 },
                        },
                      ]}
                      yAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'metric',
                          tickLabelStyle: { fill: isDark ? '#c4d0dd' : '#4e5c70', fontSize: 10 },
                        },
                      ]}
                      series={[{ dataKey: 'value', label: 'Leakage', color: '#ff8a65' }]}
                      slotProps={{ legend: { hidden: true } }}
                      grid={{ horizontal: true, vertical: false }}
                      sx={{
                        '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
                          stroke: isDark ? 'rgba(255,209,194,0.34)' : 'rgba(154,52,18,0.26)',
                        },
                        '& .MuiChartsGrid-line': {
                          stroke: isDark ? 'rgba(255,209,194,0.15)' : 'rgba(154,52,18,0.1)',
                          strokeDasharray: '4 5',
                        },
                        '& .MuiBarElement-root': {
                          borderRadius: 10,
                          filter: isDark ? 'drop-shadow(0 4px 8px rgba(25,10,10,0.4))' : 'drop-shadow(0 4px 7px rgba(124,45,18,0.2))',
                          transition: 'all .2s ease',
                        },
                        '& .MuiBarElement-root:hover': {
                          filter: isDark ? 'drop-shadow(0 7px 12px rgba(33,12,12,0.5))' : 'drop-shadow(0 6px 10px rgba(124,45,18,0.3))',
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' }, order: 99 }}>
              <Paper
                elevation={0}
                sx={{
                  ...getDashboardCardSx(isDark),
                  p: { xs: 1.2, sm: 1.35 },
                  position: 'relative',
                  overflow: 'hidden',
                  border: `1px solid ${isDark ? alpha('#8b5cf6', 0.34) : alpha('#8b5cf6', 0.25)}`,
                  background: isDark
                    ? 'linear-gradient(150deg, rgba(23,20,36,0.98) 0%, rgba(19,23,38,0.98) 50%, rgba(17,21,35,0.98) 100%)'
                    : 'linear-gradient(150deg, #ffffff 0%, #f8f5ff 54%, #f4fbff 100%)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    right: -62,
                    top: -68,
                    width: 176,
                    height: 176,
                    borderRadius: '50%',
                    bgcolor: isDark ? alpha('#8b5cf6', 0.18) : alpha('#8b5cf6', 0.12),
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: -54,
                    bottom: -78,
                    width: 148,
                    height: 148,
                    borderRadius: '50%',
                    bgcolor: isDark ? alpha('#22d3ee', 0.16) : alpha('#22d3ee', 0.11),
                  }}
                />
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={1}
                  sx={{ position: 'relative', zIndex: 1 }}
                >
                  <DashboardSectionHeader
                    icon={EventNoteIcon}
                    title="Restaurant SLA service performance"
                    subtitle={
                      dashboardAnalytics.salesTrendMode === 'date'
                        ? 'Average SLA target vs actual service minutes by date range'
                        : 'Average SLA target vs actual service minutes by service slots'
                    }
                    isDark={isDark}
                    accent="#8b5cf6"
                  />
                  <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                    <Chip size="small" label="SLA Target" sx={{ fontWeight: 700, bgcolor: alpha('#60a5fa', 0.18), color: isDark ? '#dbeafe' : '#1d4ed8' }} />
                    <Chip size="small" label="Actual Service" sx={{ fontWeight: 700, bgcolor: alpha('#a78bfa', 0.18), color: isDark ? '#ede9fe' : '#6d28d9' }} />
                    <Chip size="small" label="Delay" sx={{ fontWeight: 700, bgcolor: alpha('#fb7185', 0.18), color: isDark ? '#ffe4e6' : '#be123c' }} />
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.8, mb: 1 }}>
                  <Chip
                    size="small"
                    label={`Measured Orders ${slaPerformanceSummary.measuredOrders}`}
                    sx={{ fontWeight: 700, bgcolor: isDark ? alpha('#38bdf8', 0.2) : alpha('#38bdf8', 0.14), color: isDark ? '#bae6fd' : '#075985' }}
                  />
                  <Chip
                    size="small"
                    label={`Avg SLA ${formatDurationFromMinutes(slaPerformanceSummary.avgTargetMinutes)}`}
                    sx={{ fontWeight: 700, bgcolor: isDark ? alpha('#60a5fa', 0.2) : alpha('#60a5fa', 0.14), color: isDark ? '#dbeafe' : '#1d4ed8' }}
                  />
                  <Chip
                    size="small"
                    label={`Avg Service ${formatDurationFromMinutes(slaPerformanceSummary.avgActualMinutes)}`}
                    sx={{ fontWeight: 700, bgcolor: isDark ? alpha('#a78bfa', 0.2) : alpha('#a78bfa', 0.14), color: isDark ? '#ede9fe' : '#6d28d9' }}
                  />
                  <Chip
                    size="small"
                    label={`On-Time ${slaPerformanceSummary.onTimeRate.toFixed(1)}%`}
                    sx={{ fontWeight: 700, bgcolor: isDark ? alpha('#22c55e', 0.2) : alpha('#22c55e', 0.14), color: isDark ? '#dcfce7' : '#166534' }}
                  />
                </Stack>
                <Box
                  sx={{
                    ...getMiniCardSx(isDark, '#8b5cf6'),
                    mt: 0.8,
                    p: { xs: 0.8, sm: 1.2 },
                    borderRadius: 2.1,
                    border: `1px solid ${isDark ? alpha('#8b5cf6', 0.28) : alpha('#8b5cf6', 0.2)}`,
                    background: isDark
                      ? 'linear-gradient(160deg, rgba(19,20,34,0.86) 0%, rgba(16,23,36,0.9) 100%)'
                      : 'linear-gradient(160deg, #ffffff 0%, #faf8ff 100%)',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {shouldShowDashboardSkeleton ? (
                    <Skeleton variant="rounded" height={330} sx={{ borderRadius: 2 }} />
                  ) : (
                    <LineChart
                      dataset={slaPerformanceChartData}
                      height={330}
                      margin={{
                        top: 16,
                        right: 18,
                        left: 10,
                        bottom: dashboardAnalytics.salesTrendMode === 'date' ? 42 : 34,
                      }}
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'slot',
                          tickLabelStyle: {
                            fill: isDark ? '#ddd6fe' : '#4c1d95',
                            fontSize: dashboardAnalytics.salesTrendData.length > 20 ? 9 : 11,
                            fontWeight: 800,
                          },
                        },
                      ]}
                      yAxis={[
                        {
                          tickLabelStyle: { fill: isDark ? '#d9e3f0' : '#455468', fontSize: 11, fontWeight: 700 },
                          valueFormatter: (value) => formatDurationFromMinutes(value),
                        },
                      ]}
                      series={[
                        {
                          dataKey: 'targetAvg',
                          label: 'SLA Target',
                          color: '#60a5fa',
                          valueFormatter: (value) => formatDurationFromMinutes(value),
                          showMark: false,
                        },
                        {
                          dataKey: 'actualAvg',
                          label: 'Actual Service',
                          color: '#a78bfa',
                          valueFormatter: (value) => formatDurationFromMinutes(value),
                          showMark: false,
                        },
                        {
                          dataKey: 'delayAvg',
                          label: 'Delay',
                          color: '#fb7185',
                          valueFormatter: (value) => formatDurationFromMinutes(value),
                          showMark: false,
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                      grid={{ horizontal: true, vertical: false }}
                      sx={{
                        '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
                          stroke: isDark ? 'rgba(192,132,252,0.34)' : 'rgba(124,58,237,0.22)',
                        },
                        '& .MuiChartsGrid-line': {
                          stroke: isDark ? 'rgba(196,181,253,0.16)' : 'rgba(109,40,217,0.08)',
                          strokeDasharray: '4 5',
                        },
                        '& .MuiLineElement-root': {
                          strokeWidth: 3,
                          filter: isDark ? 'drop-shadow(0 4px 8px rgba(9,11,24,0.42))' : 'drop-shadow(0 3px 7px rgba(76,29,149,0.2))',
                        },
                        '& .MuiMarkElement-root': {
                          strokeWidth: 0,
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Box>

            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...getDashboardCardSx(isDark),
                      p: { xs: 1.2, sm: 1.35 },
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      border: `1px solid ${isDark ? alpha('#5cab1f', 0.34) : alpha('#5cab1f', 0.26)}`,
                      background: isDark
                        ? 'linear-gradient(145deg, rgba(16,23,22,0.98) 0%, rgba(20,28,24,0.98) 55%, rgba(18,24,20,0.98) 100%)'
                        : 'linear-gradient(145deg, #ffffff 0%, #f6fff2 54%, #fbfffd 100%)',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -56,
                        top: -62,
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        bgcolor: isDark ? alpha('#5cab1f', 0.18) : alpha('#5cab1f', 0.14),
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -50,
                        bottom: -76,
                        width: 136,
                        height: 136,
                        borderRadius: '50%',
                        bgcolor: isDark ? alpha('#22c55e', 0.14) : alpha('#22c55e', 0.12),
                      }}
                    />
                    <Box sx={{ mb: 1 }}>
                      <DashboardSectionHeader
                        icon={DonutLargeIcon}
                        title="Payment bifurcation"
                        subtitle="Channel contribution and mix"
                        isDark={isDark}
                        accent="#5cab1f"
                      />
                    </Box>
                    <Divider sx={{ mb: 1.2 }} />
                    <Box
                      sx={{
                        ...getMiniCardSx(isDark, '#5cab1f'),
                        p: 1.2,
                        borderRadius: 2.1,
                        border: `1px solid ${isDark ? alpha('#5cab1f', 0.25) : alpha('#5cab1f', 0.18)}`,
                        background: isDark
                          ? 'linear-gradient(155deg, rgba(13,28,20,0.78) 0%, rgba(20,27,23,0.92) 100%)'
                          : 'linear-gradient(155deg, #ffffff 0%, #f7fff3 100%)',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {shouldShowDashboardSkeleton ? (
                        <Skeleton variant="rounded" height={340} width="100%" sx={{ borderRadius: 2 }} />
                      ) : (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
                            gap: 1.2,
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: 1.8,
                              border: `1px solid ${isDark ? alpha('#84cc16', 0.3) : alpha('#65a30d', 0.22)}`,
                              bgcolor: isDark ? alpha('#0f172a', 0.24) : alpha('#ffffff', 0.84),
                              p: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                letterSpacing: 0.3,
                                color: isDark ? '#d8f3c5' : '#3f6212',
                              }}
                            >
                              Payment Method
                            </Typography>
                            <Stack spacing={0.75} sx={{ mt: 0.9 }}>
                              {paymentMethodMixRows.map((row) => (
                                <Box
                                  key={String(row.id)}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr auto',
                                    alignItems: 'center',
                                    columnGap: 0.75,
                                    p: 0.6,
                                    borderRadius: 1.1,
                                    bgcolor: isDark ? alpha('#1f2937', 0.46) : alpha('#f8fafc', 0.86),
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 9,
                                      height: 9,
                                      borderRadius: '50%',
                                      bgcolor: row.color,
                                    }}
                                  />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        fontWeight: 700,
                                        color: isDark ? '#e8edf4' : '#1f2937',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {row.label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isDark ? '#9fb0c6' : '#64748b' }}>
                                      {row.sharePercent.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 800,
                                      color: isDark ? '#d7f6be' : '#365314',
                                      textAlign: 'right',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {formatCurrency(row.value)}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
                            <PieChart
                              width={350}
                              height={320}
                              margin={{ top: 12, right: 8, bottom: 12, left: 8 }}
                              series={[
                                {
                                  data: paymentPieSeriesData,
                                  innerRadius: 66,
                                  outerRadius: 125,
                                  cornerRadius: 7,
                                  paddingAngle: 2,
                                  arcLabel: (item) =>
                                    item.label === 'No payment data'
                                      ? ''
                                      : `${((item.value / (paymentPieTotalAmount || 1)) * 100).toFixed(1)}%`,
                                  arcLabelMinAngle: 22,
                                  valueFormatter: (item) =>
                                    item.label === 'No payment data'
                                      ? item.label
                                      : `${item.label}: ${formatCurrency(item.value)}`,
                                },
                              ]}
                              slotProps={{ legend: { hidden: true } }}
                              sx={{
                                '& .MuiPieArcLabel-root': {
                                  fill: isDark ? '#f4f7fb' : '#1a2433',
                                  fontWeight: 700,
                                  fontSize: 11,
                                },
                                '& .MuiPieArc-root': {
                                  transition: 'transform .2s ease, filter .2s ease',
                                  transformOrigin: 'center center',
                                  filter: isDark ? 'drop-shadow(0 3px 8px rgba(8,18,9,0.45))' : 'drop-shadow(0 3px 6px rgba(21,128,61,0.2))',
                                },
                                '& .MuiPieArc-root:hover': {
                                  filter: isDark ? 'drop-shadow(0 6px 12px rgba(8,18,9,0.55))' : 'drop-shadow(0 6px 10px rgba(21,128,61,0.28))',
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...getDashboardCardSx(isDark),
                      p: { xs: 1.2, sm: 1.35 },
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      border: `1px solid ${isDark ? alpha('#4ce1b6', 0.32) : alpha('#4ce1b6', 0.24)}`,
                      background: isDark
                        ? 'linear-gradient(145deg, rgba(16,25,24,0.98) 0%, rgba(17,28,30,0.98) 56%, rgba(16,24,27,0.98) 100%)'
                        : 'linear-gradient(145deg, #ffffff 0%, #f2fffa 56%, #f8fffd 100%)',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -54,
                        top: -58,
                        width: 155,
                        height: 155,
                        borderRadius: '50%',
                        bgcolor: isDark ? alpha('#4ce1b6', 0.18) : alpha('#4ce1b6', 0.14),
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -50,
                        bottom: -74,
                        width: 134,
                        height: 134,
                        borderRadius: '50%',
                        bgcolor: isDark ? alpha('#06b6d4', 0.15) : alpha('#06b6d4', 0.12),
                      }}
                    />
                    <Box sx={{ mb: 1 }}>
                      <DashboardSectionHeader
                        icon={RestaurantMenuIcon}
                        title="Top selling menu items"
                        subtitle="Best selling items by sales amount"
                        isDark={isDark}
                        accent={ACCENT_COLOR}
                      />
                    </Box>
                    <Divider sx={{ mb: 1.2 }} />
                    <Box
                      sx={{
                        ...getMiniCardSx(isDark, '#4ce1b6'),
                        p: 1,
                        borderRadius: 2.1,
                        border: `1px solid ${isDark ? alpha('#4ce1b6', 0.24) : alpha('#4ce1b6', 0.18)}`,
                        background: isDark
                          ? 'linear-gradient(155deg, rgba(12,27,24,0.78) 0%, rgba(16,27,30,0.92) 100%)'
                          : 'linear-gradient(155deg, #ffffff 0%, #f2fffa 100%)',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {shouldShowDashboardSkeleton ? (
                        <Skeleton variant="rounded" height={340} sx={{ borderRadius: 2 }} />
                      ) : (
                        <BarChart
                          dataset={topSellingMenuData}
                          layout="horizontal"
                          height={340}
                          margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                          xAxis={[
                            {
                              min: 0,
                              max: topSellingMax,
                              tickLabelStyle: { fill: isDark ? '#c4d0dd' : '#4e5c70', fontSize: 10 },
                            },
                          ]}
                          yAxis={[
                            {
                              scaleType: 'band',
                              dataKey: 'item',
                              tickLabelStyle: { fill: isDark ? '#c4d0dd' : '#4e5c70', fontSize: 10 },
                            },
                          ]}
                          series={[{ dataKey: 'sales', label: 'Sales', color: '#4ce1b6' }]}
                          slotProps={{ legend: { hidden: true } }}
                          grid={{ horizontal: true, vertical: false }}
                          sx={{
                            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
                              stroke: isDark ? 'rgba(178,244,228,0.3)' : 'rgba(13,148,136,0.24)',
                            },
                            '& .MuiChartsGrid-line': {
                              stroke: isDark ? 'rgba(178,244,228,0.14)' : 'rgba(13,148,136,0.09)',
                              strokeDasharray: '4 5',
                            },
                            '& .MuiBarElement-root': {
                              borderRadius: 10,
                              filter: isDark ? 'drop-shadow(0 4px 8px rgba(7,20,18,0.4))' : 'drop-shadow(0 3px 7px rgba(13,148,136,0.2))',
                              transition: 'all .2s ease',
                            },
                            '& .MuiBarElement-root:hover': {
                              filter: isDark ? 'drop-shadow(0 7px 12px rgba(8,22,20,0.5))' : 'drop-shadow(0 6px 10px rgba(13,148,136,0.28))',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Stack>
      )}

      {activeTab === TAB_VALUES.ORDERS && (
        <Paper
          elevation={0}
          sx={getConfigCardSx(isDark, {
            p: 0,
            overflow: 'hidden',
            ...getPosOrdersDataTableContainerSx(isDark),
          })}
        >
          {ordersError ? (
            <Alert severity="error" sx={{ mb: 1.2 }}>
              {ordersError}
            </Alert>
          ) : null}
          <Box
            sx={{
              px: { xs: 1.25, sm: 2 },
              py: 1.35,
              borderBottom: '1px solid',
              borderColor: isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.1),
              backgroundColor: isDark ? alpha('#ffffff', 0.02) : alpha('#0f172a', 0.015),
            }}
          >
            <Stack spacing={1.15}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap" alignItems="center">
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <PaymentsIcon sx={{ fontSize: 16, color: POS_ACCENT }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        color: posThemeColors.subtleText,
                        textTransform: 'uppercase',
                      }}
                    >
                      Filtered totals
                    </Typography>
                  </Stack>
                  <Chip
                    size="small"
                    label={`${ordersPaymentSummary.orderCount} order${ordersPaymentSummary.orderCount === 1 ? '' : 's'}`}
                    sx={{
                      height: 24,
                      fontWeight: 700,
                      bgcolor: isDark ? alpha('#ffffff', 0.06) : alpha('#0f172a', 0.05),
                    }}
                  />
                  {ordersFilters.payment_method?.length ? (
                    <Chip
                      size="small"
                      clickable
                      onClick={clearOrdersPaymentMethodFilter}
                      label={`Clear method filter (${ordersFilters.payment_method.length})`}
                      sx={{
                        height: 24,
                        fontWeight: 700,
                        bgcolor: alpha('#f59e0b', 0.14),
                        color: isDark ? '#fbbf24' : '#b45309',
                        '&:hover': { bgcolor: alpha('#f59e0b', 0.22) },
                      }}
                    />
                  ) : null}
                </Stack>

                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="stretch">
                  {[
                    {
                      key: 'total',
                      label: ordersPaymentSummary.totalLabel,
                      value: ordersPaymentSummary.totalAmount,
                      color: POS_ACCENT,
                    },
                    {
                      key: 'collected',
                      label: 'Collected',
                      value: ordersPaymentSummary.collectedAmount,
                      color: '#22c55e',
                    },
                    ...(ordersPaymentSummary.hasMethodFilter
                      ? []
                      : [{
                          key: 'due',
                          label: 'Due',
                          value: ordersPaymentSummary.dueAmount,
                          color: ordersPaymentSummary.dueAmount > 0 ? '#f59e0b' : (isDark ? '#94a3b8' : '#64748b'),
                        }]),
                    {
                      key: 'cancelled',
                      label: `Cancelled (${ordersPaymentSummary.cancelledCount})`,
                      value: ordersPaymentSummary.cancelledAmount,
                      color: '#ef4444',
                    },
                  ].map((metric) => (
                    <Box
                      key={metric.key}
                      sx={{
                        minWidth: { xs: '30%', sm: 128 },
                        px: 1.1,
                        py: 0.7,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: alpha(metric.color, isDark ? 0.35 : 0.28),
                        bgcolor: alpha(metric.color, isDark ? 0.12 : 0.08),
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          color: alpha(metric.color, isDark ? 0.95 : 0.9),
                          lineHeight: 1.2,
                        }}
                      >
                        {metric.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: isDark ? '#f8fafc' : '#0f172a',
                          lineHeight: 1.25,
                          mt: 0.15,
                        }}
                      >
                        {formatCurrency(metric.value)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.4 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: posThemeColors.subtleText }}>
                    Collection progress
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: POS_ACCENT }}>
                    {ordersPaymentSummary.collectedShare.toFixed(0)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={ordersPaymentSummary.collectedShare}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    bgcolor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: POS_ACCENT,
                    },
                  }}
                />
              </Box>

              {ordersPaymentSummary.paymentMethods.length ? (
                <Stack spacing={0.55}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: posThemeColors.subtleText }}>
                    Payment methods — tap to filter the table
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                    {ordersPaymentSummary.paymentMethods.map((method, index) => {
                      const methodColor = getPaymentMethodChartColor(method.key, index);
                      const isSelected = (ordersFilters.payment_method || []).includes(method.key);
                      return (
                        <Tooltip
                          key={method.key}
                          title={
                            isSelected
                              ? `Remove ${method.label} filter`
                              : `Filter orders by ${method.label}`
                          }
                        >
                          <Chip
                            clickable
                            size="small"
                            onClick={() => toggleOrdersPaymentMethodFilter(method.key)}
                            label={`${method.label} ${formatCurrency(method.amount)}`}
                            sx={{
                              height: 28,
                              fontWeight: 700,
                              border: '1.5px solid',
                              borderColor: isSelected
                                ? methodColor
                                : alpha(methodColor, isDark ? 0.45 : 0.35),
                              bgcolor: isSelected
                                ? alpha(methodColor, isDark ? 0.28 : 0.18)
                                : alpha(methodColor, isDark ? 0.12 : 0.08),
                              color: isDark ? '#f8fafc' : '#0f172a',
                              transition: 'background-color .15s ease, border-color .15s ease, transform .15s ease',
                              '&:hover': {
                                bgcolor: alpha(methodColor, isDark ? 0.24 : 0.16),
                                borderColor: methodColor,
                                transform: 'translateY(-1px)',
                              },
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                  {ordersFilters.payment_method?.length ? (
                    <Typography variant="caption" sx={{ color: posThemeColors.subtleText }}>
                      Table filtered by selected payment method
                      {ordersFilters.payment_method.length > 1 ? 's' : ''}. Totals update with the table.
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="caption" sx={{ color: posThemeColors.subtleText }}>
                  No captured payments in the current filter range.
                </Typography>
              )}
            </Stack>
          </Box>
          <DataTable
            columns={orderTableColumns}
            data={paginatedOrders}
            loading={ordersLoading}
            optimizeScroll={true}
            pagination={ordersPagination}
            onPageChange={handleOrdersPageChange}
            onPerPageChange={handleOrdersPerPageChange}
            onSortChange={handleOrdersSortChange}
            sortConfig={ordersSortConfig}
            emptyMessage="No POS orders found matching the selected filters."
            stickyHeader={true}
            filterRow={orderFilterRowCells}
          />
        </Paper>
      )}

      {activeTab === TAB_VALUES.ROOM && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={getConfigCardSx(isDark)}>
              <ConfigCardHeader
                icon={HotelIcon}
                title="Room Ordering Settings"
                subtitle="Configure room order accessibility"
                isDark={isDark}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settingsForm.enable_room_order)}
                    onChange={(e) =>
                      setSettingsForm((prev) => ({ ...prev, enable_room_order: e.target.checked }))
                    }
                  />
                }
                label="Enable room orders"
              />
              <Button
                onClick={handleSaveSettings}
                variant="contained"
                sx={getPosPrimaryButtonSx(isDark, { mt: 1.5, fontWeight: 600 })}
              >
                Save Settings
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={getConfigCardSx(isDark)}>
              <ConfigCardHeader
                icon={RoomServiceIcon}
                title="Room Enable for Billing"
                subtitle="Enable or disable room-wise restaurant billing"
                isDark={isDark}
                action={
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="configured-rooms-sort-label">Sort rooms</InputLabel>
                    <Select
                      labelId="configured-rooms-sort-label"
                      label="Sort rooms"
                      value={configuredRoomsSort}
                      onChange={(event) => setConfiguredRoomsSort(event.target.value)}
                    >
                      <MenuItem value="room_number_asc">Room number (low to high)</MenuItem>
                      <MenuItem value="room_number_desc">Room number (high to low)</MenuItem>
                      <MenuItem value="floor_asc">Floor (A–Z)</MenuItem>
                      <MenuItem value="floor_desc">Floor (Z–A)</MenuItem>
                      <MenuItem value="room_type_asc">Room type (A–Z)</MenuItem>
                      <MenuItem value="room_type_desc">Room type (Z–A)</MenuItem>
                    </Select>
                  </FormControl>
                }
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: posThemeColors.subtleText }}>
                  Room Enable for Restaurant
                </Typography>
                <Button
                  onClick={handleSaveRoomConfig}
                  variant="contained"
                  sx={getPosPrimaryButtonSx(isDark, { fontWeight: 600 })}
                >
                  Save Room Configuration
                </Button>
              </Stack>
              <Grid container spacing={1}>
                {isBootstrapDataLoading ? (
                  <Grid item xs={12}>
                    <PosCardGridSkeleton count={6} height={96} />
                  </Grid>
                ) : (
                  sortedConfiguredRooms.map((room) => (
                  <Grid item xs={12} sm={6} md={4} key={room.id}>
                    <Paper variant="outlined" sx={getMiniCardSx(isDark, '#4ce1b6')}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0, pr: 0.8 }}>
                          <Typography sx={{ fontWeight: 700 }}>{room.room_number}</Typography>
                          <Chip
                            size="small"
                            label={room.room_type_name || 'Room type'}
                            sx={{
                              mt: 0.65,
                              height: 22,
                              fontWeight: 700,
                              borderRadius: 1.2,
                              color: '#4ce1b6',
                              bgcolor: isDark ? alpha('#4ce1b6', 0.16) : alpha('#4ce1b6', 0.2),
                              border: `1px solid ${alpha('#4ce1b6', 0.42)}`,
                              '& .MuiChip-label': { px: 0.9, fontSize: 11.5 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.7, color: posThemeColors.subtleText }}
                          >
                            Floor: {room.floor_label || room.floor_name || 'No floor assigned'}
                          </Typography>
                        </Box>
                        <Stack spacing={0.3} alignItems="center">
                          <IconButton
                            size="small"
                            title={`Generate QR for Room ${room.room_number || room.id}`}
                            onClick={() => handleOpenQrPreview('room', room)}
                            sx={{
                              width: 28,
                              height: 28,
                              p: 0.45,
                              color: '#4ce1b6',
                              bgcolor: isDark ? alpha('#4ce1b6', 0.16) : alpha('#4ce1b6', 0.2),
                              border: `1px solid ${alpha('#4ce1b6', 0.35)}`,
                              '&:hover': {
                                bgcolor: isDark ? alpha('#4ce1b6', 0.24) : alpha('#4ce1b6', 0.3),
                              },
                            }}
                          >
                            <QrCode2Icon fontSize="small" />
                          </IconButton>
                          <Switch
                            checked={Boolean(room.is_enabled_for_order)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setData((prev) => ({
                                ...prev,
                                rooms: prev.rooms.map((row) =>
                                  row.id === room.id ? { ...row, is_enabled_for_order: checked } : row
                                ),
                              }));
                            }}
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                  ))
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === TAB_VALUES.TABLE && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={getConfigCardSx(isDark)}>
              <ConfigCardHeader
                icon={TableRestaurantIcon}
                title="Add Table / Area"
                subtitle="Create table setup for dine-in orders"
                isDark={isDark}
              />
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Table Name"
                    value={tableForm.name}
                    onChange={(e) => setTableForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Capacity"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={tableForm.capacity}
                    onChange={(e) => setTableForm((prev) => ({ ...prev, capacity: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Extra Information"
                    placeholder="Window side, near bar, private table, etc."
                    value={tableForm.extraInformation}
                    onChange={(e) => setTableForm((prev) => ({ ...prev, extraInformation: e.target.value }))}
                  />
                </Grid>
              </Grid>
              <Button
                startIcon={<AddIcon />}
                onClick={handleCreateTable}
                sx={getPosPrimaryButtonSx(isDark, { mt: 1.5, fontWeight: 600 })}
                variant="contained"
              >
                Add Table
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={getConfigCardSx(isDark)}>
              <ConfigCardHeader
                icon={TableRestaurantIcon}
                title="Configured Tables"
                subtitle="Current active table and seating setup"
                isDark={isDark}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel id="configured-tables-sort-label">Sort tables</InputLabel>
                  <Select
                    labelId="configured-tables-sort-label"
                    label="Sort tables"
                    value={configuredTablesSort}
                    onChange={(event) => setConfiguredTablesSort(event.target.value)}
                  >
                    <MenuItem value="name_asc">Table number / name (A–Z)</MenuItem>
                    <MenuItem value="name_desc">Table number / name (Z–A)</MenuItem>
                    <MenuItem value="capacity_asc">Capacity (low to high)</MenuItem>
                    <MenuItem value="capacity_desc">Capacity (high to low)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Grid container spacing={1}>
                {isBootstrapDataLoading ? (
                  <Grid item xs={12}>
                    <PosTableCardsSkeleton count={6} />
                  </Grid>
                ) : (
                  sortedConfiguredTables.map((table) => (
                  <Grid item xs={6} sm={4} key={table.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        ...getMiniCardSx(isDark, '#4ce1b6'),
                        p: { xs: 1, sm: 1.15 },
                        minHeight: { xs: 108, sm: 112 },
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        opacity: table.is_active ? 1 : 0.72,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                          border: `1px solid ${alpha('#4ce1b6', 0.38)}`,
                          bgcolor: isDark ? alpha('#4ce1b6', 0.12) : alpha('#4ce1b6', 0.18),
                        }}
                      >
                        <TableRestaurantIcon sx={{ color: '#4ce1b6', fontSize: 24 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 800, lineHeight: 1.05, letterSpacing: -0.2, fontSize: { xs: '1.35rem', sm: '1.45rem' } }}
                        >
                          {table.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', mt: 0.32, fontWeight: 700, color: posThemeColors.text, letterSpacing: 0.08 }}
                        >
                          Seats {table.capacity}
                        </Typography>
                        {table.extra_information ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.18,
                              color: posThemeColors.subtleText,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: { xs: 105, sm: 118 },
                            }}
                            title={table.extra_information}
                          >
                            {table.extra_information}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack
                        spacing={0.2}
                        alignItems="center"
                        sx={{ ml: 'auto', flexShrink: 0, pl: 0.3 }}
                      >
                        <IconButton
                          size="small"
                          title={`Generate QR for ${table.name || `Table ${table.id}`}`}
                          onClick={() => handleOpenQrPreview('table', table)}
                          sx={{
                            width: 28,
                            height: 28,
                            p: 0.45,
                            color: '#4ce1b6',
                            bgcolor: isDark ? alpha('#4ce1b6', 0.16) : alpha('#4ce1b6', 0.2),
                            border: `1px solid ${alpha('#4ce1b6', 0.35)}`,
                            '&:hover': {
                              bgcolor: isDark ? alpha('#4ce1b6', 0.24) : alpha('#4ce1b6', 0.3),
                            },
                          }}
                        >
                          <QrCode2Icon fontSize="small" />
                        </IconButton>
                        <Switch
                          size="small"
                          checked={Boolean(table.is_active)}
                          onChange={(event) => handleToggleTableActive(table.id, event.target.checked)}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            setTableDeleteDialog({
                              open: true,
                              tableId: table.id,
                              tableName: table.name || '',
                            })
                          }
                          sx={{ color: isDark ? '#f68b8b' : '#d45b5b' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Grid>
                  ))
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === TAB_VALUES.MENU && (
        <Paper elevation={0} sx={getConfigCardSx(isDark)}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ ...getPosSectionDividerSx(isDark), pb: 1.5, mb: 0.5 }}
          >
            <Box
              sx={getPosTabsShellSx(isDark, {
                width: { xs: '100%', md: 'fit-content' },
                minWidth: { md: 420 },
                ml: 0,
              })}
            >
              <Tabs
                value={menuSectionTab}
                onChange={(_, value) => setMenuSectionTab(value)}
                variant="standard"
                sx={getPosMenuSectionTabsSx(isDark)}
              >
                <Tab value={MENU_SECTION_VALUES.ITEMS} label="Menu Item" />
                <Tab value={MENU_SECTION_VALUES.CATEGORIES} label="Categories" />
                <Tab value={MENU_SECTION_VALUES.VARIATIONS} label="Variation" />
              </Tabs>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
            >
              {(menuSectionTab === MENU_SECTION_VALUES.CATEGORIES
                || menuSectionTab === MENU_SECTION_VALUES.ITEMS) && (
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAiMenuUploaderOpen(true)}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.5,
                    borderColor: alpha('#4ce1b6', 0.55),
                    color: isDark ? '#8de5cd' : '#0f766e',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#4ce1b6',
                      bgcolor: alpha('#4ce1b6', isDark ? 0.12 : 0.08),
                    },
                  }}
                >
                  AI Menu
                </Button>
              )}
              <Button
                startIcon={<AddIcon />}
                onClick={() =>
                  menuSectionTab === MENU_SECTION_VALUES.CATEGORIES
                    ? handleOpenMenuDrawer(MENU_SECTION_VALUES.CATEGORIES)
                    : handleOpenMenuDrawer(menuSectionTab)
                }
                variant="contained"
                sx={getPosPrimaryButtonSx(isDark, {
                  whiteSpace: 'nowrap',
                })}
              >
                {menuSectionTab === MENU_SECTION_VALUES.CATEGORIES
                  ? 'Add Category'
                  : menuSectionTab === MENU_SECTION_VALUES.VARIATIONS
                    ? 'Add Variation'
                    : 'Add Menu Item'}
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mt: 1.5 }}>
            {menuSectionTab === MENU_SECTION_VALUES.ITEMS && (
              <>
                <ConfigCardHeader
                  icon={RestaurantMenuIcon}
                  title="Menu Item List"
                  subtitle="Browse all configured menu items"
                  isDark={isDark}
                />
                <DataTable
                  columns={menuTableColumns}
                  data={paginatedMenuItems}
                  loading={isBootstrapDataLoading}
                  optimizeScroll={true}
                  pagination={menuItemsPagination}
                  onPageChange={handleMenuPageChange}
                  onPerPageChange={handleMenuPerPageChange}
                  onSortChange={handleMenuSortChange}
                  sortConfig={menuItemSortConfig}
                  emptyMessage="No menu items found matching the selected filters."
                  stickyHeader={true}
                  filterRow={menuFilterRowCells}
                />
              </>
            )}

            {menuSectionTab === MENU_SECTION_VALUES.CATEGORIES && (
              <>
                <ConfigCardHeader
                  icon={CategoryIcon}
                  title="Category List"
                  subtitle="Manage parent and child category mapping"
                  isDark={isDark}
                />
                <Grid
                  container
                  spacing={1}
                  onDragOver={(event) => {
                    if (categoryDragState.draggingType === 'parent') {
                      event.preventDefault();
                    }
                  }}
                  onDrop={async (event) => {
                    if (categoryDragState.draggingType === 'parent') {
                      event.preventDefault();
                      await handleParentDrop(null);
                    }
                  }}
                >
                  {isBootstrapDataLoading ? (
                    <Grid item xs={12}>
                      <PosCategoryCardsSkeleton count={4} />
                    </Grid>
                  ) : (
                    <>
                  {parentCategories.map((parentCategory) => {
                    const childCategories = childCategoriesByParent.get(parentCategory.id) || [];
                    return (
                      <Grid item xs={12} md={6} key={parentCategory.id}>
                        <Paper
                          variant="outlined"
                          draggable={!isSavingCategoryOrder}
                          onDragStart={() => handleParentDragStart(parentCategory.id)}
                          onDragOver={(event) => {
                            if (categoryDragState.draggingType !== 'parent') return;
                            event.preventDefault();
                            setCategoryDragState((prev) => ({
                              ...prev,
                              overKey: `parent-${parentCategory.id}`,
                            }));
                          }}
                          onDrop={async (event) => {
                            if (categoryDragState.draggingType !== 'parent') return;
                            event.preventDefault();
                            await handleParentDrop(parentCategory.id);
                          }}
                          onDragEnd={() =>
                            setCategoryDragState({
                              draggingId: null,
                              draggingType: null,
                              sourceParentId: null,
                              overKey: null,
                            })
                          }
                          sx={{
                            ...getMiniCardSx(isDark, '#4ce1b6'),
                            p: 1.1,
                            cursor: isSavingCategoryOrder ? 'default' : 'grab',
                            borderColor:
                              categoryDragState.overKey === `parent-${parentCategory.id}`
                                ? alpha('#4ce1b6', 0.6)
                                : undefined,
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={0.4} alignItems="center">
                                  <DragIndicatorIcon sx={{ fontSize: 16, color: isDark ? '#94a1b4' : '#60748d' }} />
                                  <Typography sx={{ fontWeight: 800, opacity: parentCategory.is_active ? 1 : 0.7 }}>
                                    {parentCategory.name}
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" sx={{ color: isDark ? '#aeb8c6' : '#5a6d84' }}>
                                  Parent category
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.2} alignItems="center" sx={{ flexShrink: 0 }}>
                                <Switch
                                  size="small"
                                  checked={Boolean(parentCategory.is_active)}
                                  onChange={(event) =>
                                    handleToggleCategoryActive(parentCategory.id, event.target.checked)
                                  }
                                />
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setCategoryDeleteDialog({
                                      open: true,
                                      categoryId: parentCategory.id,
                                      categoryName: parentCategory.name || '',
                                    })
                                  }
                                  sx={{ color: isDark ? '#f68b8b' : '#d45b5b' }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>

                            <Box
                              sx={{
                                borderTop: '1px dashed',
                                borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
                                pt: 0.8,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ mb: 0.6 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: isDark ? '#aeb8c6' : '#5a6d84' }}
                                >
                                  Child categories
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                  onClick={() =>
                                    handleOpenMenuDrawer(MENU_SECTION_VALUES.CATEGORIES, {
                                      parentCategory,
                                    })
                                  }
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 1.2,
                                    minHeight: 28,
                                    px: 1.1,
                                    borderColor: isDark ? 'rgba(76,225,182,0.52)' : 'rgba(15,118,110,0.34)',
                                    color: isDark ? '#b8fff0' : '#0f766e',
                                    bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.14)',
                                    '&:hover': {
                                      borderColor: isDark ? 'rgba(76,225,182,0.72)' : 'rgba(15,118,110,0.5)',
                                      bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(76,225,182,0.2)',
                                    },
                                  }}
                                >
                                  Add Sub Category
                                </Button>
                              </Stack>
                              <Stack spacing={0.55}>
                                {childCategories.map((childCategory) => (
                                  <Stack
                                    key={childCategory.id}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    draggable={!isSavingCategoryOrder}
                                    onDragStart={(event) => {
                                      event.stopPropagation();
                                      handleChildDragStart(childCategory.id, parentCategory.id);
                                    }}
                                    onDragOver={(event) => {
                                      if (categoryDragState.draggingType !== 'child') return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setCategoryDragState((prev) => ({
                                        ...prev,
                                        overKey: `child-${childCategory.id}`,
                                      }));
                                    }}
                                    onDrop={async (event) => {
                                      if (categoryDragState.draggingType !== 'child') return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      await handleChildDrop(parentCategory.id, childCategory.id);
                                    }}
                                    onDragEnd={() =>
                                      setCategoryDragState({
                                        draggingId: null,
                                        draggingType: null,
                                        sourceParentId: null,
                                        overKey: null,
                                      })
                                    }
                                    sx={{
                                      border: '1px solid',
                                      borderColor: isDark ? 'rgba(76,225,182,0.22)' : 'rgba(76,225,182,0.25)',
                                      borderRadius: 1.2,
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.7)',
                                      cursor: isSavingCategoryOrder ? 'default' : 'grab',
                                      boxShadow:
                                        categoryDragState.overKey === `child-${childCategory.id}`
                                          ? `inset 0 0 0 1px ${alpha('#4ce1b6', 0.5)}`
                                          : 'none',
                                    }}
                                  >
                                    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ minWidth: 0 }}>
                                      <DragIndicatorIcon sx={{ fontSize: 14, color: isDark ? '#8f9db0' : '#60748d' }} />
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700, opacity: childCategory.is_active ? 1 : 0.65 }}
                                      >
                                        {childCategory.name}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={0.2} alignItems="center" sx={{ flexShrink: 0 }}>
                                      <Switch
                                        size="small"
                                        checked={Boolean(childCategory.is_active)}
                                        onChange={(event) =>
                                          handleToggleCategoryActive(childCategory.id, event.target.checked)
                                        }
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setCategoryDeleteDialog({
                                            open: true,
                                            categoryId: childCategory.id,
                                            categoryName: childCategory.name || '',
                                          })
                                        }
                                        sx={{ color: isDark ? '#f68b8b' : '#d45b5b' }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </Stack>
                                ))}
                                {childCategories.length === 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: isDark ? '#8f9db0' : '#61758f', fontStyle: 'italic' }}
                                  >
                                    No child categories yet. Use "Add Sub Category" to create one.
                                  </Typography>
                                )}
                              </Stack>
                              <Box
                                onDragOver={(event) => {
                                  if (categoryDragState.draggingType !== 'child') return;
                                  event.preventDefault();
                                  setCategoryDragState((prev) => ({
                                    ...prev,
                                    overKey: `child-end-${parentCategory.id}`,
                                  }));
                                }}
                                onDrop={async (event) => {
                                  if (categoryDragState.draggingType !== 'child') return;
                                  event.preventDefault();
                                  await handleChildDrop(parentCategory.id, null);
                                }}
                                sx={{
                                  mt: 0.45,
                                  height: 10,
                                  borderRadius: 1,
                                  backgroundColor:
                                    categoryDragState.overKey === `child-end-${parentCategory.id}`
                                      ? alpha('#4ce1b6', 0.22)
                                      : 'transparent',
                                }}
                              />
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
                  {parentCategories.length === 0 && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={getMiniCardSx(isDark, '#4ce1b6')}>
                        <Typography variant="body2" sx={{ color: isDark ? '#b2becf' : '#546176' }}>
                          No parent categories yet. Use "Add Category" to create a parent category first.
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                    </>
                  )}
                </Grid>
              </>
            )}

            {menuSectionTab === MENU_SECTION_VALUES.VARIATIONS && (
              <>
                <ConfigCardHeader
                  icon={TuneIcon}
                  title="Variation List"
                  subtitle="Manage variation labels and departments"
                  isDark={isDark}
                />
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Online Display Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Department Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Modified</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isBootstrapDataLoading ? (
                        <PosVariationTableSkeleton rows={5} />
                      ) : (
                        <>
                      {data.menu_variations.map((variation) => (
                        <TableRow key={variation.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{variation.name}</TableCell>
                          <TableCell>{variation.online_display_name || variation.name}</TableCell>
                          <TableCell>
                            {variation.department_label ||
                              VARIATION_DEPARTMENT_OPTIONS.find((opt) => opt.value === variation.department_name)
                                ?.label ||
                              '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={variation.is_active ? 'success' : 'default'}
                              label={variation.is_active ? 'Active' : 'Inactive'}
                            />
                          </TableCell>
                          <TableCell>{formatShortDate(variation.created_at)}</TableCell>
                          <TableCell>{formatShortDate(variation.updated_at)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.4} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenMenuDrawer(MENU_SECTION_VALUES.VARIATIONS, {
                                    variation,
                                  })
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ color: isDark ? '#f68b8b' : '#d45b5b' }}
                                onClick={() =>
                                  setVariationDeleteDialog({
                                    open: true,
                                    variationId: variation.id,
                                    variationName: variation.name || '',
                                  })
                                }
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                      {data.menu_variations.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" sx={{ py: 1.2, color: isDark ? '#b2becf' : '#546176' }}>
                              No variations yet. Click "Add Variation" to create one.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

          </Box>
        </Paper>
      )}

      <Drawer
        anchor="right"
        open={menuDrawer.open}
        onClose={handleCloseMenuDrawer}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: { xs: '100%', sm: 560 },
          }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Stack direction="row" spacing={1.1} alignItems="center">
              <ActiveMenuDrawerIcon sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: posThemeColors.text }}>
                  {menuDrawerMeta.title}
                </Typography>
                <Typography variant="caption" sx={{ color: posThemeColors.subtleText }}>
                  {menuDrawerMeta.subtitle}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleCloseMenuDrawer} disabled={menuDrawerSubmitting}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {activeMenuDrawerType === MENU_SECTION_VALUES.ITEMS && (
              <Grid container spacing={1.2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Name"
                    value={menuItemForm.name}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Short Code"
                    value={menuItemForm.short_code}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, short_code: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Category (Parent / Child)"
                    value={menuItemForm.category}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, category: e.target.value }))}
                  >
                    <MenuItem value="">Select Sub Category</MenuItem>
                    {selectableMenuCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {categoryLabelMap.get(category.id) || category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Price"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={menuItemForm.price}
                    disabled={Boolean(menuItemForm.enable_variation)}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, price: e.target.value }))}
                    helperText={menuItemForm.enable_variation ? 'Base price will remain 0 when variation is enabled.' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Dietary"
                    value={menuItemForm.dietary}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, dietary: e.target.value }))}
                  >
                    {MENU_DIETARY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="GST Type"
                    value={menuItemForm.gst_type}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, gst_type: e.target.value }))}
                  >
                    {MENU_GST_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    sx={{ height: '100%', m: 0, pl: 0.5 }}
                    control={
                      <Switch
                        checked={Boolean(menuItemForm.enable_variation)}
                        onChange={(e) => {
                          const nextEnabled = e.target.checked;
                          setMenuItemForm((prev) => ({
                            ...prev,
                            enable_variation: nextEnabled,
                            price: nextEnabled ? '0' : prev.price,
                          }));
                          if (!nextEnabled) {
                            setMenuItemVariationRows([{ variation_id: '', price: '' }]);
                          }
                        }}
                      />
                    }
                    label="Enable Variation"
                  />
                </Grid>
                {menuItemForm.enable_variation && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.8 }}>
                      Variations
                    </Typography>
                    <Stack spacing={1}>
                      {menuItemVariationRows.map((row, index) => (
                        <Stack key={`menu-item-variation-row-${index}`} direction="row" spacing={1}>
                          <TextField
                            fullWidth
                            select
                            label="Select Variation"
                            value={row.variation_id}
                            onChange={(e) =>
                              handleChangeMenuItemVariationRow(index, 'variation_id', e.target.value)
                            }
                          >
                            <MenuItem value="">Select Variation</MenuItem>
                            {data.menu_variations.map((variation) => (
                              <MenuItem key={variation.id} value={variation.id}>
                                {variation.online_display_name || variation.name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            sx={{ minWidth: 150 }}
                            label="Variation Price"
                            type="number"
                            disabled={!row.variation_id}
                            inputProps={{ min: 0, step: 0.01 }}
                            value={row.price}
                            onChange={(e) => handleChangeMenuItemVariationRow(index, 'price', e.target.value)}
                          />
                          <IconButton
                            aria-label="remove variation"
                            onClick={() => handleRemoveMenuItemVariationRow(index)}
                            sx={{ alignSelf: 'center' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddMenuItemVariationRow}
                          disabled={!menuItemVariationRows[menuItemVariationRows.length - 1]?.variation_id}
                          sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                          Add Variation
                        </Button>
                      </Box>
                    </Stack>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.3 }}>
                    Order Type
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(menuItemForm.order_room_service)}
                          onChange={(e) =>
                            setMenuItemForm((prev) => ({ ...prev, order_room_service: e.target.checked }))
                          }
                          sx={{ py: 0.2 }}
                        />
                      }
                      label="Room Service"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(menuItemForm.order_pick_up)}
                          onChange={(e) =>
                            setMenuItemForm((prev) => ({ ...prev, order_pick_up: e.target.checked }))
                          }
                          sx={{ py: 0.2 }}
                        />
                      }
                      label="Pick Up / Delivery"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(menuItemForm.order_dine_in)}
                          onChange={(e) =>
                            setMenuItemForm((prev) => ({ ...prev, order_dine_in: e.target.checked }))
                          }
                          sx={{ py: 0.2 }}
                        />
                      }
                      label="Dine In"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(menuItemForm.is_available)}
                          onChange={(e) =>
                            setMenuItemForm((prev) => ({ ...prev, is_available: e.target.checked }))
                          }
                        />
                      }
                      label="Enable Menu Item"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={menuItemForm.description}
                    onChange={(e) => setMenuItemForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ sm: 'center' }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<AddPhotoAlternateIcon />}
                      sx={{ textTransform: 'none', fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                      Add Menu Image
                      <input hidden accept="image/*" type="file" onChange={handleMenuItemImageChange} />
                    </Button>
                    {menuItemImageFile && (
                      <Typography variant="caption" color="text.secondary">
                        {menuItemImageFile.name}
                      </Typography>
                    )}
                  </Stack>
                  {menuItemImagePreview && (
                    <Box
                      component="img"
                      src={menuItemImagePreview}
                      alt="Menu item preview"
                      sx={{
                        mt: 1,
                        width: 108,
                        height: 108,
                        borderRadius: 2,
                        objectFit: 'cover',
                        border: '1px solid',
                        borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            )}

            {activeMenuDrawerType === MENU_SECTION_VALUES.CATEGORIES && (
              <Grid container spacing={1.2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  {categoryDrawerContext.mode === 'child' ? (
                    <TextField
                      fullWidth
                      label="Parent Category"
                      value={categoryDrawerContext.parentName || ''}
                      disabled
                    />
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      This will create a parent category. Use the + button on a parent card to add child categories.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            )}

            {activeMenuDrawerType === MENU_SECTION_VALUES.VARIATIONS && (
              <Grid container spacing={1.2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Variation Name"
                    value={variationForm.name}
                    onChange={(e) => setVariationForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Online Display Name"
                    value={variationForm.online_display_name}
                    onChange={(e) =>
                      setVariationForm((prev) => ({ ...prev, online_display_name: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Department Name"
                    value={variationForm.department_name}
                    onChange={(e) =>
                      setVariationForm((prev) => ({ ...prev, department_name: e.target.value }))
                    }
                  >
                    <MenuItem value="">Select</MenuItem>
                    {VARIATION_DEPARTMENT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(variationForm.is_active)}
                        onChange={(e) =>
                          setVariationForm((prev) => ({ ...prev, is_active: e.target.checked }))
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          <Box
            sx={{
              p: 1.5,
              ...getBookingDrawerFooterSx(isDark),
            }}
          >
            <Grid container spacing={1.2}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={menuDrawerSubmitting}
                  onClick={handleCloseMenuDrawer}
                  sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), textTransform: 'none' }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={menuDrawerSubmitting}
                  onClick={handleSaveMenuDrawer}
                  sx={getBookingDrawerPrimaryButtonSx(isDark)}
                >
                  {menuDrawerSubmitting ? 'Saving...' : menuDrawerMeta.actionLabel}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
      <Drawer
        anchor="right"
        open={orderViewDialog.open}
        onClose={closeOrderViewPanel}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, BOOKING_DRAWER_WIDTH_SX),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              px: 1.5,
              pt: 1.5,
              pb: 1.85,
              ...getBookingDrawerHeaderSx(isDark),
              background: isDark
                ? `linear-gradient(140deg, ${alpha(ACCENT_COLOR, 0.12)} 0%, ${posThemeColors.headerBg} 100%)`
                : `linear-gradient(140deg, ${alpha(ACCENT_COLOR, 0.12)} 0%, #ffffff 100%)`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 33,
                    height: 33,
                    borderRadius: 1.6,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: isDark ? alpha('#4ce1b6', 0.2) : alpha('#4ce1b6', 0.16),
                    border: `1px solid ${alpha('#4ce1b6', 0.4)}`,
                  }}
                >
                  <ReceiptLongIcon sx={{ color: '#4ce1b6', fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {orderDrawerMode === 'edit' ? 'Modify Order' : 'Order Details'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {orderDrawerMode === 'edit'
                      ? 'Use the place-order style drawer to modify this order.'
                      : 'Restaurant order view with SLA and serving insights'}
                  </Typography>
                </Box>
              </Stack>
              {canViewPosAudit ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setOrderAuditDrawerOpen(true)}
                  disabled={!orderViewDialog.order?.id}
                  sx={{
                    ...getBookingDrawerAuditButtonSx(isDark),
                    minWidth: 120,
                    px: 1.5,
                    py: 0.3,
                  }}
                >
                  Audit
                </Button>
              ) : null}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {orderViewLoading ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={30} />
              </Box>
            ) : orderViewDialog.order ? (
              (() => {
                const order = orderViewDialog.order;
                const statusMeta = getOrderStatusMeta(order.status);
                const timing = getOrderTimingInsight(order);
                const itemRows = getOrderItemRows(order);
                const financial = getOrderFinancialSummary(order);
                const rawTaxBreakdown = order?.tax_breakdown;
                const taxBreakdown = Array.isArray(rawTaxBreakdown)
                  ? rawTaxBreakdown
                  : (typeof rawTaxBreakdown === 'string'
                    ? (() => {
                        try {
                          const parsed = JSON.parse(rawTaxBreakdown);
                          return Array.isArray(parsed) ? parsed : [];
                        } catch {
                          return [];
                        }
                      })()
                    : []);
                const paymentHistoryRows = (Array.isArray(order?.payments) ? order.payments : [])
                  .slice()
                  .sort((first, second) => {
                    const firstTs = new Date(
                      first?.collected_at || first?.created_at || first?.updated_at || 0
                    ).getTime();
                    const secondTs = new Date(
                      second?.collected_at || second?.created_at || second?.updated_at || 0
                    ).getTime();
                    return (Number.isFinite(secondTs) ? secondTs : 0) - (Number.isFinite(firstTs) ? firstTs : 0);
                  });
                const orderNote = String(order?.notes || '').trim();
                const isEditMode = orderDrawerMode === 'edit';
                const menuItemOptions = (data.menu_items || []).filter((item) => item.is_available !== false);
                const menuItemNameById = new Map(menuItemOptions.map((item) => [Number(item.id), item.name || `Item #${item.id}`]));
                const menuItemPriceById = new Map(menuItemOptions.map((item) => [Number(item.id), Number(item.price || 0)]));
                const editableItems = Array.isArray(orderEditForm?.order_items) ? orderEditForm.order_items : [];
                const editableSubtotal = editableItems.reduce((sum, row) => {
                  const price = Number(menuItemPriceById.get(Number(row.menu_item || 0)) || 0);
                  const quantity = Number(row?.quantity || 0);
                  return sum + (price * quantity);
                }, 0);

                return (
                  <Stack spacing={1.5}>
                    <OrderDrawerSection
                      icon={AccessTimeIcon}
                      title="SLA & Serve Insights"
                      isDark={isDark}
                      sx={{
                        borderColor: alpha(timing.criticalityColor, 0.32),
                        background: isDark
                          ? `linear-gradient(145deg, ${alpha(timing.criticalityColor, 0.14)} 0%, rgba(24,24,31,0.95) 100%)`
                          : `linear-gradient(145deg, ${alpha(timing.criticalityColor, 0.1)} 0%, #ffffff 100%)`,
                      }}
                    >
                      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        <Chip label={`#${formatOrderNumber(order.order_number)}`} sx={{ fontWeight: 800 }} />
                        <Chip label={getOrderTypeLabel(order.order_type)} sx={{ fontWeight: 700 }} />
                        <Chip
                          label={statusMeta.label}
                          sx={{ bgcolor: `${statusMeta.color}22`, color: statusMeta.color, fontWeight: 700 }}
                        />
                        <Chip
                          label={timing.criticalityLabel}
                          sx={{
                            bgcolor: alpha(timing.criticalityColor, 0.18),
                            color: timing.criticalityColor,
                            fontWeight: 700,
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
                        <OrderDrawerMetricPill label="SLA Target" value={timing.targetLabel} color={timing.criticalityColor} isDark={isDark} />
                        <OrderDrawerMetricPill label={timing.serveLabel} value={timing.actualLabel} color={timing.criticalityColor} isDark={isDark} />
                        <OrderDrawerMetricPill
                          label="Delay"
                          value={timing.delayMinutes > 0 ? timing.delayLabel : 'No Delay'}
                          color={timing.delayMinutes > 0 ? timing.criticalityColor : '#16a34a'}
                          isDark={isDark}
                        />
                      </Stack>
                      <Box sx={{ position: 'relative' }}>
                        <LinearProgress
                          variant="determinate"
                          value={100}
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            bgcolor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.1)',
                            '& .MuiLinearProgress-bar': { display: 'none' },
                          }}
                        />
                        <LinearProgress
                          variant="determinate"
                          value={timing.progressPercent}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 8,
                            borderRadius: 999,
                            bgcolor: 'transparent',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 999,
                              backgroundColor: timing.criticalityColor,
                            },
                          }}
                        />
                      </Box>
                    </OrderDrawerSection>

                    {isEditMode ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          borderColor: isDark ? 'rgba(76,225,182,0.32)' : 'rgba(15,118,110,0.22)',
                          background: isDark
                            ? 'linear-gradient(165deg, rgba(22,25,32,0.98) 0%, rgba(16,18,24,0.98) 100%)'
                            : 'linear-gradient(165deg, #ffffff 0%, #f4fbff 100%)',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.85 }}>
                          Modify Order (Place Order Design)
                        </Typography>
                        <Grid container spacing={1.05}>
                          <Grid item xs={12} sm={4}>
                            <FormSelect
                              fullWidth
                              size="small"
                              label="Order Type"
                              value={orderEditForm?.order_type || 'dine_in'}
                              onChange={(event) => handleChangeOrderEditField('order_type', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            >
                              <MenuItem value="dine_in">Dine In</MenuItem>
                              <MenuItem value="room_service">Room Service</MenuItem>
                              <MenuItem value="takeaway">Pick Up / Delivery</MenuItem>
                            </FormSelect>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormSelect
                              fullWidth
                              size="small"
                              label="Status"
                              value={orderEditForm?.status || 'placed'}
                              onChange={(event) => handleChangeOrderEditField('status', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            >
                              <MenuItem value="draft">Draft</MenuItem>
                              <MenuItem value="placed">Placed</MenuItem>
                              <MenuItem value="preparing">Preparing</MenuItem>
                              <MenuItem value="served">Served</MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </FormSelect>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Prep Time (min)"
                              type="number"
                              inputProps={{ min: 5, max: 240 }}
                              value={orderEditForm?.preparation_time_minutes || 30}
                              onChange={(event) =>
                                handleChangeOrderEditField(
                                  'preparation_time_minutes',
                                  Math.max(5, Number(event.target.value || 5))
                                )}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Customer Name"
                              value={orderEditForm?.customer_name || ''}
                              onChange={(event) => handleChangeOrderEditField('customer_name', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Country Code"
                              value={orderEditForm?.customer_phone_country_code || '+91'}
                              onChange={(event) =>
                                handleChangeOrderEditField('customer_phone_country_code', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Phone"
                              value={orderEditForm?.customer_phone || ''}
                              onChange={(event) => handleChangeOrderEditField('customer_phone', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormSelect
                              fullWidth
                              size="small"
                              label="Payment Collection"
                              value={orderEditForm?.payment_collection_mode || 'collect_now'}
                              onChange={(event) => handleChangeOrderEditField('payment_collection_mode', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            >
                              <MenuItem value="collect_now">Collect Now</MenuItem>
                              <MenuItem value="due">Due</MenuItem>
                            </FormSelect>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Payment Method"
                              value={orderEditForm?.payment_method || ''}
                              disabled={(orderEditForm?.payment_collection_mode || 'collect_now') === 'due'}
                              onChange={(event) => handleChangeOrderEditField('payment_method', event.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.25,
                                  '& fieldset': {
                                    borderColor: isDark ? 'rgba(76,225,182,0.58)' : 'rgba(15,118,110,0.42)',
                                  },
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Divider sx={{ my: 1.15, borderColor: isDark ? 'rgba(76,225,182,0.22)' : 'rgba(15,118,110,0.16)' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.7 }}>
                          Order Items
                        </Typography>
                        <Stack spacing={0.75}>
                          {editableItems.length === 0 ? (
                            <Alert severity="warning" sx={{ py: 0.2 }}>
                              Add at least one item to continue.
                            </Alert>
                          ) : editableItems.map((row, index) => (
                            <Paper
                              key={`edit-item-${index}`}
                              variant="outlined"
                              sx={{
                                p: 0.95,
                                borderRadius: 1.45,
                                borderColor: isDark ? 'rgba(76,225,182,0.28)' : 'rgba(15,118,110,0.2)',
                                backgroundColor: isDark ? 'rgba(17,20,27,0.78)' : '#ffffff',
                              }}
                            >
                              <Grid container spacing={0.85} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {menuItemNameById.get(Number(row?.menu_item || 0)) || row?.item_name || 'Item'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Qty"
                                    inputProps={{ min: 1 }}
                                    value={row?.quantity || 1}
                                    onChange={(event) =>
                                      handleChangeOrderEditItem(index, { quantity: Math.max(1, Number(event.target.value || 1)) })}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Special Note"
                                    value={row?.special_note || ''}
                                    onChange={(event) =>
                                      handleChangeOrderEditItem(index, { special_note: event.target.value })}
                                  />
                                </Grid>
                                <Grid item xs={6} sm={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveOrderEditItem(index)}
                                    sx={{
                                      border: '1px solid',
                                      borderColor: isDark ? 'rgba(239,68,68,0.45)' : 'rgba(220,38,38,0.35)',
                                      color: isDark ? '#fca5a5' : '#dc2626',
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Stack>
                        <Paper
                          variant="outlined"
                          sx={{
                            mt: 0.75,
                            p: 0.85,
                            borderRadius: 1.45,
                            borderStyle: 'dashed',
                            borderColor: isDark ? 'rgba(76,225,182,0.34)' : 'rgba(15,118,110,0.25)',
                            backgroundColor: isDark ? 'rgba(76,225,182,0.06)' : 'rgba(15,118,110,0.05)',
                          }}
                        >
                          <Grid container spacing={0.85} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <FormSelect
                                fullWidth
                                size="small"
                                label="Add Item"
                                value={orderEditDraftItem.menu_item}
                                onChange={(event) =>
                                  setOrderEditDraftItem((prev) => ({ ...prev, menu_item: event.target.value }))}
                              >
                                <MenuItem value="">Select Item</MenuItem>
                                {menuItemOptions.map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </FormSelect>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Qty"
                                inputProps={{ min: 1 }}
                                value={orderEditDraftItem.quantity}
                                onChange={(event) =>
                                  setOrderEditDraftItem((prev) => ({
                                    ...prev,
                                    quantity: Math.max(1, Number(event.target.value || 1)),
                                  }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Note"
                                value={orderEditDraftItem.special_note}
                                onChange={(event) =>
                                  setOrderEditDraftItem((prev) => ({ ...prev, special_note: event.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={6} sm={1}>
                              <IconButton
                                size="small"
                                onClick={handleAddOrderEditItem}
                                sx={{
                                  border: '1px solid',
                                  borderColor: isDark ? 'rgba(76,225,182,0.6)' : 'rgba(15,118,110,0.4)',
                                  color: isDark ? '#9cead6' : '#0f766e',
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Paper>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          sx={{ mt: 1.05 }}
                          label="Order Note"
                          value={orderEditForm?.notes || ''}
                          onChange={(event) => handleChangeOrderEditField('notes', event.target.value)}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.65,
                            color: isDark ? '#9bd7c6' : '#0f766e',
                            fontWeight: 700,
                          }}
                        >
                          Approx item subtotal: {formatCurrency(editableSubtotal)}
                        </Typography>
                      </Paper>
                    ) : null}

                    <OrderDrawerSection icon={ReceiptLongIcon} title="Order Information" isDark={isDark}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <OrderDrawerInfoTile label="Source" value={getOrderSourceLabel(order)} icon={PlaceOutlinedIcon} isDark={isDark} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <OrderDrawerInfoTile label="Customer" value={order.customer_name || order.guest_name || 'Walk-In'} icon={PersonOutlineIcon} isDark={isDark} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <OrderDrawerInfoTile label="Created At" value={formatDateTime(order.created_at)} icon={ScheduleIcon} isDark={isDark} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <OrderDrawerInfoTile label="Updated At" value={formatDateTime(order.updated_at)} icon={ScheduleIcon} isDark={isDark} />
                        </Grid>
                      </Grid>
                    </OrderDrawerSection>

                    <OrderDrawerSection icon={PaymentsIcon} title="Payment History" isDark={isDark}>
                      {paymentHistoryRows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No payment history available for this order.
                        </Typography>
                      ) : (
                        <Stack spacing={0.85}>
                          {paymentHistoryRows.map((paymentEntry, index) => {
                            const paymentStatusMeta = getPaymentHistoryEntryStatusMeta(paymentEntry?.status, isDark);
                            const paymentMethod = toTitleCaseWords(paymentEntry?.payment_method || 'NA');
                            const paymentAmount = Number(paymentEntry?.amount || 0);
                            const sourceLabel = String(paymentEntry?.source_label || '').trim() || 'Walk-In';
                            const noteText = String(paymentEntry?.note || '').trim();
                            return (
                              <Box
                                key={paymentEntry?.id || `${order.id || 'order'}-payment-${index}`}
                                sx={getBookingDrawerPanelSx(isDark, {
                                  p: 1.05,
                                  borderLeft: `3px solid ${paymentStatusMeta.color}`,
                                })}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                  <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center">
                                    <Chip size="small" label={paymentMethod} sx={{ fontWeight: 700 }} />
                                    <Chip
                                      size="small"
                                      label={paymentStatusMeta.label}
                                      sx={{
                                        fontWeight: 700,
                                        color: paymentStatusMeta.color,
                                        bgcolor: paymentStatusMeta.bgcolor,
                                        border: '1px solid',
                                        borderColor: paymentStatusMeta.borderColor,
                                      }}
                                    />
                                  </Stack>
                                  <Typography variant="body1" sx={{ fontWeight: 900, color: POS_ACCENT, whiteSpace: 'nowrap' }}>
                                    {formatCurrency(paymentAmount)}
                                  </Typography>
                                </Stack>
                                <Stack spacing={0.25} sx={{ mt: 0.55 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Collected At: {formatDateTime(paymentEntry?.collected_at || paymentEntry?.created_at)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Source: {sourceLabel}
                                  </Typography>
                                  {noteText ? (
                                    <Typography variant="caption" color="text.secondary">
                                      Note: {noteText}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </OrderDrawerSection>

                    <OrderDrawerSection icon={RestaurantMenuIcon} title="Items & Prices" isDark={isDark}>
                      {itemRows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No item details available.</Typography>
                      ) : (
                        <Stack spacing={0.85}>
                          {itemRows.map((item, index) => {
                            const quantity = Number(item?.quantity || 0);
                            const unitPrice = Number(item?.unit_price || item?.price || 0);
                            const lineTotal = Number(item?.line_total || (unitPrice * quantity));
                            return (
                              <Box
                                key={`${order.id || 'order'}-item-${index}`}
                                sx={getBookingDrawerPanelSx(isDark, { p: 1.05 })}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                  <Stack direction="row" spacing={0.85} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                    <Chip
                                      size="small"
                                      label={`×${quantity}`}
                                      sx={{
                                        height: 22,
                                        fontWeight: 800,
                                        bgcolor: isDark ? alpha(POS_ACCENT, 0.18) : alpha(POS_ACCENT, 0.12),
                                        color: POS_ACCENT,
                                      }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                                        {item?.item_name || item?.menu_item_name || 'Item'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Rate: {formatCurrency(unitPrice)}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                  <Typography variant="body2" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                                    {formatCurrency(lineTotal)}
                                  </Typography>
                                </Stack>
                                {item?.special_note ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45, pl: 0.25 }}>
                                    Note: {item.special_note}
                                  </Typography>
                                ) : null}
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </OrderDrawerSection>

                    <Paper elevation={0} sx={getPosOrderDrawerSummaryPaperSx(isDark, { p: 1.35 })}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.05 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: isDark ? alpha(POS_ACCENT, 0.16) : alpha(POS_ACCENT, 0.1),
                            border: `1px solid ${alpha(POS_ACCENT, isDark ? 0.32 : 0.22)}`,
                          }}
                        >
                          <ReceiptLongIcon sx={{ fontSize: 17, color: POS_ACCENT }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          Billing Summary
                        </Typography>
                      </Stack>
                      <Stack spacing={0.55}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(financial.subtotal)}</Typography>
                        </Stack>
                        {financial.discountAmount > 0 ? (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Discount {financial.discountType === 'percentage' ? `(${financial.discountValue}%)` : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#ef4444' }}>
                              - {formatCurrency(financial.discountAmount)}
                            </Typography>
                          </Stack>
                        ) : null}
                        {taxBreakdown.length > 0 ? (
                          taxBreakdown.map((taxRow, index) => {
                            const ratePct = parsePosTaxBreakdownPercentage(taxRow);
                            const rateLabel = ratePct > 0 ? ` (${ratePct.toFixed(2)}%)` : '';
                            return (
                              <Stack key={`tax-${index}`} direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                  {taxRow?.name || taxRow?.tax_name || `Tax ${index + 1}`}
                                  {rateLabel}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(Number(taxRow?.amount || 0))}
                                </Typography>
                              </Stack>
                            );
                          })
                        ) : (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Tax</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatCurrency(financial.taxAmount)}
                            </Typography>
                          </Stack>
                        )}
                        {financial.deliveryCharge > 0 ? (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Delivery Charge</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatCurrency(financial.deliveryCharge)}
                            </Typography>
                          </Stack>
                        ) : null}
                        {financial.containerCharge > 0 ? (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Container Charge</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatCurrency(financial.containerCharge)}
                            </Typography>
                          </Stack>
                        ) : null}
                        <Divider sx={{ my: 0.35 }} />
                        <Box
                          sx={{
                            mt: 0.15,
                            p: 1.05,
                            borderRadius: 1.6,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: isDark
                              ? `linear-gradient(135deg, ${alpha(POS_ACCENT, 0.22)} 0%, ${alpha(POS_ACCENT, 0.08)} 100%)`
                              : `linear-gradient(135deg, ${alpha(POS_ACCENT, 0.16)} 0%, ${alpha(POS_ACCENT, 0.06)} 100%)`,
                            border: `1px solid ${alpha(POS_ACCENT, isDark ? 0.34 : 0.24)}`,
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 800 }}>Total</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900, color: POS_ACCENT, lineHeight: 1.1 }}>
                            {formatCurrency(financial.total)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>

                    {orderNote ? (
                      <OrderDrawerSection icon={StickyNote2OutlinedIcon} title="Order Note" isDark={isDark}>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                          {orderNote}
                        </Typography>
                      </OrderDrawerSection>
                    ) : null}
                  </Stack>
                );
              })()
            ) : null}
          </Box>

          <Box
            sx={{
              p: 1.5,
              ...getBookingDrawerFooterSx(isDark),
            }}
          >
            <Grid container spacing={1.2}>
              <Grid item xs={orderDrawerMode === 'edit' ? 6 : 4}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    if (orderDrawerMode === 'edit') {
                      setOrderDrawerMode('view');
                      setOrderEditForm(buildOrderEditFormFromOrder(orderViewDialog.order || {}));
                      return;
                    }
                    closeOrderViewPanel();
                  }}
                  sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), textTransform: 'none', fontWeight: 700 }}
                >
                  {orderDrawerMode === 'edit' ? 'Cancel Edit' : 'Close'}
                </Button>
              </Grid>
              {orderDrawerMode !== 'edit' ? (
                <Grid item xs={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ReceiptLongIcon sx={{ fontSize: 18 }} />}
                    disabled={!orderViewDialog.order?.id}
                    onClick={() => openThermalInvoicePreview(orderViewDialog.order)}
                    sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), textTransform: 'none', fontWeight: 700 }}
                  >
                    Invoice
                  </Button>
                </Grid>
              ) : null}
              <Grid item xs={orderDrawerMode === 'edit' ? 6 : 4}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!orderViewDialog.order?.id || orderEditSaving}
                  onClick={() => {
                    if (orderDrawerMode === 'edit') {
                      handleSaveOrderEdits();
                    } else {
                      const selectedOrder = orderViewDialog.order;
                      closeOrderViewPanel();
                      openPlaceOrderEditor(selectedOrder);
                    }
                  }}
                  sx={getBookingDrawerPrimaryButtonSx(isDark)}
                >
                  {orderDrawerMode === 'edit' ? (orderEditSaving ? 'Saving...' : 'Save Changes') : 'Modify Order'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
      <Dialog
        open={thermalInvoicePreview.open}
        onClose={closeThermalInvoicePreview}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#12151c' : '#f8fafc',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
          Invoice Preview
          <IconButton
            aria-label="Close invoice preview"
            onClick={closeThermalInvoicePreview}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: isDark ? '#0b0d12' : '#eef2f7' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Same thermal invoice layout used during billing. Print uses the existing web/desktop print flow.
          </Typography>
          <Box
            sx={{
              mx: 'auto',
              width: '100%',
              maxWidth: 360,
              minHeight: 420,
              bgcolor: '#fff',
              borderRadius: 1,
              border: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
              overflow: 'hidden',
            }}
          >
            {thermalInvoicePreview.html ? (
              <Box
                component="iframe"
                title="Restaurant invoice preview"
                srcDoc={thermalInvoicePreview.html}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: 520,
                  border: 0,
                  bgcolor: '#fff',
                }}
              />
            ) : (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography color="text.secondary">No invoice content</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={closeThermalInvoicePreview}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleThermalInvoiceDownload}
            disabled={!thermalInvoicePreview.html}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintOutlinedIcon />}
            onClick={handleThermalInvoicePrint}
            disabled={!thermalInvoicePreview.html || thermalInvoicePreview.printing}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#4ce1b6',
              color: '#06110e',
              '&:hover': { bgcolor: '#3bc9a0' },
            }}
          >
            {thermalInvoicePreview.printing ? 'Printing...' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>
      <PosOrderPaymentDrawer
        open={orderPaymentDrawer.open}
        onClose={closeOrderPaymentDrawer}
        order={orderPaymentDrawer.order}
        isDark={isDark}
        onPaymentsChanged={handleOrderPaymentsChanged}
      />
      {canViewPosAudit ? (
      <Drawer
        anchor="right"
        open={orderAuditDrawerOpen}
        onClose={() => setOrderAuditDrawerOpen(false)}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, BOOKING_DRAWER_WIDTH_SX),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FactCheckIcon sx={{ color: ACCENT_COLOR, fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: posThemeColors.text }}>
                Order Audit
              </Typography>
              {orderViewDialog.order ? (
                <Chip
                  size="small"
                  label={formatOrderNumber(orderViewDialog.order.order_number)}
                  sx={{
                    fontWeight: 700,
                    bgcolor: isDark ? alpha('#4ce1b6', 0.18) : alpha('#4ce1b6', 0.12),
                    color: isDark ? '#c7fff0' : '#0f766e',
                  }}
                />
              ) : null}
            </Stack>
            <IconButton onClick={() => setOrderAuditDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => refreshOrderAuditTimeline()}
              startIcon={<AutorenewIcon />}
              disabled={orderAuditLoading || !orderViewDialog.order?.id}
              sx={{
                mb: 1.1,
                textTransform: 'none',
                fontWeight: 800,
                color: '#4ce1b6',
                '&:hover': {
                  backgroundColor: isDark ? alpha('#4ce1b6', 0.12) : alpha('#4ce1b6', 0.1),
                },
              }}
            >
              Refresh timeline
            </Button>

            {orderAuditLoading ? (
              <Box sx={{ py: 3.5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : orderAuditError ? (
              <Alert severity="warning">{orderAuditError}</Alert>
            ) : orderAuditLogs.length === 0 ? (
              <Alert severity="info">No audit logs available for this order.</Alert>
            ) : (
              <Box sx={{ position: 'relative', pl: 1.4 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 17,
                    top: 8,
                    bottom: 10,
                    width: 2,
                    bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(76,225,182,0.26)',
                  }}
                />
                <Stack spacing={1.1}>
                  {orderAuditLogs.map((log) => {
                    const activityKey = String(log?.payload?.activity || '').toLowerCase();
                    const eventMeta = getOrderAuditEventMeta(log.event_type, activityKey);
                    const changedFields = Array.isArray(log?.payload?.changed_fields)
                      ? log.payload.changed_fields
                      : [];
                    const message = String(log?.payload?.message || '').trim();
                    const activityLabel = String(log?.payload?.activity_label || '').trim();
                    const activityDetails = log?.payload?.activity_details && typeof log.payload.activity_details === 'object'
                      ? log.payload.activity_details
                      : {};
                    const isCreationEvent = String(log?.event_type || '').toLowerCase() === 'created';
                    const hasStatusTransition = Boolean(
                      log?.status_from
                      && log?.status_to
                      && String(log.status_from) !== String(log.status_to)
                    );
                    const itemChanges = activityDetails?.item_changes && typeof activityDetails.item_changes === 'object'
                      ? activityDetails.item_changes
                      : (log?.payload?.item_changes && typeof log.payload.item_changes === 'object'
                        ? log.payload.item_changes
                        : {});
                    const fromTarget = String(activityDetails?.from_target || '').trim();
                    const toTarget = String(activityDetails?.to_target || '').trim();
                    const addedItemsCount = Number(itemChanges?.added_count || 0);
                    const removedItemsCount = Number(itemChanges?.removed_count || 0);
                    const updatedItemsCount = Number(itemChanges?.updated_count || 0);
                    return (
                      <Box key={`order-audit-${log.id}`} sx={{ position: 'relative', pl: 3.1 }}>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 12,
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            border: `2px solid ${alpha(eventMeta.color, 0.65)}`,
                            bgcolor: isDark ? alpha(eventMeta.color, 0.2) : alpha(eventMeta.color, 0.16),
                            display: 'grid',
                            placeItems: 'center',
                            color: eventMeta.color,
                          }}
                        >
                          <FactCheckIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.05,
                            borderRadius: 1.6,
                            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.1)',
                            bgcolor: isDark ? 'rgba(19,23,31,0.75)' : '#ffffff',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {activityLabel || log.event_type_label || eventMeta.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.created_at_display || formatDateTime(log.created_at)}
                            </Typography>
                          </Stack>
                          {message ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {message}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mt: 0.65 }}>
                            <Chip
                              size="small"
                              label={`By ${log.actor_display || log.actor || 'System'}`}
                              sx={{
                                bgcolor: isDark ? alpha('#4ce1b6', 0.2) : alpha('#4ce1b6', 0.12),
                                color: isDark ? '#c7fff0' : '#0f766e',
                                fontWeight: 700,
                              }}
                            />
                            {hasStatusTransition ? (
                              <Chip
                                size="small"
                                label={`${(log.status_from || '—').replace('_', ' ')} -> ${(log.status_to || '—').replace('_', ' ')}`}
                                sx={{
                                  bgcolor: isDark ? alpha('#8b5cf6', 0.22) : alpha('#8b5cf6', 0.12),
                                  color: isDark ? '#ddd6fe' : '#6d28d9',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                            {changedFields.length > 0 && !isCreationEvent ? (
                              <Chip
                                size="small"
                                label={`Changed ${changedFields.length} fields`}
                                sx={{
                                  bgcolor: isDark ? alpha('#38bdf8', 0.2) : alpha('#38bdf8', 0.12),
                                  color: isDark ? '#bae6fd' : '#0369a1',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                            {fromTarget && toTarget && fromTarget !== toTarget ? (
                              <Chip
                                size="small"
                                label={`${fromTarget} -> ${toTarget}`}
                                sx={{
                                  bgcolor: isDark ? alpha('#14b8a6', 0.22) : alpha('#14b8a6', 0.12),
                                  color: isDark ? '#99f6e4' : '#0f766e',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                            {addedItemsCount > 0 ? (
                              <Chip
                                size="small"
                                label={`+${addedItemsCount} item${addedItemsCount > 1 ? 's' : ''}`}
                                sx={{
                                  bgcolor: isDark ? alpha('#22c55e', 0.2) : alpha('#22c55e', 0.12),
                                  color: isDark ? '#bbf7d0' : '#166534',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                            {removedItemsCount > 0 ? (
                              <Chip
                                size="small"
                                label={`-${removedItemsCount} item${removedItemsCount > 1 ? 's' : ''}`}
                                sx={{
                                  bgcolor: isDark ? alpha('#ef4444', 0.2) : alpha('#ef4444', 0.12),
                                  color: isDark ? '#fecaca' : '#991b1b',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                            {updatedItemsCount > 0 ? (
                              <Chip
                                size="small"
                                label={`Updated ${updatedItemsCount} item${updatedItemsCount > 1 ? 's' : ''}`}
                                sx={{
                                  bgcolor: isDark ? alpha('#38bdf8', 0.2) : alpha('#38bdf8', 0.12),
                                  color: isDark ? '#bae6fd' : '#0369a1',
                                  fontWeight: 700,
                                }}
                              />
                            ) : null}
                          </Stack>
                        </Paper>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
      ) : null}
      <Dialog
        open={menuItemViewDialog.open}
        onClose={() => setMenuItemViewDialog({ open: false, menuItem: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Menu Item Details</DialogTitle>
        <DialogContent>
          {menuItemViewDialog.menuItem ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {(menuItemViewDialog.menuItem.menu_image_url || menuItemViewDialog.menuItem.menu_image) ? (
                  <Box
                    component="img"
                    src={menuItemViewDialog.menuItem.menu_image_url || menuItemViewDialog.menuItem.menu_image}
                    alt={menuItemViewDialog.menuItem.name || 'menu'}
                    sx={{
                      width: 102,
                      height: 102,
                      borderRadius: 2,
                      objectFit: 'cover',
                      border: '1px solid',
                      borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.16)',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 102,
                      height: 102,
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)',
                      display: 'grid',
                      placeItems: 'center',
                      color: isDark ? '#93a4ba' : '#6f8199',
                      flexShrink: 0,
                    }}
                  >
                    <RestaurantMenuIcon sx={{ fontSize: 36 }} />
                  </Box>
                )}
                <Stack spacing={0.7} sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {menuItemViewDialog.menuItem.name || '—'}
                  </Typography>
                  <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      color={menuItemViewDialog.menuItem.is_available ? 'success' : 'default'}
                      label={menuItemViewDialog.menuItem.is_available ? 'Available' : 'Unavailable'}
                    />
                    <Chip
                      size="small"
                      label={
                        menuItemViewDialog.menuItem.dietary_label ||
                        (menuItemViewDialog.menuItem.is_veg ? 'Veg' : 'Non-Veg')
                      }
                    />
                    <Chip size="small" label={menuItemViewDialog.menuItem.gst_type_label || 'Services'} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Category:{' '}
                    {(menuItemViewDialog.menuItem.category
                      ? categoryLabelMap.get(menuItemViewDialog.menuItem.category)
                      : menuItemViewDialog.menuItem.category_name) || 'Uncategorized'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Price: {formatCurrency(menuItemViewDialog.menuItem.price)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Short code: {menuItemViewDialog.menuItem.short_code || '—'}
                  </Typography>
                </Stack>
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.7 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.7 }}>
                  Order Type
                </Typography>
                <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                  {(Array.isArray(menuItemViewDialog.menuItem.order_types) &&
                  menuItemViewDialog.menuItem.order_types.length > 0
                    ? menuItemViewDialog.menuItem.order_types
                    : ['room_service', 'pick_up', 'dine_in']
                  ).map((type) => (
                    <Chip key={`dialog-order-type-${type}`} size="small" label={getOrderTypeLabel(type)} />
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.7 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.7 }}>
                  Variation Mapping
                </Typography>
                {Array.isArray(menuItemViewDialog.menuItem.variation_config) &&
                menuItemViewDialog.menuItem.variation_config.length > 0 ? (
                  <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                    {menuItemViewDialog.menuItem.variation_config
                      .filter((row) => row && row.variation_id)
                      .map((row) => {
                        const variationLabel =
                          variationLabelMap.get(Number(row.variation_id)) || `Variation #${row.variation_id}`;
                        const extraPrice = Number(row.price || 0);
                        return (
                          <Chip
                            key={`dialog-variation-${row.variation_id}`}
                            size="small"
                            label={
                              extraPrice > 0
                                ? `${variationLabel} (+${formatCurrency(extraPrice)})`
                                : variationLabel
                            }
                          />
                        );
                      })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No variations mapped.
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.7 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {menuItemViewDialog.menuItem.description || '—'}
                </Typography>
              </Paper>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <Paper variant="outlined" sx={{ p: 0.9, borderRadius: 1.4, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Created
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatShortDate(menuItemViewDialog.menuItem.created_at)}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 0.9, borderRadius: 1.4, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Last Updated
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatShortDate(menuItemViewDialog.menuItem.updated_at)}
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMenuItemViewDialog({ open: false, menuItem: null })}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={qrPreviewDialog.open}
        onClose={() => {
          if (!qrPreviewDialog.loading) {
            setQrPreviewDialog({
              open: false,
              loading: false,
              targetType: '',
              targetId: null,
              targetDisplay: '',
              targetMeta: {},
              landingUrl: '',
              qrDataUrl: '',
            });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          QR Preview {qrPreviewDialog.targetDisplay ? `- ${qrPreviewDialog.targetDisplay}` : ''}
        </DialogTitle>
        <DialogContent>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.4, sm: 1.8 },
              borderRadius: 2,
              borderColor: alpha('#4ce1b6', 0.36),
              background: isDark
                ? 'linear-gradient(135deg, rgba(76,225,182,0.12), rgba(15,23,42,0.8) 58%, rgba(45,212,160,0.12))'
                : 'linear-gradient(135deg, rgba(76,225,182,0.22), rgba(248,253,255,0.96) 58%, rgba(45,212,160,0.18))',
              boxShadow: isDark
                ? '0 16px 34px rgba(2,6,23,0.5)'
                : '0 16px 30px rgba(15,23,42,0.14)',
            }}
          >
            {qrPreviewDialog.loading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }} spacing={1.2}>
                <CircularProgress size={30} />
                <Typography variant="body2" color="text.secondary">
                  Generating secure QR preview...
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.4}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, color: isDark ? '#f5f8ff' : '#1f2d45' }}>
                      {qrPreviewDialog.targetDisplay || 'POS Target'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#9fb2c6' : '#5a6b83' }}>
                      {qrPreviewDialog.targetType === 'room'
                        ? `Room Number: ${qrPreviewDialog.targetMeta?.room_number || qrPreviewDialog.targetId || '-'}`
                        : `Table Capacity: ${qrPreviewDialog.targetMeta?.capacity || 0} seats`}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={qrPreviewDialog.targetType === 'room' ? 'Room Service QR' : 'Dine-in Table QR'}
                    sx={{
                      height: 24,
                      fontWeight: 700,
                      borderRadius: 1.4,
                      color: '#4ce1b6',
                      bgcolor: isDark ? alpha('#4ce1b6', 0.15) : alpha('#4ce1b6', 0.22),
                      border: `1px solid ${alpha('#4ce1b6', 0.42)}`,
                    }}
                  />
                </Stack>
                <Grid container spacing={1.2} alignItems="stretch">
                  <Grid item xs={12} md={7}>
                    <Box
                      sx={{
                        mx: 'auto',
                        width: 'fit-content',
                        p: 1.05,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#4ce1b6', 0.46)}`,
                        bgcolor: isDark ? alpha('#0b1220', 0.8) : alpha('#e6f4ef', 0.84),
                        boxShadow: isDark
                          ? '0 14px 30px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(76,225,182,0.16)'
                          : '0 10px 24px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(76,225,182,0.2)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px) scale(1.01)',
                          boxShadow: isDark
                            ? '0 18px 34px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(76,225,182,0.25)'
                            : '0 14px 30px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(76,225,182,0.24)',
                        },
                      }}
                    >
                      {qrPreviewDialog.qrDataUrl ? (
                        <Box
                          component="img"
                          src={qrPreviewDialog.qrDataUrl}
                          alt={qrPreviewDialog.targetDisplay || 'POS QR'}
                          sx={{
                            width: { xs: 220, sm: 260 },
                            height: { xs: 220, sm: 260 },
                            display: 'block',
                            borderRadius: 1.25,
                            border: `1px solid ${alpha('#10233d', 0.18)}`,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: '100%',
                        p: 1.2,
                        borderRadius: 1.8,
                        borderColor: alpha('#4ce1b6', 0.35),
                        bgcolor: isDark ? alpha('#0b1220', 0.44) : alpha('#ffffff', 0.74),
                        display: 'grid',
                        gap: 0.9,
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, fontSize: 12.5, color: isDark ? '#d7fef1' : '#14532d' }}>
                        QR Details
                      </Typography>
                      <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap">
                        <Chip
                          size="small"
                          label={qrUiDetails.usageLabel}
                          sx={{ fontWeight: 700 }}
                        />
                        <Chip size="small" label={qrUiDetails.targetLabel} sx={{ fontWeight: 700 }} />
                      </Stack>
                      <Typography variant="caption" sx={{ color: isDark ? '#a6bed0' : '#475569' }}>
                        Context: {qrUiDetails.contextLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#a6bed0' : '#475569' }}>
                        Link Status: {qrUiDetails.linkReady ? 'Ready to scan' : 'Preparing link'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#a6bed0' : '#475569' }}>
                        Use this QR for guests at the selected location only.
                      </Typography>
                      <Stack direction="row" spacing={0.8}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                          onClick={handleCopyQrLandingUrl}
                          sx={{ textTransform: 'none', borderRadius: 1.4, fontWeight: 700 }}
                        >
                          Copy Link
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                          onClick={handleOpenQrLandingUrl}
                          sx={{ textTransform: 'none', borderRadius: 1.4, fontWeight: 700 }}
                        >
                          Open Menu
                        </Button>
                      </Stack>
                      <Typography variant="caption" sx={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                        Scan flow: Preview {'->'} Scan {'->'} Open menu {'->'} Place order.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setQrPreviewDialog({
                open: false,
                loading: false,
                targetType: '',
                targetId: null,
                targetDisplay: '',
                targetMeta: {},
                landingUrl: '',
                qrDataUrl: '',
              })
            }
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownloadQr}
            disabled={qrPreviewDialog.loading || !qrPreviewDialog.qrDataUrl}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4a0 100%)',
              color: '#06251e',
              '&:hover': {
                background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
              },
            }}
          >
            Download QR
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={menuItemDeleteDialog.open}
        onClose={() => {
          if (!isDeletingMenuItem) {
            setMenuItemDeleteDialog({ open: false, menuItemId: null, menuItemName: '' });
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete menu item?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {menuItemDeleteDialog.menuItemName || 'this menu item'}
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMenuItemDeleteDialog({ open: false, menuItemId: null, menuItemName: '' })}
            disabled={isDeletingMenuItem}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteMenuItem(menuItemDeleteDialog.menuItemId)}
            disabled={isDeletingMenuItem}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isDeletingMenuItem ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <BookingInvoicePreviewDialog
        open={orderInvoiceDialog.open}
        onClose={closeOrderInvoiceDialog}
        bookingId={orderInvoiceDialog.order?.id}
        invoiceNumber={
          orderInvoiceDialog.order?.order_number
            ? `Restaurant Invoice ${formatOrderNumber(orderInvoiceDialog.order.order_number)}`
            : 'Restaurant Invoice'
        }
        bookingCode={formatOrderNumber(orderInvoiceDialog.order?.order_number || orderInvoiceDialog.order?.id || '')}
        guestName={orderInvoiceDialog.order?.customer_name || orderInvoiceDialog.order?.room_number || 'Restaurant Guest'}
        isDark={isDark}
        accentColor="#4ce1b6"
        invoiceScope="restaurant"
        fetchOfficialPdfBlob={fetchPosOrderInvoicePdfBlob}
        downloadOfficialInvoice={(orderId) => downloadPosOrderInvoice(orderId, orderInvoiceDialog.order?.order_number)}
        fetchDraftPdfBlob={fetchPosOrderDraftInvoicePdfBlob}
        fetchDraftDefaults={fetchPosOrderDraftInvoiceDefaults}
      />
      {placeOrderEditor.open && placeOrderEditor.orderId ? (
        <RestaurantLandingPage
          embedded={true}
          initialEditOrderId={placeOrderEditor.orderId}
          onEmbeddedClose={closePlaceOrderEditor}
          onEmbeddedSaved={handlePlaceOrderEditorSaved}
        />
      ) : null}
      <Dialog
        open={tableDeleteDialog.open}
        onClose={() => {
          if (!isDeletingTable) {
            setTableDeleteDialog({ open: false, tableId: null, tableName: '' });
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete table?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {tableDeleteDialog.tableName || 'this table'}
            </Box>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTableDeleteDialog({ open: false, tableId: null, tableName: '' })}
            disabled={isDeletingTable}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteTable(tableDeleteDialog.tableId, tableDeleteDialog.tableName)}
            disabled={isDeletingTable}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isDeletingTable ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={categoryDeleteDialog.open}
        onClose={() => {
          if (!isDeletingCategory) {
            setCategoryDeleteDialog({ open: false, categoryId: null, categoryName: '' });
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Remove category?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to remove{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {categoryDeleteDialog.categoryName || 'this category'}
            </Box>
            ? This will permanently delete it. Mapped menu items and child categories will be detached automatically.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCategoryDeleteDialog({ open: false, categoryId: null, categoryName: '' })}
            disabled={isDeletingCategory}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteCategory(categoryDeleteDialog.categoryId)}
            disabled={isDeletingCategory}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isDeletingCategory ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={variationDeleteDialog.open}
        onClose={() => {
          if (!isDeletingVariation) {
            setVariationDeleteDialog({ open: false, variationId: null, variationName: '' });
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Remove variation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to remove{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {variationDeleteDialog.variationName || 'this variation'}
            </Box>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setVariationDeleteDialog({ open: false, variationId: null, variationName: '' })}
            disabled={isDeletingVariation}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteVariation(variationDeleteDialog.variationId)}
            disabled={isDeletingVariation}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {isDeletingVariation ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <PosAiMenuUploaderOverlay
        open={aiMenuUploaderOpen}
        onClose={() => setAiMenuUploaderOpen(false)}
        isDark={isDark}
        onSuccess={(summary) => {
          const createdItems = Number(summary?.items_created || 0);
          const updatedItems = Number(summary?.items_updated || 0);
          const createdCats = Number(summary?.categories_created || 0);
          setToastMessage(
            'success',
            `AI menu imported: ${createdItems} items created, ${updatedItems} updated, ${createdCats} categories created.`
          );
          // Defer reload so the uploader can close without freezing the tab.
          window.setTimeout(() => {
            loadData();
          }, 150);
        }}
      />
    </Box>
  );
};

export default PosRestaurantDashboard;
